import {
	createTransaction as createTransactionRemote,
	patchTransaction as patchTransactionRemote,
	buildTransactionRecord,
	queueExternalWrite,
	flushExternalQueue,
	adjustCapTable,
} from '../services/TransactionService.js';
import { wfExecutionsProgressURL } from './api-urls.js';
import { balanceTolerance, nearlyEqualBalance } from './client-flags.js';
import { AppState } from './app-state.js';

let CERT_COLUMN;
let CLASS_COLUMN;
let showGainsColumns;
let stringOptions;
let sharesOwned = 0;
let builtGiftTransferModalAlready;
let addedGiftTransferListenersAlready;
let viewRecipients;
let confirmRequestRemove;

function resetGiftTransferState() {
	builtGiftTransferModalAlready = false;
	sharesOwned = 0;
	window._stepwiseMasterLen = undefined;
	window._stepwiseTargetLen = null;
}

function giftTransfer(shareholder, fromUser, options = {}) {
	/* NOTES
	This method creates the Gift / Transfer modal
	For each class, it gets relevant data for the active user
	The resultant display can be filtered by class

	Options:
	- dataOnly: boolean - If true, skip DOM operations and return processed data
	- clientData: object - Override client data (for dataOnly mode)
	- shareholderData: object - Override shareholder data (for dataOnly mode)
	*/
	const dataOnly = options.dataOnly === true;
	const _clientData = options.clientData;
	const _shareholderData = options.shareholderData;

	// In dataOnly mode, skip DOM checks and use provided data
	if (!dataOnly) {
		// #6061: shareholderData can still be null here if this modal opens before the page populates it; rehydrate like adjustBalances does (~line 5137).
		if (!shareholderData) shareholderData = JSON.parse(sessionStorage.getItem('shareholder.Data'));
		if (userData?.userid == 'consolidated.view' || shareholderData?.userid == 'consolidated.view') {
			// Use AppState shareholderData (bridged via window) rather than re-parsing sessionStorage
			if (!shareholderData) return;
			userData = shareholderData;
			shareholder = userData.userid;
		}
		let currentHeadline = document.getElementById('giftHeadline').innerText;
		currentHeadline = currentHeadline.replace('From:', '').trim();
		if (currentHeadline && currentHeadline != userData.user_name) {
			builtGiftTransferModalAlready = false;
			sharesOwned = 0;
		}

		if (builtGiftTransferModalAlready) return;
		builtGiftTransferModalAlready = true;
	}

	let members;
	let allGifts = [];
	let ttlBasis = 0;
	let ttlAfter = 0;
	let ttlBefore = 0;
	let pairedQty = 0;
	let selectedClass;
	let minDiff = 999; // arbitrarily large starting number used in tranche analysis
	let diffCount = 0; // counter used in tranche analysis
	let removeGiftId = '';
	let giftRecipients = [];
	// Cash Sale: keyed by recipient userid rather than stored on the recipient
	// object (those are shared refs into members/capTableData). All-or-nothing
	// per recipient -- see renderGiftRecipients()/'cash-sale-checkbox' listener.
	let cashSaleRecipients = {};
	let priorTransfers = [];
	let includedClasses = [];
	let giftTransactions = [];
	let historicHoldings = [];

	// #6144: which matching signal decided a Historic Holdings/Prior Transfer row --
	// feeds the "Keys" legend under the Prior Tranches table (Stepwise Analysis).
	const MATCH_KEY_NAMES = {
		A: 'Explicit Cert Match',
		B: 'Notes Text Match',
		C: 'Perfect Fit',
		D: 'Split Across Tranches',
		E: 'Best-Guess Match',
		F: 'Basis Carried Forward',
		G: 'Sender Basis Match',
		H: 'Date Match (from Notes)',
	};
	const MATCH_KEY_DESCS = {
		A: 'Linked by an exact certificate/lot number referenced in the notes.',
		B: 'Linked by a free-text description in the notes.',
		C: "One prior holding exactly covers this transfer's quantity.",
		D: 'Multiple prior holdings were combined to exactly cover this transfer.',
		E: 'No explicit cert/notes link; the closest candidate by quantity, date, and price was inferred.',
		F: 'Share Price/Tax Basis was inherited from the matched holding rather than recorded on this transaction.',
		G: "The sender-declared basis exactly matches this candidate holding's recorded Tax Basis.",
		H: 'A date written in the notes is within 5 days of the actual transaction date on one side of the match.',
	};
	// Short mnemonic shown in the badge/legend -- avoids arbitrary single letters.
	const MATCH_KEY_LABELS = { A: 'E.C.', B: 'N.M.', C: 'P.F.', D: 'S.T.', E: 'B.G.', F: 'B.C.', G: 'S.B.', H: 'D.M.' };
	function tagMatch(item, key, transferForSenderCheck) {
		if (!item || item._matchKey) return;
		if (transferForSenderCheck && senderBasisMatchesCandidate(transferForSenderCheck, item)) key = 'G';
		item._matchKey = key;
	}
	let origTransactions = [];
	let selectedTranches = [];
	let toggle = 'Show Positves';
	let trancheErrorText = '';

	let giftUserData = dataOnly ? _shareholderData || {} : shareholderData;
	let giftUserId = giftUserData?.userid || shareholder;

	let viewGiftTransferModal, userGiftTransferTable, BUY_BACK;
	if (!dataOnly) {
		BUY_BACK = pn == '/' && clientData.flags?.buy_back_active;
		viewGiftTransferModal = document.getElementById('viewGiftTransferModal');
		userGiftTransferTable = document.getElementById('userGiftTransferTable');
	}

	// Need to wait for external data - use provided data in dataOnly mode
	if (dataOnly && _clientData) {
		clientData = _clientData;
	} else {
		clientData = JSON.parse(sessionStorage.getItem(client + '.Data'));
	}

	// Helper functions used by both dataOnly mode and renderUserGiftTransModal
	function numberToLetters(num) {
		let letters = '';
		while (num >= 0) {
			letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[num % 26] + letters;
			num = Math.floor(num / 26) - 1;
		}
		return letters;
	}

	function lettersToNumbers(ltrs) {
		if (!ltrs) return '?';
		let n = 0;
		for (let p = 0; p < ltrs.length; p++) {
			n = ltrs[p].charCodeAt() - 64 + n * 26;
		}
		return n.toString().padStart(6, '0');
	}

	const SORT_CLASS_BASIS_DATE_QTY = true; // or client-specific flag

	// By date then descending effective tax basis (unknowns last)
	function sortByClassThenBasisThenDateThenQty(transArray) {
		transArray.sort((a, b) => {
			if (SORT_CLASS_BASIS_DATE_QTY) {
				// 1) Class ascending
				if (a.shareClass < b.shareClass) return -1;
				if (a.shareClass > b.shareClass) return 1;

				// 2) Effective tax basis descending, unknowns last
				function normBasis(val) {
					return isKnownNumber(val) ? Number(val) : null;
				}

				let basisA = normBasis(a._effectiveTaxBasis);
				let basisB = normBasis(b._effectiveTaxBasis);

				if (basisA === null && basisB !== null) return 1;
				if (basisA !== null && basisB === null) return -1;

				if (basisA !== null && basisB !== null) {
					if (basisA < basisB) return 1;
					if (basisA > basisB) return -1;
				}

				// 3) Date ascending
				let dateA = Number(a.date);
				let dateB = Number(b.date);
				if (dateA < dateB) return -1;
				if (dateA > dateB) return 1;

				// 4) Quantity ascending
				let quantityA = Number(a.quantity);
				let quantityB = Number(b.quantity);
				if (quantityA < quantityB) return -1;
				if (quantityA > quantityB) return 1;

				return 0;
			}
			if (isVERM) {
				// First, sort by 'class'
				if (a.shareClass < b.shareClass) return -1;
				if (a.shareClass > b.shareClass) return 1;

				// Next, sort by 'date' ascending
				let dateA = Number(a.date);
				let dateB = Number(b.date);
				if (dateA < dateB) return -1;
				if (dateA > dateB) return 1;
			} else if (!isBB) {
				// First, sort by 'date' descending
				let dateA = Number(a.date);
				let dateB = Number(b.date);
				if (dateA < dateB) return 1;
				if (dateA > dateB) return -1;

				// Next, sort by 'class'
				if (a.shareClass < b.shareClass) return -1;
				if (a.shareClass > b.shareClass) return 1;
			}

			// Then, sort by effective tax basis (descending, unknowns last)
			function normBasis(val) {
				return isKnownNumber(val) ? Number(val) : null;
			}

			let basisA = normBasis(a._effectiveTaxBasis);
			let basisB = normBasis(b._effectiveTaxBasis);

			// Unknowns go LAST
			if (basisA === null && basisB !== null) return 1;
			if (basisA !== null && basisB === null) return -1;

			// Both known → descending
			if (basisA !== null && basisB !== null) {
				if (basisA < basisB) return 1;
				if (basisA > basisB) return -1;
			}

			if (isBB) {
				// First, sort by 'class'
				if (a.shareClass < b.shareClass) return -1;
				if (a.shareClass > b.shareClass) return 1;

				// Next, sort by 'date' ascending
				let dateA = Number(a.date);
				let dateB = Number(b.date);
				if (dateA < dateB) return -1;
				if (dateA > dateB) return 1;
			}

			// Finally, sort by 'quantity'
			let quantityA = Number(a.quantity);
			let quantityB = Number(b.quantity);
			if (quantityA < quantityB) return -1;
			if (quantityA > quantityB) return 1;

			return 0;
		});

		return transArray;
	}

	// Build a new transactions list
	let classes = Object.keys(clientData.equity);
	if (classes.length > 1) CLASS_COLUMN = true;
	classes.sort();

	// Seed CERT_COLUMN from the client's real certificates config, not just the
	// "#" notes heuristic below -- that heuristic misses certificate clients
	// with no cert-tagged notes yet (e.g. no Prior Transfers).
	CERT_COLUMN ||= !!(clientData.flags?.show_certificates || AppState.USE_CERTIFICATES);

	classes.forEach((c, i) => {
		// console.log(clientData.equity[c].transactions)
		let classIncluded = false;
		// Try looking at Transactions first, then Approved balances
		// For EXTERNAL_DATA admin views, scoped transactions are on shareholderData.scopedTransactions
		// rather than clientData.equity[c].transactions (which is re-read from clean sessionStorage here).
		let classTransactions =
			EXTERNAL_DATA && shareholderData?.scopedTransactions?.[c] ? shareholderData.scopedTransactions[c] : clientData.equity[c].transactions || [];
		classTransactions.forEach(function (t) {
			if ((t.receiver && t.receiver.toLowerCase() == shareholder) || (t.sender && t.sender.toLowerCase() == shareholder)) {
				t.shareClass = classes[i];
				giftTransactions.push(t);
				classIncluded = true;
			}
		});

		// Gift Transfer uses certificate-aware matching as analysis logic,
		// not merely table rendering. Keep CERT_COLUMN as a runtime analysis flag.
		CERT_COLUMN ||= classTransactions.some((t) => t.notes.includes('#'));

		// Dont't display future (beyond this year) Events or Transactions
		let nextYear = new Date(new Date().getFullYear() + 1, 1, 1) / 1000;
		giftTransactions = giftTransactions.filter((t) => Number(t.date) < nextYear);
		console.log('initialTransactions[' + c + ']: ', giftTransactions);

		if (classIncluded) includedClasses.push(c);
		//clientEvents = clientEvents.concat(clientData.equity[c].events);

		// sharesOwned/balanceBefore only feed the interactive headline -- skip in dataOnly mode, where
		// a bare caller (e.g. Ledgers' Tax Basis placeholder) may never have populated shareholderData.
		if (!dataOnly) {
			// #6061: optional chaining as a backstop in case shareholderData is still unavailable after the rehydration above.
			let shareholderClientData = shareholderData?.clients?.[client];
			let userTransactions = classTransactions.filter((t) => t.receiver == shareholder || t.sender == shareholder);
			if (userTransactions.length && shareholderClientData?.shares[c]) {
				// Need to rationalize, or at least say, Owned vs Approved
				sharesOwned += Number(shareholderClientData.shares[c].approved || '0');
			}
		}
	});

	let ttlCurrentValue = 'n.a.';
	let balanceBefore = sharesOwned;
	let balanceAfter = balanceBefore;
	// let shortName = giftUserData.clients[client].notes;

	// dataOnly mode: this whole block is pure DOM/UI side effect, gated so a
	// dataOnly caller without the GT modal's HTML (e.g. Ledgers) doesn't throw
	// on getElementById(). Vars stay `let` since later interactive-only code
	// still references them, just unused/unreached on the dataOnly path.
	let giftHeadline, balanceHeadline, stepwiseAnalysis, giftTransferControls;
	if (!dataOnly) {
		giftHeadline = document.getElementById('giftHeadline');
		balanceHeadline = document.getElementById('balanceHeadline');
		stepwiseAnalysis = document.getElementById('stepwiseAnalysis');
		giftTransferControls = document.getElementById('giftTransferControls');
		if (!sharesOwned || stepwiseAnalysis?.checked) giftTransferControls.classList.add('hidden');
		else giftTransferControls.classList.remove('hidden');
		giftHeadline.innerHTML = `From:&ensp;<span class="text-light">` + giftUserData.user_name + `</span>`;
		// giftHeadline.innerHTML = `From:&ensp;<span class="text-light">` + (shortName ? shortName + " | " : "") + giftUserData.user_name + `</span>`;
		balanceHeadline.innerHTML =
			'Shares Owned Now:&emsp;<span class="text-primary">' +
			balanceBefore.toLocaleString('en-US', customFormat()) +
			`</span>&nbsp;Before&emsp;<span id="balanceAfterElem" class="text-primary${balanceAfter < 0 ? ' red-text font-bold' : ''}">` +
			balanceAfter.toLocaleString('en-US', customFormat()) +
			`</span>&nbsp;After`;
	}

	// SEE NOTES HERE: https://docs.google.com/document/d/1tASR37WcKOE-x_LJKlCy0JhD0g0cWPaTUCUPNAlRjNE/edit
	// First, sort all transactions chronologically, oldest to newest.
	// Next, scan for dates with multiple transactions.
	// Each of these dates must be analyzed separately for possible splits or related swaps.
	// In all cases, tax basis must be carried forward.

	// Determine if these transactions share the current account as both sender and receiver.
	// If all go only in one direction (single-leg) these transactions require no special treatment.

	// If the transactions go in opposite directions, start by sorting all of the transactions for a given date by descending dollar amounts.

	// Now look for matching quantities (swaps).
	// Do the total aggregate quantities in each direction match?
	// If not, do any individual quantities in each direction match?
	// If not, does any individual quantity match a group of opposite quantities?

	// If matching quantities are not found, look for matching dollar amounts (splits).
	// Do the total aggregate dollar amounts in each direction match?
	// If not, do any individual dollar amounts in each direction match?
	// If not, does any individual amount match a group of opposite amounts?
	// If any matches are found, determine and enter the new post-split tax basis.
	// Filter out first leg (cancellation) of stock swaps and stock splits.

	// No clue why, but we need new array objects...
	giftTransactions = JSON.parse(JSON.stringify(giftTransactions));

	// --- Stepwise: clamp giftTransactions to cutoff date  ---
	// In dataOnly mode (statement generation), reset stepwise state so all transactions are included
	if (dataOnly) window._stepwiseTargetLen = null;
	(function () {
		// build sorted unique list of all dates
		let allDates = Array.from(new Set(giftTransactions.map((t) => String(t.date)))).sort((a, b) => Number(a) - Number(b));

		// master and target length
		window._stepwiseMasterLen = allDates.length;
		if (window._stepwiseTargetLen == null) window._stepwiseTargetLen = _stepwiseMasterLen;

		// clamp target length
		if (_stepwiseMasterLen === 0) {
			window._stepwiseTargetLen = 0;
		} else {
			if (_stepwiseTargetLen < 1) _stepwiseTargetLen = 1;
			if (_stepwiseTargetLen > _stepwiseMasterLen) _stepwiseTargetLen = _stepwiseMasterLen;
		}

		// cutoff date = last visible date
		const cutoffSec = _stepwiseTargetLen > 0 ? Number(allDates[_stepwiseTargetLen - 1]) : 0;

		// filter giftTransactions IN PLACE
		// Guard on _stepwiseTargetLen, not cutoffSec -- pre-1970 dates make cutoffSec negative.
		if (_stepwiseTargetLen > 0) {
			giftTransactions = giftTransactions.filter((t) => Number(t.date) <= cutoffSec);
		}

		// optional: update label/buttons
		const label = document.getElementById('stepLabel');
		if (label) {
			if (_stepwiseTargetLen === 0) {
				label.textContent = 'As of --/--/-- (0 of 0)';
			} else {
				const ms = cutoffSec * 1000;
				const dstr = window.moment ? moment(ms).format('MM/DD/YY') : new Date(ms).toLocaleDateString();
				label.textContent = `As of ${dstr} (${_stepwiseTargetLen} of ${_stepwiseMasterLen})`;
			}
		}
	})();

	function reduceToVestedOnly() {
		const userid = giftUserData.userid;
		const shareClasses = giftUserData.clients?.[client]?.shares || {};
		const grants = [];

		// Loop over all share classes
		for (const [classKey, classData] of Object.entries(shareClasses)) {
			const vestingData = classData.vesting || {};

			// Build array of structured grant data
			for (const [grantName, grant] of Object.entries(vestingData)) {
				const schedule = grant.schedule || {};
				const timestamps = Object.keys(schedule).map((t) => Number(t));
				const total = timestamps.reduce((sum, t) => sum + Number(schedule[t].amount || 0), 0);
				const vested = timestamps.filter((t) => schedule[t].posted).reduce((sum, t) => sum + Number(schedule[t].amount || 0), 0);

				// Extract a grant date from the title, e.g., "1/1/24"
				const dateMatch = grantName.match(/(\d{1,2}\/\d{1,2}\/\d{2,4})/);
				let titleDateStr = null;
				if (dateMatch) titleDateStr = dateMatch[1];

				grants.push({ grantName, total, vested, titleDateStr, classKey });
			}
		}

		// Adjust the transactions
		for (const tx of giftTransactions) {
			if (tx.notes.toLowerCase().includes('restricted stock grant') && tx.receiver === userid) {
				const txQty = Number(tx.quantity);
				const txDate = Number(tx.date);
				const txClass = tx.shareClass;

				// Format transaction date as "m/d/yy" and "m/d/yyyy"
				const txDateStr = moment.unix(txDate).utc().format('M/D/YYYY');
				const [m, d, y] = txDateStr.split('/');
				const altYear = y.length === 4 ? y.slice(2) : '20' + y;
				const altDateStr = `${m}/${d}/${altYear}`;

				// Match against grants
				const match = grants.find(
					(grant) =>
						grant.total === txQty &&
						grant.classKey === txClass &&
						grant.titleDateStr &&
						(grant.titleDateStr === txDateStr || grant.titleDateStr === altDateStr)
				);

				if (match) {
					tx.quantity = match.vested.toString(); // even if 0
				}
			}
		}
	}

	// Returns:
	// - null    => no deferral
	// - "LOCK"  => TBD / future use (admin must edit to unlock)
	// - number  => unix seconds at midnight of the "use on/after" day
	function deferUseTs(notes) {
		const s = String(notes || '').trim();
		if (!s) return null;

		// Must contain the "for use" concept (or "for future use")
		if (!/\bfor\s+(?:future\s+)?use\b/i.test(s)) return null;

		// "for future use" => locked until admin edits
		if (/\bfor\s+future\s+use\b/i.test(s)) return 'LOCK';

		// Recognize TBD in any common form: TBD, T.B.D., T. B. D, etc.
		const TBD_RE = /\bt\s*\.?\s*b\s*\.?\s*d\s*\.?\b/i;

		// Allow: "... for use TBD" (no on/after keyword)
		if (/\bfor\s+use\b/i.test(s) && TBD_RE.test(s)) return 'LOCK';

		// Allow: "for use on X", "for use after X", "for use on/after X"
		const m = s.match(/\bfor\s+use\b.*?\b(on\/after|on|after)\b\s*[:\-]?\s*([A-Za-z0-9,./\- ]{2,60}|\bt\s*\.?\s*b\s*\.?\s*d\s*\.?\b)\b/i);
		if (!m) return null;

		const raw = String(m[2] || '').trim();
		if (TBD_RE.test(raw)) return 'LOCK';

		// Parse "any date format" using moment loose first, then Date.parse fallback
		let ms = null;
		if (window.moment) {
			const mm = moment(raw);
			if (mm.isValid()) ms = mm.valueOf();
		}
		if (ms == null) {
			const dp = Date.parse(raw);
			if (!isNaN(dp)) ms = dp;
		}
		if (ms == null) return null;

		return moment(ms).utc().startOf('day').unix();
	}

	// too early for historical context
	// if (isEDM) reduceToVestedOnly();

	giftTransactions.sort((a, b) => Number(a.date) - Number(b.date));
	origTransactions = JSON.parse(JSON.stringify(giftTransactions));

	// normalize all dates to midnight of that date
	giftTransactions.forEach((t) => t.date && (t.date = moment.unix(t.date).utc().startOf('day').unix()));
	giftTransactions.forEach((t) => (t._useAfter = deferUseTs(t.notes)));
	console.log(
		'LOCK lots:',
		giftTransactions.filter((t) => t._useAfter === 'LOCK')
	);

	// NEW EFECTIVE TAX BASIS CODE //
	function isNA(val) {
		const s = String(val ?? '')
			.trim()
			.toLowerCase();
		return s === 'n/a' || s === 'n.a.' || s === 'na' || s === 'n.a';
	}

	function isKnownNumber(val) {
		if (val === null || val === undefined) return false;
		if (typeof val === 'string') {
			const s = val.trim().toLowerCase();
			if (s === '' || isNA(s)) return false;
		}
		return !isNaN(Number(val));
	}

	// #6144: swaps the column header/tooltip to "Share Price" while Cash Sale is
	// checked (the cell still writes to taxBasis either way, only the label changes).
	function basisHeaderContent(isCashSale) {
		return isCashSale
			? {
					label: 'Share&nbsp;Price',
					title: 'The price paid per share in this cash sale. Cash Sale is checked for this recipient, so this value also becomes Tax Basis for each transaction.',
				}
			: {
					label: 'Tax&nbsp;Basis',
					title: 'Also called Cost Basis: the value used for tax reporting. Differs from Share Price/Price Paid -- e.g., 0 for an ordinary gift, unless Cash Sale is checked for this recipient, in which case Share Price mirrors this value.',
				};
	}

	// #6144: shared epsilon-tolerant quantity equality. Quantities are strings
	// (so '.777' vs '0.777' fails a bare ==) and can carry float noise from
	// upstream math -- Number() fixes the former, the epsilon fixes the latter.
	function quantitiesMatch(a, b) {
		const numA = Number(a);
		const numB = Number(b);
		if (isNaN(numA) || isNaN(numB)) return false;
		return nearlyEqualBalance(numA, numB);
	}

	function getLatestValuationField(txn, clientData, fieldName) {
		if (!txn?.shareClass || !txn?.date) return null;
		let vals = clientData?.equity?.[txn.shareClass]?.valuations;
		if (!Array.isArray(vals) || !vals.length) return null;
		let txnDate = Number(txn.date);
		let bestValue = null;
		let bestDate = null;
		vals.forEach((v) => {
			let vDate = Number(v?.date);
			if (!isFinite(vDate) || vDate > txnDate) return;
			if (!isKnownNumber(v?.[fieldName])) return;
			if (bestDate === null || vDate > bestDate) {
				bestDate = vDate;
				bestValue = Number(v[fieldName]);
			}
		});
		return bestValue;
	}

	// #6144: a sender can declare a lot's tax basis via `senderBasis` or embedded
	// in notes/Description (e.g. "Gift, basis: 4.50", or a bare "$46.70" --
	// notes rarely use "$" for anything else) -- highest-priority signal in
	// resolveEffectiveTaxBasis()/scoreMatches() below. Permissive on format since
	// this is admin-typed free text.
	function parseSenderBasisFromNotes(notesText) {
		const s = String(notesText || '');
		const m = s.match(/\bbasis\s*:?\s*\$?\s*(-?[\d,]*\.?\d+)/i) || s.match(/\$\s*(-?[\d,]*\.?\d+)/);
		if (!m) return null;
		const value = Number(m[1].replace(/,/g, ''));
		return isNaN(value) ? null : value;
	}

	// #6144: tolerant numeric coercion for a sender-basis value that may come
	// back formatted ("$4.50", "1,234.5") depending on how it was entered.
	function coerceBasisNumber(raw) {
		if (raw === null || raw === undefined) return null;
		const cleaned = typeof raw === 'string' ? raw.replace(/[$,]/g, '').trim() : raw;
		if (cleaned === '') return null;
		const n = Number(cleaned);
		return isNaN(n) ? null : n;
	}

	function getSenderDeclaredBasis(txn) {
		// Accept either key -- deriveCustomColumnKey() slugifies "Sender Basis"
		// to snake_case, so clients' actual custom-column data uses that form.
		let val = coerceBasisNumber(txn?.senderBasis);
		if (val != null) return val;
		val = coerceBasisNumber(txn?.sender_basis);
		if (val != null) return val;
		// Also check any client-configured custom column that IS a "Sender Basis"
		// column but landed on a different derived key (e.g. a label typed
		// slightly differently, or a key collision appended "_1").
		const customCols = clientData?.transaction_custom_columns || [];
		for (const col of customCols) {
			if (!col?.key || col.key === 'senderBasis' || col.key === 'sender_basis') continue;
			if (!/sender.?s?\s*basis/i.test(col.label || col.key)) continue;
			val = coerceBasisNumber(txn?.[col.key]);
			if (val != null) return val;
		}
		return parseSenderBasisFromNotes(txn?.notes);
	}

	// True when a SEND transaction's sender-declared basis (see
	// getSenderDeclaredBasis()) matches a candidate prior holding's own
	// recorded taxBasis -- the high-priority matching signal used by
	// scoreMatches()/scoreDifferences() below.
	function senderBasisMatchesCandidate(transfer, h) {
		const declared = getSenderDeclaredBasis(transfer);
		if (declared == null || !isKnownNumber(h?.taxBasis)) return false;
		return Math.abs(declared - Number(h.taxBasis)) < 0.005;
	}

	// #6144: same date-in-notes heuristic scoreDifferences() uses internally -- reused here
	// only to identify (for the 'H' Key) whether a note-embedded date, not the score itself, decided a pick.
	function extractNoteDate(text) {
		const match = String(text || '').match(/(\d{1,4}[\/\-]\d{1,2}[\/\-]\d{1,4}|\w{3} \d{1,2}, \d{4})/);
		if (match) {
			const m = moment(match[0], dateFormats, true);
			if (m.isValid()) return m.valueOf();
		}
		return null;
	}
	function isNoteDateCloseInTime(msFromNote, dateStampSeconds) {
		return Math.abs(msFromNote - Number(dateStampSeconds) * 1000) <= 5 * 24 * 60 * 60 * 1000;
	}
	function noteDateMatchesCandidate(transfer, h) {
		const transferNoteDate = extractNoteDate(transfer?.notes);
		const historicNoteDate = extractNoteDate(h?.notes);
		return Boolean(
			(transferNoteDate && isNoteDateCloseInTime(transferNoteDate, h?.date)) ||
			(historicNoteDate && isNoteDateCloseInTime(historicNoteDate, transfer?.date))
		);
	}

	function resolveEffectiveTaxBasis(txn, clientData) {
		// 0) explicit n/a means "do not derive"
		let tb = String(txn.taxBasis || '')
			.trim()
			.toLowerCase();
		if (isNA(tb)) return { value: txn.taxBasis, source: 'explicitNA' };

		// 1) sender-declared basis (dedicated field or parsed from notes) --
		// highest priority per Chris: an explicit basis the SENDER attached to
		// this transfer wins over the transaction's own (possibly blank or
		// auto-derived) taxBasis field.
		const senderBasis = getSenderDeclaredBasis(txn);
		if (senderBasis != null) return { value: senderBasis, source: 'senderBasis' };

		// 2) explicit tax basis
		if (isKnownNumber(txn.taxBasis)) return { value: Number(txn.taxBasis), source: 'taxBasis' };

		// 3) purchase price / transaction price -- excludes 0, which is now the
		// display default for a never-entered Share Price (not necessarily a
		// real $0), so it's no longer distinguishable from "unknown" here.
		if (isKnownNumber(txn.sharePrice) && Number(txn.sharePrice) > 0) return { value: Number(txn.sharePrice), source: 'sharePrice' };

		// 4) valuation-derived fallbacks
		let ref = getLatestValuationField(txn, clientData, 'referencePrice');
		if (isKnownNumber(ref)) return { value: ref, source: 'referencePrice' };

		let valPrice = getLatestValuationField(txn, clientData, 'sharePrice');
		if (isKnownNumber(valPrice)) return { value: valPrice, source: 'valuationSharePrice' };

		return { value: null, source: 'unresolved' };
	}

	// #6144: per-share value to compare against cost basis for the optional Gain/Loss
	// column -- prefers the latest Reference Price valuation as of today, falling back
	// to the latest Share Price valuation. Internal estimate only, not tax advice.
	function getCurrentPerShareValue(t, clientData) {
		const asOfToday = { shareClass: t.shareClass, date: Math.floor(Date.now() / 1000) };
		let ref = getLatestValuationField(asOfToday, clientData, 'referencePrice');
		if (isKnownNumber(ref)) return Number(ref);
		let sp = getLatestValuationField(asOfToday, clientData, 'sharePrice');
		if (isKnownNumber(sp)) return Number(sp);
		return null;
	}

	// #6144: estimated capital gain/loss for the optional Gain/Loss column --
	// (current per-share value - effective Tax Basis) * quantity. Long-term vs.
	// short-term is based on this tranche's own date, which is the acquisition
	// date a gift recipient inherits under IRS basis "tacking" rules. Returns
	// null when there isn't enough known data to estimate.
	function computeGainLoss(t, clientData) {
		if (!Number.isFinite(t._effectiveTaxBasis)) return null;
		const currentValue = getCurrentPerShareValue(t, clientData);
		if (!isKnownNumber(currentValue)) return null;
		const qty = Number(String(t.quantity).replace(/[^.\d-]/g, ''));
		if (!qty) return null;
		const totalGain = (currentValue - t._effectiveTaxBasis) * qty;
		const isLongTerm = moment.unix(t.date).add(1, 'year').isSameOrBefore(moment());
		return { totalGain, isLongTerm };
	}
	function normalizeCertToken(token) {
		let s = String(token || '')
			.trim()
			.toUpperCase();
		if (!s) return '';

		s = s.replace(/[.\-_:\/\\•|\s#]/g, '');

		let m = s.match(/^([A-Z]*)(\d+)$/);
		if (m) return m[1] + (m[2].replace(/^0+/, '') || '0');

		s = s.replace(/^0+(\d)/, '$1');
		return s;
	}

	// Matches cert numbers like "62-409", "SD.468" as one token (not truncated at the separator).
	// Only one separator allowed, so it won't accidentally match multi-part dates.
	const CERT_TOKEN = '[A-Za-z]*[-.]?0*\\d+(?:[-.]\\d+)?';

	function parseCertificateRefs(notesText) {
		const s = String(notesText || '');
		const result = {
			newCert: '',
			sourceCerts: [],
		};

		// First cert inside parens is often the new cert
		// e.g. Transfer (#15954 from ...)
		let m = s.match(new RegExp('\\((?:cert\\s*)?#?\\s*(' + CERT_TOKEN + ')', 'i'));
		if (m && m[1]) result.newCert = normalizeCertToken(m[1]);

		// Anything after "from" is a source list
		// e.g. from 14479, 14506, 14507
		m = s.match(/\bfrom\b(.*?)(?:\)|$)/i);
		if (m && m[1]) {
			const sourceCertPattern = new RegExp('\\b(' + CERT_TOKEN.replace('\\d+', '\\d{4,}') + ')\\b');
			result.sourceCerts = m[1]
				.split(',')
				.map((part) => {
					const mm = part.match(sourceCertPattern);
					return mm && mm[1] ? normalizeCertToken(mm[1]) : '';
				})
				.filter(Boolean);
		}

		// Fallback for simple cases like Cancellation (0006620)
		if (!result.newCert && !result.sourceCerts.length) {
			m = s.match(new RegExp('\\((?:cert\\s*)?#?\\s*(' + CERT_TOKEN + ')\\)', 'i'));
			if (m && m[1]) result.newCert = normalizeCertToken(m[1]);
		}

		// Cancellations that list multiple certs together with no "from", e.g.
		// "Stock Certificate Cancellation (62-422, 62-1051)" -- only the first was
		// being captured above. Scoped to "cancel" notes so nothing else changes.
		if (!result.sourceCerts.length && /cancel/i.test(s)) {
			const parenMatch = s.match(/\(([^)]+)\)/);
			if (parenMatch) {
				const tokens = (parenMatch[1].match(new RegExp(CERT_TOKEN, 'g')) || []).map(normalizeCertToken).filter(Boolean);
				if (tokens.length > 1) {
					result.sourceCerts = [...new Set(tokens)];
					result.newCert = '';
				}
			}
		}

		return result;
	}

	giftTransactions.forEach((t) => {
		const basis = resolveEffectiveTaxBasis(t, clientData);
		t._effectiveTaxBasis = basis.value;
		t._basisSource = basis.source;
		console.log('EFFECTIVE BASIS = ' + basis.value + ' ' + basis.source);
	});

	// For each Recieve from company with no sharePrice, insert latest valuation or assumed price
	// giftTransactions.forEach(r => {
	// 	if (r.receiver == giftUserId && (r.sender == "" || r.sender == client || r.senderName.toLowerCase() == "issuer"))
	// 		r.sharePrice = toNumber(r.sharePrice).toString();
	// 		let classValuations = clientData.equity[r.shareClass].valuations.sort((a, b) => Number(a.date) - Number(b.date));
	// 		let nearestValuation = null;
	// 		for (const valuation of classValuations) {
	// 			if (Number(valuation.date) > Number(r.date)) break;
	// 			nearestValuation = valuation;
	// 		}
	// 		if (nearestValuation) r.sharePrice = nearestValuation.sharePrice;
	// });

	// On first pass, go class-by-class.  May need to revisit with mixed classes for swaps.
	classes.forEach((c) => {
		// First, find dates with multiple transations on the same date
		let transactionsDateCount = giftTransactions
			.filter((g) => g.shareClass == c)
			.reduce((t, r) => {
				t[r.date] = (t[r.date] || 0) + 1;
				return t;
			}, {});
		let multipleSameDates = Object.keys(transactionsDateCount).filter((date) => transactionsDateCount[date] >= 1);
		multipleSameDates.push(Math.floor(Date.now() / 1000).toString()); // add today to catch any recent single sends.
		multipleSameDates.sort((a, b) => Number(a) - Number(b));
		console.log('multipleSameDates[' + c + ']: ', multipleSameDates);

		const usedSendNrids = new Set();
		function addPriorTransfers(transArray) {
			transArray.forEach(addPriorTransfer);
		}
		function addPriorTransfer(trans) {
			if (!trans?.nrid || usedSendNrids.has(trans.nrid)) return false;
			priorTransfers.push(trans);
			usedSendNrids.add(trans.nrid);
			return true;
		}

		let carryOverDeduct = 0;

		// Now, examine each of these dates, starting with the oldest one
		multipleSameDates.forEach(function (d) {
			console.log('### SAME DATE ###: ', moment.unix(d).utc().format('DD-MMM-YY'));

			// First handle any earlier single Send that occurs on a given date by itself
			let singleSendTransactions = giftTransactions.filter((g) => sameClassAndSender(g) && earlierDate(g));
			singleSendTransactions.forEach((singleSend) => {
				let targetQty = Number(singleSend.quantity);
				if (handleNotesMatch(singleSend, targetQty, Number(singleSend.date))) {
					console.log('NOTES MATCH Transaction: ', singleSend);
					singleSend.tranche = c + '-' + singleSend.date;
					addPriorTransfer(singleSend);
					giftTransactions = giftTransactions.filter((g) => g.nrid != singleSend.nrid);
				} else if (handlePerfectFit(singleSend, targetQty, Number(singleSend.date) - 43200, false)) {
					// 1/2 day
					console.log('SINGLE SEND Transaction: ', singleSend);
					singleSend.tranche = c + '-' + singleSend.date;
					addPriorTransfer(singleSend);
					giftTransactions = giftTransactions.filter((g) => g.nrid != singleSend.nrid);
				} else if (handlePerfectFit(singleSend, targetQty, Number(singleSend.date), false)) {
					console.log('SINGLE SEND Transaction: ', singleSend);
					singleSend.tranche = c + '-' + singleSend.date;
					addPriorTransfer(singleSend);
					giftTransactions = giftTransactions.filter((g) => g.nrid != singleSend.nrid);
				} else {
					console.log('*** NO SEND-ONLY MATCH ***');
					let safety = 0;
					let firstHistoric;
					// #6191: prefer a candidate whose own recorded tax basis matches what the sender
					// declared for this send (getSenderDeclaredBasis()/senderBasisMatchesCandidate()) --
					// otherwise this loop picks purely by array position (reversed), which can silently
					// draw shares from the wrong basis lot when an unrelated same-day receive is also in
					// play. Falls back to the original reversed-array search when no basis is declared or
					// no candidate matches it.
					const declaredBasis = getSenderDeclaredBasis(singleSend);
					while (targetQty > 0 && safety < 20) {
						safety++;
						let firstPrior =
							(declaredBasis != null &&
								giftTransactions.find(
									(g) =>
										sameClassAndReceiver(g) &&
										Number(g.date) <= Number(singleSend.date) &&
										eligibleAsOf(g, singleSend.date) &&
										isKnownNumber(g.taxBasis) &&
										Math.abs(Number(g.taxBasis) - declaredBasis) < 0.005
								)) ||
							giftTransactions
								.slice()
								.reverse()
								.find((g) => sameClassAndReceiver(g) && Number(g.date) <= Number(singleSend.date) && eligibleAsOf(g, singleSend.date));
						if (firstPrior) {
							if (firstPrior.quantity > targetQty) {
								firstPrior.quantity = (Number(firstPrior.quantity) - targetQty).toString();
								firstHistoric = JSON.parse(JSON.stringify(firstPrior));
								firstHistoric.quantity = targetQty.toString();
								firstHistoric.tranche = c + '-' + singleSend.date;
								historicHoldings.push(firstHistoric);
								tagMatch(firstHistoric, 'E', singleSend);
								targetQty = 0;
							} else {
								targetQty -= Number(firstPrior.quantity);
								firstPrior.tranche = c + '-' + singleSend.date;
								historicHoldings.push(firstPrior);
								tagMatch(firstPrior, 'E', singleSend);
								giftTransactions = giftTransactions.filter((g) => g.nrid != firstPrior.nrid);
							}
						}
					}
					console.log('SINGLE SEND Transaction: ', singleSend);
					singleSend.tranche = c + '-' + singleSend.date;
					addPriorTransfer(singleSend);
					giftTransactions = giftTransactions.filter((g) => g.nrid != singleSend.nrid);
				}
			});

			let sameDateTransactions = giftTransactions.filter((t) => t.date == d && t.shareClass == c);
			let sameDateSends = sameDateTransactions.filter((t) => t.sender == giftUserId);
			let sameDateReceives = sameDateTransactions.filter((t) => t.receiver == giftUserId);

			const certPairs = CERT_COLUMN ? matchSameDayByCertificate(sameDateTransactions) : [];
			if (certPairs.length) {
				certPairs.forEach(({ send, receive }) => {
					send.tranche = c + '-' + d;
					receive.tranche = c + '-' + d;
					addPriorTransfer(send);
					const certPairHolding = { ...receive, tranche: c + '-' + d, quantity: send.quantity };
					tagMatch(certPairHolding, 'A');
					historicHoldings.push(certPairHolding);

					giftTransactions = giftTransactions.filter((g) => g.nrid != send.nrid && g.nrid != receive.nrid);
				});

				sameDateTransactions = giftTransactions.filter((t) => t.date == d && t.shareClass == c);
				sameDateSends = sameDateTransactions.filter((t) => t.sender == giftUserId);
				sameDateReceives = sameDateTransactions.filter((t) => t.receiver == giftUserId);
			}

			// Next process any notes-match transfers (remove as we go)
			const descMatchedSendNrids = new Set();
			// IMPORTANT: iterate a snapshot because we mutate giftTransactions inside the loop
			for (const send of [...sameDateSends]) {
				if (handleNotesMatch(send, Number(send.quantity), Number(send.date))) {
					console.log('NOTES MATCH Transaction: ', send);
					send.tranche = c + '-' + send.date;
					addPriorTransfer(send);
					// Remove the SEND from the active pool so later branches don't touch it
					descMatchedSendNrids.add(send.nrid);
				}
			}
			// Apply removals once (avoids mid-loop topology weirdness)
			if (descMatchedSendNrids.size) giftTransactions = giftTransactions.filter((t) => !descMatchedSendNrids.has(t.nrid));
			// Recompute same-day lists after removing desc-matched sends
			sameDateTransactions = giftTransactions.filter((t) => t.date == d && t.shareClass == c);
			sameDateSends = sameDateTransactions.filter((t) => t.sender == giftUserId);
			sameDateReceives = sameDateTransactions.filter((t) => t.receiver == giftUserId);

			// Next look for same day cancel/reissue (e.g. lost certificate)
			if (isEDM && sameDateSends.length && sameDateReceives.length >= 1) releaseRestrictedEquity(sameDateTransactions);

			const PREFER_CERT_LINEAGE = CERT_COLUMN && !isASP;
			const hasSelfContainedCertFlow = isSelfContainedCertificateFlow(sameDateTransactions);
			const shouldApplyCertLineage = PREFER_CERT_LINEAGE && !hasSelfContainedCertFlow && sameDateSends.length;
			if (shouldApplyCertLineage) cancelReissueCertificate(sameDateTransactions);

			// Next, more complex scenarios
			sameDateTransactions = giftTransactions.filter((t) => t.date == d && t.shareClass == c);
			sameDateTransactions.sort((a, b) => Number(b.quantity) - Number(a.quantity));
			sameDateSends = sameDateTransactions.filter((t) => t.sender == giftUserId);
			sameDateReceives = sameDateTransactions.filter((t) => t.receiver == giftUserId);
			let sameDateSenders = [...new Set(sameDateTransactions.map((t) => t.sender))];
			let sameDateReceivers = [...new Set(sameDateTransactions.map((t) => t.receiver))];
			let sameDateSendQtySum = sameDateSends.reduce((a, b) => a + Number(b.quantity), 0);
			let sameDateReceiveQtySum = sameDateReceives.reduce((a, b) => a + Number(b.quantity), 0);
			let sameDateSendAmtSum = sameDateSends.reduce((a, b) => a + Number(b.quantity) * toNumber(b.sharePrice), 0);
			let sameDateReceiveAmtSum = sameDateReceives.reduce((a, b) => a + Number(b.quantity) * toNumber(b.sharePrice), 0);
			let sameDateNetQty = Math.abs(sameDateSendQtySum - sameDateReceiveQtySum);
			let sameDateNetAmt = Math.abs(sameDateSendAmtSum - sameDateReceiveAmtSum);
			// console.log("sameDate: ", d);
			// console.log("sameDateTrans: ", sameDateTransactions);
			// console.log("Senders: ", sameDateSenders);
			// console.log("Receivers: ", sameDateReceivers);
			// console.log("Sends: ", sameDateSends);
			// console.log("Receives: ", sameDateReceives);
			// console.log("SendQtySum: ", sameDateSendQtySum);
			// console.log("ReceiveQtySum: ", sameDateReceiveQtySum);
			// console.log("SendAmtSum: ", sameDateSendAmtSum);
			// console.log("ReceiveAmtSum: ", sameDateReceiveAmtSum);

			let isSwapOrSplit = false;
			if (sameDateSends.length == 0) {
				// ONE WAY recieve transactions - do nothing
				console.log('ONE-WAY Receiving: ', sameDateReceives);
			} else if (sameDateReceives.length == 0) {
				// ONE WAY send transactions - look for matches
				console.log('ONE-WAY Sending: ', sameDateSends);

				if (moment.unix(Number(d)).utc().format('YYYY-MM-DD') == '2015-04-08' && c == 'C') {
					console.log(
						'AL sameDateSends:',
						sameDateSends.map((s) => ({
							nrid: s.nrid,
							notes: s.notes,
							quantity: s.quantity,
						}))
					);
				}

				// First try finding old certificates, where each exactly matches one of the Sends
				let exactMatches = oneForOneMatches(sameDateSends);
				if (exactMatches) return;

				// Attempt 1 - first look for one prior Receive that exactly matches the collection of Sends
				let exactMatchTrans = giftTransactions.find(
					(g) =>
						sameClassAndReceiver(g) &&
						earlierDate(g) &&
						eligibleAsOf(g, d) &&
						sameQty(g, sameDateSendQtySum) &&
						!(sameDateSends.length === 1 && cancellationCertMismatch(sameDateSends[0].notes, g.notes))
				);
				if (exactMatchTrans) {
					let sendTransactions = giftTransactions.filter((g) => sameClassAndSender(g) && sameDate(g));
					sendTransactions.forEach((s) => (s.tranche = c + '-' + d));
					addPriorTransfers(sendTransactions);
					console.log('SEND Transactions: ', sendTransactions);
					// FIX: only remove the sends we *actually captured*, not everything <= date
					const sendNridSet = new Set(sendTransactions.map((t) => t.nrid).filter(Boolean));
					giftTransactions = giftTransactions.filter((g) => !sendNridSet.has(g.nrid));
					// giftTransactions = giftTransactions.filter(g => !sameClassAndSender(g) || !sameDate(g));
					giftTransactions = giftTransactions.filter((g) => g.nrid != exactMatchTrans.nrid);
					exactMatchTrans.tranche = c + '-' + d;
					tagMatch(exactMatchTrans, 'C', sameDateSends[0]);
					historicHoldings.push(exactMatchTrans);
				} else {
					// Need to tear things apart, Send by Send
					let closeMatchTrans;
					sameDateSends.forEach((s) => {
						// Attempt 2 & 3 -Try to find one or more prior Receives that match this Send
						exactMatchTrans = giftTransactions.find(
							(g) =>
								sameClassAndReceiver(g) &&
								earlierDate(g) &&
								eligibleAsOf(g, s.date) &&
								sameQty(g, Number(s.quantity)) &&
								!cancellationCertMismatch(s.notes, g.notes) &&
								(notRestricted(g) || !isEDM)
						);
						if (exactMatchTrans || !parseTransfers()) {
							let sendTransaction = giftTransactions.filter((g) => g.nrid == s.nrid)[0];
							if (!sendTransaction) return;
							sendTransaction.tranche = c + '-' + d;
							addPriorTransfer(sendTransaction);
							giftTransactions = giftTransactions.filter((g) => g.nrid != s.nrid);
							if (exactMatchTrans) {
								// <- Good, a simple solution, all done (Attempt 2)
								giftTransactions = giftTransactions.filter((g) => g.nrid != exactMatchTrans.nrid);
								exactMatchTrans.tranche = c + '-' + d;
								tagMatch(exactMatchTrans, 'C', sendTransaction);
								historicHoldings.push(exactMatchTrans);
							} else if (!closeMatchTrans) {
								// <- Need to score "open" prior Receives to find the best one (Attempt 3 & 4)
								let openTransactions = giftTransactions.filter(
									(g) =>
										sameClassAndReceiver(g) &&
										earlierDate(g) &&
										!g.tranche &&
										eligibleAsOf(g, d) && // or sendTransaction.date ??
										(notRestricted(g) || !isEDM)
								);
								if (openTransactions.length) {
									// After finding the closest match, clone this Receive as a "Twin" and move on to the next Send for this date
									closeMatchTrans = scoreMatches(sendTransaction, openTransactions);
									console.log('CLOSE Match: ', closeMatchTrans);
									if (closeMatchTrans) {
										// need new object
										closeMatchTrans = JSON.parse(JSON.stringify(closeMatchTrans));
										// giftTransactions = giftTransactions.filter(g => g.nrid != bestMatchTrans.nrid);
										closeMatchTrans.tranche = c + '-' + d;
										if (!closeMatchTrans.before) closeMatchTrans.before = closeMatchTrans.quantity;
										closeMatchTrans.quantity = s.quantity;
										tagMatch(closeMatchTrans, 'E', sendTransaction);
										historicHoldings.push(closeMatchTrans);
									}
								}
							}
						}
						// For a close match (not exact), deduct the from the "Twin" the quantity of every Send
						if (closeMatchTrans) {
							let matchTwin = giftTransactions.find((h) => h.nrid == closeMatchTrans.nrid);
							if (!matchTwin.before) matchTwin.before = Number(matchTwin.quantity);
							matchTwin.quantity = Number(matchTwin.quantity) - Number(s.quantity);
							if (carryOverDeduct) {
								matchTwin.quantity += carryOverDeduct;
								carryOverDeduct = 0;
							}
							if (matchTwin.quantity < 0) {
								carryOverDeduct = matchTwin.quantity;
								matchTwin.quantity = 0;
							}
							matchTwin.quantity = matchTwin.quantity.toString();
						}
						closeMatchTrans = null; // reset
					});
				}
			} else if (sameDateReceivers.length < 3 && (sameDateNetQty < 0.1 || sameDateNetAmt < 0.1)) {
				if (sameDateNetQty < 0.1) {
					if (sameDateSenders.sort().toString() != sameDateReceivers.sort().toString()) {
						// Attempt 1 - it's a TRANSFER
						let uniqueSenders = sameDateSenders.filter((u) => !sameDateReceivers.includes(u));
						let uniqueReceivers = sameDateReceivers.filter((u) => !sameDateSenders.includes(u));
						console.log('TRANSFER Transactions: ', uniqueSenders.concat(uniqueReceivers));
					}
					// Attempt 1 - it's a SWAP
					isSwapOrSplit = true;
					parseTransfers();
				} else if (sameDateNetAmt < 0.1) {
					// Attempt 2 - it's a SPLIT
					isSwapOrSplit = true;
					parseTransfers();
				}
			} else {
				// Attempt 2 - it might be a Vermeer-style reissue transfer
				// Look for a pair transaction with the issuer (equal quantity, cancellation/re-issue)
				pairedQty = 0;
				let exactMatch = false;
				// Remove active certificates - NO!!
				// sameDateSends = sameDateSends.filter(s => !activeCertificate(s));
				sameDateSends.forEach((send) => {
					sameDateReceives.forEach((receive) => {
						if (quantitiesMatch(send.quantity, receive.quantity)) pairedQty += Number(receive.quantity);
					});
				});

				// First try finding old certificates, where each quantity exactly matches one of the Sends
				if (pairedQty && !exactMatch) exactMatch = oneForOneMatches(sameDateSends);

				// Next try to find one old certificate that is an exact match for the collection of Sends
				let exactMatchTrans = giftTransactions.find(
					(g) =>
						sameClassAndReceiver(g) &&
						earlierDate(g) &&
						sameQty(g, sameDateSendQtySum) &&
						!(sameDateSends.length === 1 && cancellationCertMismatch(sameDateSends[0].notes, g.notes))
				);
				if (exactMatchTrans) {
					giftTransactions = giftTransactions.filter((g) => g.nrid != exactMatchTrans.nrid);
					exactMatchTrans.tranche = c + '-' + d;
					tagMatch(exactMatchTrans, 'C', sameDateSends[0]);
					historicHoldings.push(exactMatchTrans);
					// Remove all of the associated send transactions
					let sendTransactions = giftTransactions.filter((g) => sameClassAndSender(g) && thisDateOrEarlier(g));
					sendTransactions.forEach((s) => (s.tranche = c + '-' + d));
					sendTransactions.forEach((adjTrans) => {
						carryForwardBasisFields(adjTrans, exactMatchTrans);
					});
					addPriorTransfers(sendTransactions);
					console.log('SEND Transactions: ', sendTransactions);
					// FIX: only remove the sends we *actually captured*, not everything <= date
					const sendNridSet = new Set(sendTransactions.map((t) => t.nrid).filter(Boolean));
					giftTransactions = giftTransactions.filter((g) => !sendNridSet.has(g.nrid));
					// giftTransactions = giftTransactions.filter(g => !sameClassAndSender(g) || !thisDateOrEarlier(g));
					sameDateReceives.forEach((adjTrans) => {
						carryForwardBasisFields(adjTrans, exactMatchTrans);
					});
					exactMatch = true;
				}

				let handledByParseTransfers = false;

				// If that fails, start rolling up old certificates using stub logic below
				if (pairedQty && !exactMatch) handledByParseTransfers = parseTransfers();

				// It's not a Vermeer-style reissue...
				if (!pairedQty && sameDateReceives.length == 1 && sameDateSends.length > 1) {
					// If net sending (no STUB), bail out.  Not sure how to handle if mult-legged....
					if (sameDateReceiveAmtSum > sameDateSendAmtSum + 1) {
						console.log('NET Receive Amount: ', sameDateReceiveAmtSum - sameDateSendAmtSum);
					}
					// It's a STUB
					console.log('NET Send Amount: ', sameDateSendAmtSum - sameDateReceiveAmtSum);
					handledByParseTransfers = parseTransfers();
				}

				// Finally look for cancel/reissue certificate number
				if (CERT_COLUMN && !handledByParseTransfers) canceledCertificate(sameDateSends);
			}

			function matchSameDayByCertificate(sameDateTransactions) {
				const sends = sameDateTransactions.filter((t) => t.sender == giftUserId);
				const receives = sameDateTransactions.filter((t) => t.receiver == giftUserId);

				const usedReceiveNrids = new Set();
				const exactPairs = [];

				sends.forEach((send) => {
					const sendRefs = parseCertificateRefs(send.notes);
					const sendCert = sendRefs.newCert || (sendRefs.sourceCerts?.length == 1 ? sendRefs.sourceCerts[0] : '');
					if (!sendCert) return;

					const match = receives.find((r) => {
						if (usedReceiveNrids.has(r.nrid)) return false;

						const receiveRefs = parseCertificateRefs(r.notes);
						const receiveCert = receiveRefs.newCert || (receiveRefs.sourceCerts?.length == 1 ? receiveRefs.sourceCerts[0] : '');
						if (!receiveCert) return false;

						return receiveCert == sendCert && quantitiesMatch(r.quantity, send.quantity);
					});

					if (match) {
						usedReceiveNrids.add(match.nrid);
						exactPairs.push({ send, receive: match });
					}
				});

				return exactPairs;
			}

			function isSelfContainedCertificateFlow(transactions) {
				const getSourceCert = (notes) => {
					const match = notes?.match(/from (\d+)/);
					if (match) return match[1];
					const fallback = notes?.match(/\(#?(\d+)/);
					return fallback ? fallback[1] : null;
				};
				let sourceCerts = new Set();
				transactions.forEach((t) => {
					if (t.sender !== 'asplundh') {
						const cert = getSourceCert(t.notes);
						if (cert) sourceCerts.add(cert);
					}
				});
				return sourceCerts.size === 1;
			}

			function releaseRestrictedEquity(sameDaySends) {
				console.log('Begining sameDateSends: ', sameDaySends);
				// First try finding certificates, where each exactly matches one of the Sends
				let nonMatch;
				let matchCount = 0;
				sameDaySends.forEach((send) => {
					if (!toIssuer(send)) return;
					console.log('This Send: ', send);
					let exactMatches = giftTransactions.filter(
						(g) =>
							send.nrid != g.nrid &&
							quantitiesMatch(send.quantity, g.quantity) &&
							sameClassAndReceiver(g) &&
							sameDate(g) &&
							sameCertificate(g.notes, send.notes)
					);
					let exactMatch = !exactMatches ? null : exactMatches.length == 1 ? exactMatches[0] : scoreMatches(send, exactMatches);
					console.log('Very close match: ', exactMatch);
					if (exactMatch) {
						let sendQty = Number(send.quantity);
						let otherSends = giftTransactions.filter((g) => send.nrid != g.nrid && sameClassAndSender(g) && sameDate(g) && !isReleaseLike(g));
						console.log('Other sends: ', otherSends);
						let otherSend = otherSends.length == 1 ? otherSends[0] : scoreDifferences(send, otherSends);
						let otherSendQty = otherSend ? Number(otherSend.quantity) : 0;
						// if (otherSends.length) otherSendQty = otherSends.reduce((sum, g) => sum + Number(g.quantity), 0);

						// Get the other receive tranche, the non-match
						let nonMatches = giftTransactions.filter(
							(g) =>
								g.nrid !== exactMatch.nrid &&
								sameClassAndReceiver(g) &&
								thisDateOrEarlier(g) &&
								eligibleAsOf(g, send.date) &&
								Number(g.quantity) >= sendQty
						);
						if (nonMatches?.length) {
							// handle the nonMatch as new object
							nonMatch = JSON.parse(JSON.stringify(nonMatches.length == 1 ? nonMatches[0] : scoreDifferences(send, nonMatches)));
							let nonMatchQty = Number(nonMatch.quantity);
							// If excess quantity, split the NON MATCH. otherwise remove the NON MATCH from current gift transactions
							if (nonMatchQty < sendQty || otherSendQty > sendQty) return false;
							else {
								let origNonMatch = giftTransactions.find((g) => g.nrid == nonMatch.nrid);
								origNonMatch.quantity = (nonMatchQty - sendQty).toString();
								send.quantity = (sendQty - otherSendQty).toString();
								exactMatch.quantity = send.quantity;
							}

							nonMatch.quantity = sendQty.toString();
							nonMatch.tranche = c + '-' + d;
							tagMatch(nonMatch, nonMatches.length == 1 ? 'C' : 'E', send);
							// 'G' (sender-declared basis) is the stronger signal -- only promote to 'H' if G didn't already win.
							if (nonMatch._matchKey !== 'G' && noteDateMatchesCandidate(send, nonMatch)) nonMatch._matchKey = 'H';
							historicHoldings.push(nonMatch);

							matchCount++;
							processSendTransaction(send);
							// if (otherSends.length) otherSends.forEach(processSendTransaction);
							if (otherSend) processSendTransaction(otherSend);
						}
					}
				});
				console.log('Ending sameDateSends: ', sameDaySends);
				return matchCount > 0;

				function processSendTransaction(g) {
					g.tranche = c + '-' + d;
					giftTransactions = giftTransactions.filter((x) => x.nrid !== g.nrid);
					carryForwardBasisFields(g, nonMatch);
					addPriorTransfer(g);
					console.log('SEND Transaction: ', g);
					sameDateSends = sameDateSends.filter((s) => s.nrid !== g.nrid);
				}
			}

			function cancelReissueCertificate(sameDateSends) {
				console.log('sameDateSends: ', sameDateSends);
				// First try finding certificates, where each exactly matches one of the Sends
				let matchCount = 0;
				sameDateSends.forEach((send) => {
					if (!toIssuer(send)) return;
					console.log('This Send: ', send);
					let exactMatches = giftTransactions.filter(
						(g) =>
							send.nrid != g.nrid &&
							quantitiesMatch(send.quantity, g.quantity) &&
							sameClassAndReceiver(g) &&
							sameDate(g) &&
							sameCertificate(g.notes, send.notes)
					);
					let exactMatch = !exactMatches ? null : exactMatches.length == 1 ? exactMatches[0] : scoreMatches(send, exactMatches);
					console.log('SAME Certificate: ', exactMatch);
					if (exactMatch) {
						matchCount++;
						send.tranche = c + '-' + d;
						// Remove the SEND from current gift transactions
						giftTransactions = giftTransactions.filter((g) => g.nrid != send.nrid);
						let matches = giftTransactions.filter(
							(g) =>
								send.nrid != g.nrid &&
								quantitiesMatch(send.quantity, g.quantity) &&
								sameClassAndReceiver(g) &&
								sameDate(g) &&
								sameCertificate(g.notes, send.notes)
						);
						if (matches.length) {
							// there should only be one match, alert otherwise??
							let match = matches.length == 1 ? matches[0] : scoreMatches(send, matches);
							match.tranche = c + '-' + d;
							// Add the MATCH to historic holdings
							tagMatch(match, 'A');
							historicHoldings.push(match);
							// Remove the MATCH from current gift transactions
							giftTransactions = giftTransactions.filter((g) => g.nrid != match.nrid);
							carryForwardBasisFields(send, match);
							// Add the SEND to prior transfers
							addPriorTransfer(send);
							console.log('SEND Transaction: ', send);
						}
					}
				});
				return matchCount > 0;
			}

			function canceledCertificate(sameDateSends) {
				// First, see whether this path applies at all
				let foundAny = sameDateSends.some((send) => {
					let exactMatches = giftTransactions.filter(
						(g) =>
							quantitiesMatch(send.quantity, g.quantity) &&
							sameClassAndReceiver(g) &&
							earlierDate(g) &&
							eligibleAsOf(g, send.date) &&
							sameCertificate(g.notes, send.notes)
					);
					let exactMatch = !exactMatches ? null : exactMatches.length == 1 ? exactMatches[0] : scoreMatches(send, exactMatches);
					return !!exactMatch;
				});

				if (!foundAny) return false;

				// Process the whole send set ONCE
				sameDateSends.forEach((s) => {
					s.tranche = c + '-' + d;
					giftTransactions = giftTransactions.filter((g) => g.nrid != s.nrid);

					let matches = giftTransactions.filter(
						(g) => sameClassAndReceiver(g) && earlierDate(g) && eligibleAsOf(g, s.date) && sameCertificate(g.notes, s.notes)
					);

					if (!matches.length) {
						matches = giftTransactions.filter(
							(g) => quantitiesMatch(s.quantity, g.quantity) && sameClassAndReceiver(g) && sameDate(g) && sameCertificate(g.notes, s.notes)
						);
					}

					if (matches.length) {
						let match = matches.length == 1 ? matches[0] : scoreMatches(s, matches);
						match.tranche = c + '-' + d;
						tagMatch(match, 'A');
						historicHoldings.push(match);
						giftTransactions = giftTransactions.filter((g) => g.nrid != match.nrid);
						carryForwardBasisFields(s, match);
					}
				});

				addPriorTransfers(sameDateSends);
				console.log('SEND Transactions: ', sameDateSends);
				return true;
			}

			// A cancellation's quantity can coincidentally match an unrelated transaction's.
			// If the cancellation names real cert(s), require the candidate to share one.
			function cancellationCertMismatch(sendNotes, candidateNotes) {
				if (!/\bcancel/i.test(sendNotes || '')) return false;
				const sendRefs = parseCertificateRefs(sendNotes);
				const candRefs = parseCertificateRefs(candidateNotes);
				const sendHasCert = sendRefs.newCert || sendRefs.sourceCerts.length;
				const candHasCert = candRefs.newCert || candRefs.sourceCerts.length;
				if (!sendHasCert || !candHasCert) return false;
				return !sameCertificate(candidateNotes, sendNotes);
			}

			function oneForOneMatches(sameDateSends) {
				// First try finding old certificates, where each exactly matches one of the Sends
				let matchCount = 0;
				// need clone of giftTransactions to handle potential duplicate quantities
				let giftClone = JSON.parse(JSON.stringify(giftTransactions));
				if (isEDM) giftClone = giftClone.filter((g) => notRestricted(g));
				sameDateSends.forEach((send) => {
					let exactMatches = giftClone.filter(
						(g) =>
							!activeCertificate(g) &&
							sameClassAndReceiver(g) &&
							earlierDate(g) &&
							eligibleAsOf(g, send.date) &&
							(notRestricted(g) || !isEDM) &&
							sameQty(g, Number(send.quantity))
					);
					let exactMatch = !exactMatches ? null : exactMatches.length == 1 ? exactMatches[0] : scoreMatches(send, exactMatches);
					console.log('EXACT Match: ', exactMatch);
					if (exactMatch) {
						giftClone = giftClone.filter((g) => g.nrid != exactMatch.nrid);
						matchCount++;
					}
				});

				if (moment.unix(Number(d)).utc().format('YYYY-MM-DD') == '2015-04-08' && c == 'C') {
					console.log('AL oneForOne matchCount:', matchCount, 'needed:', sameDateSends.length);
				}

				if (matchCount == sameDateSends.length) {
					sameDateSends.forEach((s) => {
						s.tranche = c + '-' + d;
						giftTransactions = giftTransactions.filter((g) => g.nrid != s.nrid);
						let matches = giftTransactions.filter(
							(g) =>
								!activeCertificate(g) &&
								sameClassAndReceiver(g) &&
								earlierDate(g) &&
								eligibleAsOf(g, s.date) &&
								(notRestricted(g) || !isEDM) &&
								sameQty(g, Number(s.quantity))
						);
						if (matches.length) {
							let match = matches.length == 1 ? matches[0] : scoreMatches(s, matches);
							match.tranche = c + '-' + d;
							tagMatch(match, matches.length == 1 ? 'C' : 'E', s);
							historicHoldings.push(match);
							giftTransactions = giftTransactions.filter((g) => g.nrid != match.nrid);
							carryForwardBasisFields(s, match);
						}
					});
					addPriorTransfers(sameDateSends);
					console.log('SEND Transactions: ', sameDateSends);
					return true;
				}
				return false;
			}

			// This method identifies the best combination of Prior Receives to match a collection of Sends
			// It also handles swaps and splits, if identified
			function parseTransfers() {
				let splitRatio = 1;
				if (isSwapOrSplit) {
					splitRatio = sameDateReceiveQtySum / sameDateSendQtySum;
					console.log('SPLIT Ratio: ', splitRatio);
				}
				// If stub, get stub info
				let stubs = sameDateTransactions.filter((b) => b.receiver == giftUserId);
				// let stubMatches = sameDateTransactions.filter(m => sameClassAndSender(m));
				let stubMatches = giftTransactions.filter((m) => sameClassAndSender(m) && sameDate(m));
				// Remove active certificates - Maybe...
				// stubMatches = stubMatches.filter(s => !activeCertificate(s));
				console.log('STUB Transactions: ', stubs);
				console.log('STUB Matches: ', stubMatches);

				// For each re-issue, remove the returned cert(s)
				// Find matching old cert(s), and calculate gross cost
				// Move old cert(s) to Prior list

				let foundGroupFit;
				let priorGrossQty = stubMatches.reduce((a, b) => a + Number(b.quantity), 0);
				let priorGrossCost = stubMatches.reduce((a, b) => a + Number(b.quantity) * toNumber(b.sharePrice), 0);
				let newPrice = priorGrossQty ? priorGrossCost / priorGrossQty : 0;
				console.log('PRIOR grossQty = ' + priorGrossQty);
				console.log('PRIOR grossCost = ' + priorGrossCost);
				console.log('NEW price = ' + newPrice);
				if (!sameDateReceives.length) {
					// Look for perfect match
					// find and remove the prior receive transaction(s) that sum to targetQty
					let priorTrans = giftTransactions.filter((g) => thisDateOrEarlier(g) && sameClassAndReceiver(g));
					priorTrans = priorTrans.filter((p) => eligibleAsOf(p, d));
					// Remove active certificates
					priorTrans = priorTrans.filter((p) => !activeCertificate(p));
					priorTrans.sort((a, b) => (Number(a.date) > Number(b.date) ? -1 : 1)); // newest to oldest
					priorTrans.sort((a, b) => Number(b.quantity) - Number(a.quantity)); // largest to smallest
					console.log('PRIOR Transactions: ', priorTrans);
					minDiff = 999; // reset arbitrarily large starting number used in tranche analysis
					findSubset.callCount = 0;
					let perfectFit = findSubset(priorGrossQty, priorTrans);
					if (perfectFit) {
						foundGroupFit = true;
						console.log('PERFECT Fit: ', perfectFit);
						perfectFit.forEach((p) => (p.tranche = c + '-' + d));
						perfectFit.forEach((p) => tagMatch(p, perfectFit.length > 1 ? 'D' : 'C', stubMatches[0]));
						historicHoldings = historicHoldings.concat(perfectFit);
						let perfectNrids = new Set(perfectFit.map((item) => item.nrid));
						giftTransactions = giftTransactions.filter((g) => !perfectNrids.has(g.nrid));
						priorGrossQty = perfectFit.reduce((acc, val) => acc + Number(val.quantity), 0);
						priorGrossCost = perfectFit.reduce((acc, val) => acc + Number(val.quantity) * toNumber(val.sharePrice), 0);
					} else console.log('no further fit');

					// let thisTransaction = giftTransactions.find(g => g.nrid == trans.nrid);
					// if (thisTransaction) thisTransaction.sharePrice = (priorGrossCost / (isSwapOrSplit ? Number(thisTransaction.quantity) : priorGrossQty)).toString();
					// console.log("ADJUSTED Transaction: ", thisTransaction);
					// return perfectFit ? true : false;
				} else {
					let totalReceived = 0;
					let targetQuantity = priorGrossQty * splitRatio;
					sameDateReceives.forEach((receiveTran) => {
						if (foundGroupFit && totalReceived >= targetQuantity) return;
						// if (foundGroupFit) return; <-- FAILS with Walt at Mazzetti
						if (isSwapOrSplit) {
							priorGrossQty = Number(receiveTran.quantity) / splitRatio;
							console.log('SPLIT grossQty = ' + priorGrossQty);
						}
						if (handlePerfectFit(receiveTran, priorGrossQty, Number(d), isSwapOrSplit)) {
							totalReceived += Number(receiveTran.quantity);
							console.log('TOTAL received = ' + totalReceived);
							receiveTran.tranche = c + '-' + d;
							foundGroupFit = true;
						}
					});
				}
				if (foundGroupFit) {
					// remove all of the associated send transactions
					let sendTransactions = giftTransactions.filter((g) => sameClassAndSender(g) && sameDate(g));
					let sendSum = sendTransactions.reduce((a, b) => a + Number(b.quantity), 0);
					console.log('PRIOR GrossQty = ' + priorGrossQty, ' SEND Sum = ' + sendSum);
					sendTransactions.forEach((s) => (s.tranche = c + '-' + d));
					addPriorTransfers(sendTransactions);
					console.log('SEND Transactions: ', sendTransactions);
					giftTransactions = giftTransactions.filter((g) => !sameClassAndSender(g) || !sameDate(g));
				}
				return foundGroupFit ? true : false;
			}

			// Utility functions
			function toIssuer(trans) {
				return (
					trans.receiver == client ||
					String(trans.receiver || '').toLowerCase() == 'issuer' ||
					String(trans.receiverName || '').toLowerCase() == 'issuer' ||
					itsTreasury(trans.receiver, trans.receiverName)
				);
			}
			function fromIssuer(trans) {
				return (
					trans.sender == client ||
					String(trans.sender || '').toLowerCase() == 'issuer' ||
					String(trans.senderName || '').toLowerCase() == 'issuer' ||
					itsTreasury(trans.sender, trans.senderName)
				);
			}
			function thisDateOrEarlier(trans) {
				return Number(trans.date) <= Number(d);
			}
			function earlierDate(trans) {
				return Number(trans.date) < Number(d);
			}
			function sameDate(trans) {
				return Number(trans.date) == Number(d);
			}
			function sameQty(trans, qty) {
				return Number(trans.quantity) == qty;
			}

			function isReleaseLike(trans) {
				const s = trans.notes;
				if (!s) return false;
				const t = s.toLowerCase();
				// specific phrase first
				if (/release\s+of\s+unvested\s+shares\s+granted/.test(t)) return true;
				// broader release signal
				if (t.includes('release') && (t.includes('unvested') || t.includes('restricted'))) return true;
				return false;
			}

			// Check historicHoldings for negative quantities
			historicHoldings.forEach((h, i) => {
				if (Number(h.quantity) < 0) alert(`Negative quantity in historicHoldings[${i}]:`, h);
			});

			// Check giftTransactions for negative quantities
			giftTransactions.forEach((g, i) => {
				if (Number(g.quantity) < 0) alert(`Negative quantity in giftTransactions[${i}]:`, g);
			});
		});

		function allocateCarryOver() {
			let remaining = -carryOverDeduct; // Make positive for internal logic
			while (remaining > 0.0001) {
				// Step 1: get valid candidates with quantity > 0
				const candidates = giftTransactions.filter((gt) => {
					const qty = Number(gt.quantity);
					return !isNaN(qty) && qty > 0 && eligibleAsOf(gt, gt.date || 0);
				});

				// Step 2: pick tranche already being reduced
				let target = candidates.find((gt) => Number(gt.before) > Number(gt.quantity));

				// Step 3: otherwise pick by lowest basis + oldest date
				if (!target) {
					candidates.sort((a, b) => {
						const basisA = isKnownNumber(a._effectiveTaxBasis) ? Number(a._effectiveTaxBasis) : Infinity;
						const basisB = isKnownNumber(b._effectiveTaxBasis) ? Number(b._effectiveTaxBasis) : Infinity;
						if (basisA !== basisB) return basisA - basisB;
						return Number(a.date || 0) - Number(b.date || 0);
					});
					target = candidates[0];
				}

				if (!target) break; // Nothing valid left

				// Initialize .before if not already set
				if (target.before == null) target.before = Number(target.quantity);

				const qty = Number(target.quantity);
				const deductNow = Math.min(remaining, qty);

				target.quantity = (qty - deductNow).toFixed(6).replace(/\.?0+$/, '');
				remaining -= deductNow;
			}
		}

		// Must account for any remaining carryOverDeduct
		if (carryOverDeduct) allocateCarryOver();

		// For Edmund Optics, never include SARS, RSGs, or Awards
		if (isEDM) giftTransactions = giftTransactions.filter((g) => notRestricted(g));

		function carryForwardBasisFields(targetTrans, sourceTrans) {
			if (!sourceTrans) return;
			// #6144: tag when a value here didn't come from the transaction's own record -- feeds the 'F' Key.
			let carried = false;
			if (!isKnownNumber(targetTrans.sharePrice) && isKnownNumber(sourceTrans.sharePrice)) {
				targetTrans.sharePrice = sourceTrans.sharePrice;
				carried = true;
			}
			if (!isKnownNumber(targetTrans.taxBasis)) {
				if (isKnownNumber(sourceTrans.taxBasis)) {
					targetTrans.taxBasis = sourceTrans.taxBasis;
					carried = true;
				} else if (isKnownNumber(sourceTrans._effectiveTaxBasis)) {
					targetTrans.taxBasis = Number(sourceTrans._effectiveTaxBasis).toString();
					carried = true;
				}
			}
			if (carried) targetTrans._basisCarried = true;
			let basis = resolveEffectiveTaxBasis(targetTrans, clientData);
			targetTrans._effectiveTaxBasis = basis.value;
			targetTrans._basisSource = basis.source;
			console.log('ADJUSTED Transaction: ', targetTrans);
		}

		// This method looks for one or more prior transactions specified precisely in the send notes
		function handleNotesMatch(trans, targetQty, date) {
			let priorTrans = giftTransactions.filter((g) => Number(g.date) <= date && sameClassAndReceiver(g));
			priorTrans = priorTrans.filter((p) => eligibleAsOf(p, date));

			// NEW: explicit source-cert handling
			const explicitSourceCerts = CERT_COLUMN ? getExplicitSourceCertSet(trans.notes) : null;
			if (explicitSourceCerts) {
				let certMatches = priorTrans.filter((p) => {
					const lotCert = getLotCertToken(p.notes);
					return lotCert && explicitSourceCerts.has(lotCert);
				});

				if (certMatches.length) {
					let remainingTarget = Number(targetQty) || 0;
					const stagedItems = [];
					const stagedUpdates = [];

					certMatches.sort((a, b) => Number(a.date) - Number(b.date));

					certMatches.forEach((match) => {
						if (remainingTarget <= 0) return;

						const priorGrossQty = Number(match.quantity) || 0;
						const consumeQty = Math.min(remainingTarget, priorGrossQty);

						stagedItems.push({
							...match,
							tranche: c + '-' + trans.date,
							quantity: consumeQty.toString(),
						});

						stagedUpdates.push({
							nrid: match.nrid,
							remaining: priorGrossQty - consumeQty,
							grossQty: priorGrossQty,
						});

						remainingTarget -= consumeQty;
					});

					// IMPORTANT: commit only if the explicit cert list fully satisfies the send
					if (remainingTarget <= 0) {
						stagedItems.forEach((p) => tagMatch(p, 'A'));
						historicHoldings = historicHoldings.concat(stagedItems);

						stagedUpdates.forEach((u) => {
							// Epsilon-tolerant, not <= 0 -- float noise can leave remaining ~1e-16.
							if (u.remaining <= balanceTolerance(u.grossQty)) {
								giftTransactions = giftTransactions.filter((g) => g.nrid != u.nrid);
							} else {
								const idx = giftTransactions.findIndex((g) => g.nrid == u.nrid);
								if (idx >= 0) giftTransactions[idx].quantity = u.remaining.toString();
							}
						});

						let thisTransaction = giftTransactions.find((g) => g.nrid == trans.nrid);
						if (thisTransaction) carryForwardBasisFields(thisTransaction, certMatches[0]);
						return true;
					}

					// If cert-linked lots are insufficient, do NOTHING here and let later logic handle it
					return false;
				}
			}

			// EXISTING behavior for simpler notes
			let notesMatch = findNotesMatch(trans.notes, priorTrans);
			if (notesMatch) {
				console.log('NOTES MATCH: ', notesMatch);

				const priorGrossQty = Number(notesMatch.quantity) || 0;
				const consumeQty = Math.min(Number(targetQty) || 0, priorGrossQty);

				// Clone BEFORE pushing so we don't mutate the pushed reference
				const trancheItem = {
					...notesMatch,
					tranche: c + '-' + trans.date,
					quantity: consumeQty.toString(),
				};
				// Immutable List -> reassign
				tagMatch(trancheItem, 'B');
				historicHoldings.push(trancheItem);

				// Update/remove the ORIGINAL in giftTransactions
				const remaining = priorGrossQty - consumeQty;
				// #6144-follow-up: same epsilon-tolerant check as above -- see comment there.
				if (remaining <= balanceTolerance(priorGrossQty)) {
					giftTransactions = giftTransactions.filter((g) => g.nrid != notesMatch.nrid);
				} else {
					const idx = giftTransactions.findIndex((g) => g.nrid == notesMatch.nrid);
					if (idx >= 0) giftTransactions[idx].quantity = remaining.toString();
				}

				// Carry price to the current transaction
				let thisTransaction = giftTransactions.find((g) => g.nrid == trans.nrid);
				if (thisTransaction) carryForwardBasisFields(thisTransaction, notesMatch);
				return true;
			}

			console.log('no notes match');
			return false;
		}

		function getExplicitSourceCertSet(notesText) {
			const refs = parseCertificateRefs(notesText);
			return refs.sourceCerts?.length ? new Set(refs.sourceCerts) : null;
		}

		function getLotCertToken(notesText) {
			const refs = parseCertificateRefs(notesText);

			// For old lots like "Issuance (Cert 0002475)", use the cert on the lot itself
			if (refs.newCert) return refs.newCert;

			// If somehow the lot only parses as a single source cert, allow that too
			if (refs.sourceCerts?.length == 1) return refs.sourceCerts[0];

			return '';
		}

		function findNotesMatch(transNotes, priorTrans) {
			console.log('transNotes', transNotes);

			const tn = String(transNotes || '').trim();
			const tnLower = tn.toLowerCase();

			// Hard gate: only match notes that explicitly say "... from ...", to avoid
			// accidental substring matches on busy accounts.
			const hasFromPhrase = /\bfrom\b\s*[:\-]?\s+\S+/i.test(tn);
			if (!hasFromPhrase) {
				console.log("findNotesMatch: skipping (no '... from ...' phrase)");
				return false;
			}

			function extractPossibleDate(str) {
				if (!str) return null;
				const dateRegex = /(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\.?\s*\d{1,2},?\s*\d{2,4}|\b\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}\b/i;
				const match = str.match(dateRegex);
				return match ? match[0] : null;
			}

			const possibleDateString = extractPossibleDate(tn);
			const transMoment = possibleDateString ? moment(possibleDateString, dateFormats, true) : null;

			for (const t of priorTrans) {
				if (!t || !t.notes || !t.date) continue;

				const priorNotes = String(t.notes || '').trim();
				if (!priorNotes) continue;

				// Keep your original "notes contain prior notes" behavior
				const notesHit = tnLower.includes(priorNotes.toLowerCase());
				if (!notesHit) continue;

				let dateMatch = true; // allow notes-only when no parseable date
				if (transMoment && moment.unix(Number(t.date)).utc().isValid()) {
					const tMoment = moment.unix(Number(t.date)).utc();
					dateMatch = tMoment.isSame(transMoment, 'day');
				}

				if (dateMatch) {
					console.log('Matched prior transaction:', t);
					return t; // <- returns a single OBJECT
				}
			}

			return false;
		}

		// Character overlap similarity (basic fuzzy match)
		function areSimilar(a, b) {
			const setA = new Set(a.toLowerCase());
			const setB = new Set(b.toLowerCase());
			const overlap = [...setA].filter((c) => setB.has(c)).length;
			const avgLen = (setA.size + setB.size) / 2;
			return overlap / avgLen;
		}

		// true if this prior lot is eligible to be consumed as-of "asOf"
		function eligibleAsOf(priorLot, asOf) {
			const u = priorLot?._useAfter;
			if (!u) return true;
			if (u === 'LOCK') return false;

			const asOfSec = Number(asOf);
			const useAfterSec = Number(u);

			if (!Number.isFinite(asOfSec) || !Number.isFinite(useAfterSec)) return false;

			return asOfSec >= useAfterSec;
		}

		function sameCertificate(transNotes, notes) {
			if (!CERT_COLUMN) return areSimilar(transNotes, notes);

			// PRIOR EFFORTS
			// console.log("transNotes", (transNotes.match(/\(([^)]*?(\d+)[^)]*?)\)/) || [])[2])
			// console.log("notes", (notes.match(/\(([^)]*?(\d+)[^)]*?)\)/) || [])[2])
			// return (transNotes.match(/\((\d+)\)/) || [])[1] == (notes.match(/\((\d+)\)/) || [])[1];
			// if ((transNotes.match(/\(([^)]*?(\d+)[^)]*?)\)/) || [])[2] == (notes.match(/\(([^)]*?(\d+)[^)]*?)\)/) || [])[2]) {
			// 	console.log("***MATCH***");
			// 	return true;
			// } else return false;

			// Extract a cert token from the first (...) group; must contain a digit
			// (so "(Cancelled)" etc. don't match).
			const a = parseCertificateRefs(transNotes);
			const b = parseCertificateRefs(notes);

			console.log('CERT PARSE:', { a, b });

			// Best case: overlap of source cert lists
			if (a.sourceCerts.length || b.sourceCerts.length) {
				const aSet = new Set(a.sourceCerts.length ? a.sourceCerts : a.newCert ? [a.newCert] : []);
				const bSet = new Set(b.sourceCerts.length ? b.sourceCerts : b.newCert ? [b.newCert] : []);

				for (const cert of aSet) {
					if (bSet.has(cert)) {
						console.log('***MATCH via source overlap***');
						return true;
					}
				}
				return false;
			}

			// Simple fallback: compare single certs
			if (a.newCert && b.newCert && a.newCert == b.newCert) {
				console.log('***MATCH via single cert***');
				return true;
			}

			return false;
		}

		// This method takes a given Send and tries to find a collection of Prior Receives that fits perfectly
		function handlePerfectFit(trans, targetQty, date, isSwapOrSplit) {
			// Look for perfect match
			let priorGrossQty = 0;
			let priorGrossCost = 0;
			// find and remove the prior receive transaction(s) that sum to targetQty
			let priorTrans = giftTransactions.filter((g) => Number(g.date) <= date && sameClassAndReceiver(g));
			priorTrans = priorTrans.filter((p) => eligibleAsOf(p, date));
			// Remove active certificates
			priorTrans = priorTrans.filter((p) => !activeCertificate(p));
			// Remove restricted stock
			if (isEDM) priorTrans = priorTrans.filter((p) => !p.notes.startsWith('Restricted'));
			// Sort according to priority
			if (isVERM)
				priorTrans.sort((a, b) => (Number(a.date) > Number(b.date) ? 1 : -1)); // oldest to newest
			else {
				priorTrans.sort((a, b) => (Number(a.date) > Number(b.date) ? -1 : 1)); // newest to oldest
				priorTrans.sort((a, b) => Number(b.quantity) - Number(a.quantity)); // largest to smallest
			}
			console.log('PRIOR Transactions: ', priorTrans);
			// first try with only older transactions
			minDiff = 999; // reset arbitrarily large starting number used in tranche analysis
			findSubset.callCount = 0;
			let perfectFit = findSubset(
				targetQty,
				priorTrans.filter((g) => Number(g.date) < date)
			);
			// now include same-date transactions
			minDiff = 999; // reset arbitrarily large starting number used in tranche analysis
			findSubset.callCount = 0;
			if (!perfectFit) perfectFit = findSubset(targetQty, priorTrans);
			if (perfectFit) {
				console.log('PERFECT Fit: ', perfectFit);
				perfectFit.forEach((p) => (p.tranche = c + '-' + trans.date));
				perfectFit.forEach((p) => tagMatch(p, perfectFit.length > 1 ? 'D' : 'C', trans));
				historicHoldings = historicHoldings.concat(perfectFit);
				let perfectNrids = new Set(perfectFit.map((item) => item.nrid));
				giftTransactions = giftTransactions.filter((g) => !perfectNrids.has(g.nrid));
				priorGrossQty = perfectFit.reduce((acc, val) => acc + Number(val.quantity), 0);
				priorGrossCost = perfectFit.reduce((acc, val) => acc + Number(val.quantity) * toNumber(val.sharePrice), 0);
			} else console.log('no further fit');

			let thisTransaction = giftTransactions.find((g) => g.nrid == trans.nrid);
			if (thisTransaction) thisTransaction.sharePrice = (priorGrossCost / (isSwapOrSplit ? Number(thisTransaction.quantity) : priorGrossQty)).toString();
			console.log('ADJUSTED Transaction: ', thisTransaction);
			return perfectFit ? true : false;
		}

		// This subroutine finds a subset of Receives that sum exactly to a target quantity
		function findSubset(targetQty, priorTrans, subset = [], index = 0, currentSum = 0) {
			if (!findSubset.callCount) findSubset.callCount = 0;
			findSubset.callCount++;
			if (findSubset.callCount > 500000) return null; // hard stop

			// tolerate tiny decimal noise
			if (Math.abs(currentSum - targetQty) < 0.000001) return subset;

			let diff = Math.abs(targetQty - currentSum);
			if (diff < minDiff) {
				minDiff = diff;
				diffCount = 0;
				console.log('minDiff = ' + minDiff);
			}
			diffCount++;

			if (diff < 1 && diffCount > 99) return subset;

			if (currentSum > targetQty || index >= priorTrans.length) return null;

			let qty = Number(priorTrans[index].quantity) || 0;

			let includeCurrent = findSubset(targetQty, priorTrans, [...subset, priorTrans[index]], index + 1, currentSum + qty);
			if (includeCurrent) return includeCurrent;

			return findSubset(targetQty, priorTrans, subset, index + 1, currentSum);
		}

		// This method scores potential matches, and returns the one with the highest score
		function scoreMatches(transfer, historics) {
			let bestMatch;
			let bestScore = 0;
			if (historics.length) bestMatch = historics[0];
			historics.forEach((h) => {
				let score = areSimilar(transfer.notes, h.notes);
				// High-priority sender-declared-basis signal (2026-08-28, per Chris,
				// GT improvements #6144) -- outweighs the exact-quantity match (7)
				// below since an explicit sender assertion is more trustworthy than
				// any heuristic here.
				if (senderBasisMatchesCandidate(transfer, h)) score += 10;
				if (transfer.sharePrice == h.sharePrice) score += 3;
				if (quantitiesMatch(transfer.quantity, h.quantity)) score += 7;
				if (transfer.notes.toLowerCase().includes(h.notes.toLowerCase())) score += 5;
				// $0 transfers (gifts/distributions) have no real sale price to compare against --
				// this guard only makes sense for an actual paid transfer.
				if (Number(transfer.sharePrice) > 0 && Number(transfer.sharePrice) < Number(h.sharePrice)) score = 0;
				if (Number(h.quantity) > Number(transfer.quantity)) score += 1;
				if (Number(h.quantity) < Number(transfer.quantity)) score = 0;
				if (Number(transfer.date) < Number(h.date)) score = 0;
				// if (activeCertificate(h)) score = 0;
				if (score > bestScore) {
					bestScore = score;
					bestMatch = h;
				}
			});
			return bestMatch;
		}

		function scoreDifferences(transfer, historics) {
			let bestMatch;
			let bestScore = -Infinity; // allow 0 scores
			if (historics.length) bestMatch = historics[0];

			historics.forEach((h) => {
				let score = areSimilar(transfer.notes, h.notes);
				if (score > 0.9) score = 0; // handle EO Releases

				// Extract dates from notes
				const transferNoteDate = extractDate(transfer.notes);
				const historicNoteDate = extractDate(h.notes);

				// Strongly prefer a note-date ≈ record .date
				const dateBoost =
					(transferNoteDate && isCloseInTime(transferNoteDate, h.date) ? 1.0 : 0) +
					(historicNoteDate && isCloseInTime(historicNoteDate, transfer.date) ? 0.5 : 0);

				// Small bonus if share prices match (helps pick the Grant @ 318.03)
				const priceBoost = +transfer.sharePrice && +h.sharePrice && Math.abs(+transfer.sharePrice - +h.sharePrice) < 0.005 ? 0.5 : 0;

				// High-priority sender-declared-basis signal (2026-08-28, per Chris,
				// GT improvements #6144) -- outweighs dateBoost/priceBoost above.
				const basisBoost = senderBasisMatchesCandidate(transfer, h) ? 2.0 : 0;

				// Down-weight loose text similarity; up-weight structured signals
				const totalScore = score * 0.3 + dateBoost + priceBoost + basisBoost;

				if (totalScore > bestScore) {
					bestScore = totalScore;
					bestMatch = h;
				}
			});

			return bestMatch;

			// Extract timestamp (ms) from note string if any recognizable date found
			function extractDate(text) {
				const match = text.match(/(\d{1,4}[\/\-]\d{1,2}[\/\-]\d{1,4}|\w{3} \d{1,2}, \d{4})/);
				if (match) {
					const m = moment(match[0], dateFormats, true);
					if (m.isValid()) return m.valueOf(); // ms since epoch
				}
				return null;
			}

			// True if two dates are within ±5 days (input: ms, seconds)
			function isCloseInTime(msFromNote, dateStampSeconds) {
				const dateFromItem = Number(dateStampSeconds) * 1000;
				const delta = Math.abs(msFromNote - dateFromItem);
				return delta <= 5 * 24 * 60 * 60 * 1000; // ±5 days in ms
			}
		}

		function notRestricted(trans) {
			const note = trans.notes?.toLowerCase() || '';
			return !['restricted stock grant', 'stock award', 'appreciation rights grant'].some((term) => note.includes(term));
		}

		function activeCertificate(trans) {
			return /\(.*?#.*?\)/.test(trans.notes);
		}

		function sameClassAndSender(trans) {
			return trans.shareClass == c && trans.sender == giftUserId;
		}

		function sameClassAndReceiver(trans) {
			return trans.shareClass == c && trans.receiver == giftUserId;
		}
	});

	// dataOnly mode: filterViewGT/showDecimals DOM reads are skipped (same as the
	// giftHeadline block above); stringOptions just keeps its m0 default.
	let filterViewGT;
	stringOptions = m0;
	if (!dataOnly) {
		filterViewGT = document.getElementById('filterViewGiftTrans');
		if (document.getElementById('showDecimals').checked) stringOptions = m2;

		if (true) {
			//includedClasses.length <= 1) {
			document.getElementById('filterWrapViewGiftTrans').style.display = 'none';
		} else {
			document.getElementById('filterWrapViewGiftTrans').style.display = 'block';
			let labelViewGT = document.getElementById('labelViewGiftTrans');
			if (labelViewGT) labelViewGT.innerHTML = clientData.use_real_estate_language ? 'Property' : UFL ? 'Fund' : 'Share&nbsp;Class';

			if (isJMS || isACC) selectedClass = classes[0];
			else includedClasses.unshift('ALL');

			filterViewGT.innerHTML = `
				<option value="" disabled selected>Select</option>
				${includedClasses
					.map(
						(className, index) =>
							`
						<option ${index == 0 ? 'selected' : ''} value="${className}">${className}</option>
					`
					)
					.join('')}
				<label>Fund</label>

			`;
		}
	}

	let treasuryId;
	let giftSources;
	let lastCertNum;
	let nextCertNum;
	let recipientId;
	let recipientName;
	let currentTranche;
	let originalLastCertNum;

	let holders = [];
	let gifts = [' '];
	let theseGifts = {};
	const NEW_CODE = true;
	let trancheAutoTimers = {};
	theseGifts[' '] = ['', '', ,];

	let recipientSearchData = [];
	let recipientSearchIndex = -1;
	let recipientSearchMatches = [];

	// Remove giftTransactions with quantity == 0;  <-- NO !!
	// giftTransactions = giftTransactions.filter(g => g.quantity != "0");

	// #6108 (Ullico) fallback: if a class has exactly one leftover unconsumed entry
	// and its quantity disagrees with the precomputed "Shares Owned Now" balance,
	// correct that entry to match. No-op if matching fully reconciled or 2+ are left over.
	classes.forEach((c) => {
		const correctBalanceRaw = giftUserData?.clients?.[client]?.shares?.[c]?.approved;
		if (correctBalanceRaw == null) return;
		const correctBalance = Number(correctBalanceRaw);
		if (!Number.isFinite(correctBalance)) return;

		const leftover = giftTransactions.filter((g) => g.shareClass == c && g.receiver == giftUserId);
		if (leftover.length !== 1) return; // ambiguous or already empty -- leave alone

		const leftoverQty = Number(leftover[0].quantity);
		if (leftoverQty === correctBalance) return; // already reconciled, nothing to do

		console.warn(
			`giftTransfer (#6108 fallback): unreconciled tranche history for ${giftUserId}/${c} -- leftover entry shows ${leftoverQty}, precomputed balance is ${correctBalance}. Displaying the precomputed balance instead of the stale tranche quantity.`
		);
		leftover[0].quantity = correctBalance.toString();
		leftover[0]._reconciliationFallback = true; // marks this entry for anyone inspecting the data later
	});

	// In dataOnly mode, apply sorting and tranche letters, then return data
	if (dataOnly) {
		console.log('giftTransfer: dataOnly mode - sorting and assigning tranches for', shareholder);

		// Sort: non-zero quantities first, then zero quantities (same as renderUserGiftTransModal)
		let nonZero = giftTransactions.filter((t) => Number(t.quantity) != 0);
		let zeroQty = giftTransactions.filter((t) => Number(t.quantity) == 0).sort((a, b) => Number(b.date) - Number(a.date));
		giftTransactions = sortByClassThenBasisThenDateThenQty(nonZero).concat(zeroQty);
		historicHoldings = sortByClassThenBasisThenDateThenQty(historicHoldings);
		priorTransfers = sortByClassThenBasisThenDateThenQty(priorTransfers);

		// Sort historicHoldings by tranche (class-date format)
		historicHoldings.sort((a, b) => {
			if (!a.tranche || !b.tranche) return 0;
			let [classA, dateA] = a.tranche.toString().split('-');
			let [classB, dateB] = b.tranche.toString().split('-');
			dateA = Number(dateA);
			dateB = Number(dateB);
			if (dateA < dateB) return 1;
			if (dateA > dateB) return -1;
			if (classA < classB) return -1;
			if (classA > classB) return 1;
			return 0;
		});

		// Assign tranche letters (A, B, C, etc.) - same logic as renderUserGiftTransModal
		let letterCount = 0;
		giftTransactions.forEach(function (h) {
			let trancheLetter = numberToLetters(letterCount);
			if (!h.tranche || h.tranche.length > 3) {
				h.tranche = trancheLetter;
				letterCount++;
			}
			if (!h.before) h.before = h.quantity;
		});
		historicHoldings.forEach(function (h) {
			let trancheLetter = numberToLetters(letterCount);
			if (!h.tranche) {
				h.tranche = trancheLetter;
				letterCount++;
			} else if (h.tranche.length > 3) {
				let timeStamp = h.tranche;
				historicHoldings.forEach((k) => {
					if (k.tranche == timeStamp) {
						k.tranche = trancheLetter;
					}
				});
				priorTransfers.forEach((p) => {
					if (p.tranche == timeStamp) p.tranche = trancheLetter;
				});
				letterCount++;
			}
			if (!h.before) h.before = h.quantity;
		});

		console.log('giftTransfer: dataOnly mode - returning', giftTransactions.length, 'transactions');
		return {
			giftTransactions: JSON.parse(JSON.stringify(giftTransactions)),
			historicHoldings: JSON.parse(JSON.stringify(historicHoldings)),
			priorTransfers: JSON.parse(JSON.stringify(priorTransfers)),
			origTransactions: JSON.parse(JSON.stringify(origTransactions)),
		};
	}

	if (giftTransactions.length > 0 || NEW_CODE) showFilteredGiftTrans();

	function showFilteredGiftTrans() {
		console.log('origTransactions: ', origTransactions);
		console.log('giftTransactions: ', giftTransactions);
		console.log('historicHoldings: ', historicHoldings);
		console.log('priorTransfers: ', priorTransfers);
		// let filteredTransactions = isMAZ ? giftTransactions : origTransactions;
		let filteredTransactions = NEW_CODE ? giftTransactions.concat(historicHoldings).concat(priorTransfers) : origTransactions;
		filteredTransactions = filteredTransactions.filter(
			(t) => !(clientData.equity[t.shareClass] && clientData.equity[t.shareClass].hasOwnProperty('interestRate'))
		);
		if (selectedClass && selectedClass != 'ALL') {
			filteredTransactions = filteredTransactions.filter(function (item) {
				if (item.shareClass) return item.shareClass == selectedClass;
				else return true;
			});
		}
		renderUserGiftTransModal(filteredTransactions);
	}

	function renderUserGiftTransModal(filteredGiftTransactions) {
		// Put giftTransactions with quantityu == "0" at the end
		let nonZero = filteredGiftTransactions.filter((t) => Number(t.quantity) != 0);
		let zeroQty = filteredGiftTransactions.filter((t) => Number(t.quantity) == 0).sort((a, b) => Number(b.date) - Number(a.date));

		filteredGiftTransactions = sortByClassThenBasisThenDateThenQty(nonZero).concat(zeroQty);

		// Compile list of reference prices from Valuations
		let referenceVals = [];
		classes.forEach(function (c) {
			let clientEquity = clientData.equity[c];
			if (clientEquity) {
				if (clientEquity.valuations) {
					clientEquity.valuations.forEach(function (v) {
						let ref = {
							class: c,
							date: v.date,
							price: v.sharePrice,
							ref: v.referencePrice,
						};
						referenceVals.push(ref);
					});
				}
				if (clientEquity.transactions) CERT_COLUMN ||= clientEquity.transactions.some((t) => t.notes.includes('#'));
			}
		});

		// insert reference price if available
		let ttlTrans = 0;
		Object.keys(filteredGiftTransactions).forEach(function (trans, i) {
			let t = filteredGiftTransactions[trans];
			let refPrice = referenceVals.find(
				(v) => v.class == t.shareClass && Number(v.price) == Number(t.sharePrice) && Math.abs(Number(v.date) - Number(t.date)) <= 2 * 86400
			);
			if (refPrice) filteredGiftTransactions[i].referencePrice = refPrice.ref;
			ttlTrans += filteredGiftTransactions[i].receiver == shareholder ? Number(filteredGiftTransactions[i].quantity) : 0;
		});

		console.log(filteredGiftTransactions.filter((t) => !t.receiver));

		// Put giftTransactions with quantityu == "0" at the end
		nonZero = giftTransactions.filter((t) => Number(t.quantity) != 0);
		zeroQty = giftTransactions.filter((t) => Number(t.quantity) == 0).sort((a, b) => Number(b.date) - Number(a.date));

		giftTransactions = sortByClassThenBasisThenDateThenQty(nonZero).concat(zeroQty);
		historicHoldings = sortByClassThenBasisThenDateThenQty(historicHoldings);
		priorTransfers = sortByClassThenBasisThenDateThenQty(priorTransfers);
		historicHoldings.sort((a, b) => {
			let [classA, dateA] = a.tranche.split('-');
			let [classB, dateB] = b.tranche.split('-');
			dateA = Number(dateA);
			dateB = Number(dateB);
			if (dateA < dateB) return 1;
			if (dateA > dateB) return -1;
			if (classA < classB) return -1;
			if (classA > classB) return 1;
			return 0;
		});
		historicHoldings.sort((a, b) => {
			a.tranche > b.tranche ? -1 : 1;
		});
		console.log('POST giftTransactions: ', giftTransactions);
		console.log('POST historicHoldings: ', historicHoldings);
		console.log('POST priorTransfers: ', priorTransfers);

		let letterCount = 0;
		giftTransactions.forEach(function (h, i) {
			let trancheLetter = numberToLetters(letterCount);
			if (!h.tranche || h.tranche.length > 3) {
				h.tranche = trancheLetter;
				letterCount++;
			}
			if (!h.before) h.before = h.quantity;
		});
		historicHoldings.forEach(function (h, i) {
			let trancheLetter = numberToLetters(letterCount);
			if (!h.tranche) {
				h.tranche = trancheLetter;
				letterCount++;
			} else if (h.tranche.length > 3) {
				let timeStamp = h.tranche;
				historicHoldings.forEach((k) => {
					if (k.tranche == timeStamp) {
						k.tranche = trancheLetter;
					}
				});
				priorTransfers.forEach((p) => {
					if (p.tranche == timeStamp) p.tranche = trancheLetter;
				});
				letterCount++;
			}
			if (!h.before) h.before = h.quantity;
		});

		// If there remain unmatched prior transfers, start assigning them to tranches with the highest tax basis
		priorTransfers.forEach(function (p, i) {
			if (!p.tranche && nthAdmin) alert('Unmatched Prior Transfer: ' + JSON.stringify(p));
		});

		let ttlPrior = 0;
		let ttlHist = 0;
		priorTransfers.forEach((p) => (ttlPrior += Number(p.quantity)));
		historicHoldings.forEach((h) => (ttlHist += Number(h.quantity)));

		priorTransfers = priorTransfers.sort((a, b) => (a.tranche < b.tranche ? -1 : 1));
		stringOptions = isTBC ? m6 : m5;
		if (CERT_COLUMN) {
			// insert certificate number if available
			filteredGiftTransactions.forEach(function (f, index) {
				let certNumber = '';
				if (f.notes) {
					certNumber = getCertNumber(f.notes);
				}
				if (certNumber) {
					filteredGiftTransactions[index].certNumber = certNumber;
					let certSort = certNumber.split('-')[1];
					filteredGiftTransactions[index].certSort = Number((certSort ? certSort : certNumber).replace(/[^\d.]/g, ''));
				} else {
					filteredGiftTransactions[index].certNumber = '';
					filteredGiftTransactions[index].certSort = 0;
				}
			});
			historicHoldings.sort((a, b) => (a.certSort < b.certSort ? -1 : 1));
		}

		// Restore original sharePrice after temporary adjustments used during matching
		// giftTransactions.forEach((t) => {
		// 	let origTrans = origTransactions.find((o) => o.nrid == t.nrid);
		// 	if (origTrans) t.sharePrice = origTrans.sharePrice;
		// });

		// NEW VERSION
		// Restore original pricing / basis fields after temporary matching adjustments
		// giftTransactions.forEach((t) => {
		// 	let origTrans = origTransactions.find((o) => o.nrid == t.nrid);
		// 	if (!origTrans) return;
		// 	t.sharePrice = origTrans.sharePrice != null ? origTrans.sharePrice : '';
		// 	t.taxBasis = origTrans.taxBasis != null ? origTrans.taxBasis : '';
		// 	let basis = resolveEffectiveTaxBasis(t, clientData);
		// 	t._effectiveTaxBasis = basis.value;
		// 	t._basisSource = basis.source;
		// });

		const labelTaxBasis = language?.label_tax_basis || 'Tax Basis';

		function friendlyBasisTooltip(source) {
			switch (source) {
				case 'senderBasis':
					return 'Sender-Declared Basis';
				case 'taxBasis':
					return 'Recorded Tax Basis';
				case 'sharePrice':
					return 'From Share Price';
				case 'referencePrice':
					return 'From Reference Price';
				case 'valuationSharePrice':
					return 'From Valuation';
				default:
					return '';
			}
		}

		function basisColorClass(source) {
			switch (source) {
				case 'senderBasis':
					return 'text-primary'; // explicit sender assertion -- most trusted source
				case 'taxBasis':
					return ''; // default
				case 'sharePrice':
					return 'text-secondary'; // subtle
				case 'referencePrice':
					return 'text-warning'; // more noticeable
				case 'valuationSharePrice':
					return 'text-warning darken-2'; // strongest fallback
				default:
					return 'text-secondary';
			}
		}

		// #6144: superscript Key badge for a Historic Holdings/Prior Transfers line --
		// legend below always lists all Keys, regardless of what's used on this holder.
		function matchKeyBadge(key) {
			if (!key || !MATCH_KEY_NAMES[key]) return '';
			// Tooltip is just the abbreviation spelled out -- the full explanation lives in the legend below.
			return `<sup class="text-blue-400" style="cursor: help;" title="${MATCH_KEY_LABELS[key]} = ${MATCH_KEY_NAMES[key]}">${MATCH_KEY_LABELS[key]}</sup>`;
		}
		// Always show every Key, per Chris (2026-09-03): this is meant to educate admins on how
		// the matching engine reasons, not just report what happened to show up on this holder.
		const keysLegendHtml =
			`<div class="text-xs font-bold mb-0">Keys</div>` +
			Object.keys(MATCH_KEY_LABELS)
				.sort((a, b) => MATCH_KEY_LABELS[a].localeCompare(MATCH_KEY_LABELS[b]))
				.map(
					(k) =>
						`<div class="text-xs text-secondary mb-0"><b class="text-blue-400">${MATCH_KEY_LABELS[k]}</b> -- ${MATCH_KEY_NAMES[k]}: ${MATCH_KEY_DESCS[k]}</div>`
				)
				.join('');

		const taxBasisHeaderTooltip =
			'Also called Cost Basis: the value used for tax-reporting purposes.\nMay differ from Share Price/Price Paid. For gifts and transfers,\nTax Basis often carries over from the prior owner.';
		const gainLossTooltip =
			"Estimated gain/loss if sold today: (Current Value - Tax Basis) x Quantity.\nCurrent Value = latest available valuation. A red ST flag means < a year old.\nNo flag shown if > a year. Gift recipients generally inherit the sender's holding period.\nInternal estimate for admin review only, not tax advice.";

		let userTransactionsMarkup = `
			<div class="table-wrap card p-1 mt-4 mb-0 pb-1">
				<div class="sm-hz-scroll-no-margin">
					${
						!giftTransactions.length
							? `
						<div class="bg-grey-lightest rounded border-grey grey-text center-align p-1 mt-4">
							No available holdings to send. To create a new holding, click <a id="newHoldingButton" class="link-accent">here</a>.<br><span class="text-sm">Gift Transfers require at least one "open" (unmatched) Transaction with ${giftUserData.user_name} as the receiver.</span>
						</div>
					`
							: `
					<div id="showHideGainsDiv" class="mb-0 mr-6 right-align">
						<span id="showHideGains" class="text-xs link-accent">
							${showGainsColumns ? 'Hide Gain/Loss' : 'Show Gain/Loss'}
						</span>
					</div>
					<table id="historicalsSortTable" class="highlight table-content">
						<thead>
							<tr style="height: 30px;">
								<th class="text-sm center-align p-0" style="width:8%" data-sort-default>Tranche</th>
								${CLASS_COLUMN ? `<th class="text-sm center-align p-0" style="width:8%">Class</th>` : ``}
								${CERT_COLUMN ? `<th class="text-sm center-align p-0" style="width:8%">Cert #</th>` : ``}
								<th class="text-sm center-align p-0" style="width:15%">Date</th>
								<th class="text-sm p-0">Description</th>
								<!-- <th>Sender</th> -->
								<!-- <th>Receiver</th> -->
								<th class="text-sm center-align p-0" style="width:12%">Initial</th>
								<th class="text-sm center-align p-0" style="width:12%">Remaining</th>
								<th class="hidden">Exact Basis</th>
								<th class="hidden">Reference Basis</th>
								<th class="hidden">Qty * Exact Basis</th>
								<th class="hidden">Qty * Ref. Basis</th>
								<th class="center-align" title="${taxBasisHeaderTooltip}">
									${(() => {
										const words = labelTaxBasis.split(' ');
										return words.length > 1
											? `${words[0]}<i class="material-icons" style="font-size: 10px; vertical-align: middle; color: #999; margin-left: 2px;">info_outline</i>&ensp;&ensp;<br>${words.slice(1).join(' ')}`
											: `${labelTaxBasis}<i class="material-icons" style="font-size: 10px; vertical-align: middle; color: #999; margin-left: 2px;">info_outline</i>`;
									})()}
								</th>
								<th class="${showGainsColumns ? '' : 'hidden'} gains-column text-sm center-align p-0" style="width:10%" title="${gainLossTooltip}">Gain/<i class="material-icons" style="font-size: 10px; vertical-align: middle; color: #999; margin-left: 2px;">info_outline</i>&ensp;&ensp;<br>Loss</th>
							</tr>
						</thead>
						<tbody>
							${giftTransactions
								.map(
									(t, i) => `
								<tr id="historicsTable${i}" ${t.quantity == '0' ? 'class="hidden"' : ''} style="height: 10px;">
									<td class="p-0" data-sort='${lettersToNumbers(t.tranche)}'>
										<div class="shareholder-row" style="position: relative;">
											<p id="cbElem-${t.tranche}" class="${fromUser ? '' : 'selectShareholder'} ml-6 mt-0 mb-0" style="overflow: hidden; text-align: center;">
												${
													fromUser
														? ``
														: `
													<label>
														<input class="selectTrancheInput" type="checkbox" data-tranche=${t.tranche} data-class=${t.shareClass} />
														<span></span>
													</label>
												`
												}
											</p>
											<div id="imgElem-${t.tranche}" class="text-sm font-bold ${fromUser ? '' : 'shareholder-img'} center-align ml-6">${t.tranche}</div>
										</div>
									</td>
									${
										CLASS_COLUMN
											? `
										<td class="text-xs center-align p-0" style="width: 80px; max-width: 80px;">
											${t.shareClass}
										</td>
									`
											: ``
									}
									${
										CERT_COLUMN
											? `
										<td class="text-xs center-align p-0" style="width: 80px; max-width: 80px;" data-sort='${t.certSort}'>
											${t.certNumber}
										</td>
									`
											: ``
									}
									<td class="text-xs center-align p-0" data-sort='${t.date}'>
										${moment.unix(t.date).utc().format('MMM D, YYYY')}
									</td>
									<td class="text-xs p-0">
										${t.notes}
									</td>
									<td class="text-xs center-align p-0">
										${Number(t.before).toLocaleString('en-US', m5)}
									</td>
									<td id="quantity${t.tranche}" class="text-xs center-align p-0">
										${Number(t.quantity.replace(/[^.\d]/g, '')).toLocaleString('en-US', m5)}
									</td>
									<td class="hidden">
										${t.sharePrice && !isNaN(t.sharePrice) ? Number(t.sharePrice.replace(/[^.\d]/g, '')).toLocaleString('en-US', usp) : 'n.a.'}
									</td>
									<td class="hidden">
										${t.referencePrice && !isNaN(t.referencePrice) ? Number(t.referencePrice.replace(/[^.\d]/g, '')).toLocaleString('en-US', usp) : ''}
									</td>
									<td class="hidden">
										${t.quantity && t.sharePrice && !isNaN(t.sharePrice) ? (Number(t.quantity.replace(/[^.\d]/g, '')) * Number(t.sharePrice.replace(/[^.\d]/g, ''))).toLocaleString('en-US', usp) : ''}
									</td>
									<td class="hidden">
										${t.quantity && t.referencePrice && !isNaN(t.referencePrice) ? (Number(t.quantity.replace(/[^.\d]/g, '')) * Number(t.referencePrice.replace(/[^.\d]/g, ''))).toLocaleString('en-US', usp) : ''}
									</td>
									<td id="historicalBasis-${i}" class="text-xs center-align p-0 editable-tax-basis ${basisColorClass(t._basisSource)}"
										data-nrid="${t.nrid || ''}" data-share-class="${t.shareClass || ''}" data-row-index="${i}"
										title="${
											isAdmin && !VOA && !isExec && !isSupv
												? 'Click to edit -- entering a value here sets the actual, reported Tax Basis on this transaction.'
												: friendlyBasisTooltip(t._basisSource)
										}"
										${isAdmin && !VOA && !isExec && !isSupv ? 'contenteditable' : ''}>
										${
											/^(n\/?a|n\.a\.?)$/i.test(String(t._effectiveTaxBasis).trim())
												? t._effectiveTaxBasis
												: Number.isFinite(t._effectiveTaxBasis)
													? t._effectiveTaxBasis.toLocaleString('en-US', usd)
													: ''
										}
									</td>
									<td class="${showGainsColumns ? '' : 'hidden'} gains-column text-xs center-align p-0" title="${gainLossTooltip}">
										${(() => {
											const gl = computeGainLoss(t, clientData);
											if (!gl) return 'n.a.';
											const amountClass = gl.totalGain >= 0 ? 'text-primary' : 'text-error';
											const stBadge = gl.isLongTerm ? '' : '&nbsp;<span class="text-error text-xxs font-bold">ST</span>';
											return `<span class="${amountClass}">${gl.totalGain.toLocaleString('en-US', usd)}</span>${stBadge}`;
										})()}
									</td>
								</tr>
							`
								)
								.join('')}
							<tfoot id="historicalsFooter">
								<tr style="height: 36px;">
									<td id="consolidate" class="text-xs py-0 px-4" style="max-width: 50px; white-space: nowrap; overflow: visible; position: relative; z-index: 2;"></td>
									${CLASS_COLUMN ? `<td class="p-0"></td>` : ``}
									${CERT_COLUMN ? `<td class="p-0"></td>` : ``}
									<td class="p-0"></td>
									<td class="p-0"></td>
									<td id="totalBefore" class="text-xs font-bold center-align p-0"></td>
									<td id="totalAfter" class="text-xs font-bold center-align p-0"></td>
									<td class="hidden"></td>
									<td class="hidden"></td>
									<td class="hidden"></td>
									<td class="hidden"></td>
									<td id="avgCostBasis" class="text-xs font-bold center-align p-0"></td>
									<td id="totalGainLoss" class="${showGainsColumns ? '' : 'hidden'} gains-column text-xs font-bold center-align p-0"></td>
								</tr>
							</tfoot>
						</tbody>
					</table>
					`
					}
				</div>
				${
					priorTransfers.length
						? `
				<div id="showHidePriorsDiv" class="${stepwiseAnalysis?.checked ? 'mb-2' : 'mb-0'} mt-3 flex justify-between items-center">
					<span id="showHidePriors" class="text-xs ml-6 p-0 link-accent">
						${stepwiseAnalysis?.checked ? 'Hide Prior Tranches' : 'Show Prior Tranches'}
					</span>
				</div>
				`
						: `
				`
				}
				<table id="trancheTable" class="highlight table-content${stepwiseAnalysis?.checked ? '' : ' hidden'}">
					<thead>
						<tr>
							<th class="text-xs center-align p-05" style="width:4%" data-sort-default>Tranche</th>
							<th class="text-xs center-align p-05" style="width:6%">Class</th>
							<th class="text-xs p-05">Historic Holdings (source)</th>
							<th class="text-xs p-05">Prior Transfers (destination)</th>
						</tr>
					</thead>
					<tbody id="tableBody">
						<!-- Table rows will be added here -->
					</tbody>
				</table>
				<div id="trancheKeysLegend" class="mt-1 ml-1${stepwiseAnalysis?.checked ? '' : ' hidden'}">${keysLegendHtml}</div>
			</div>
		`;

		let table, sort;
		userGiftTransferTable.innerHTML = userTransactionsMarkup;
		table = document.getElementById('historicalsSortTable');
		if (table) sort = new Tablesort(table, { ascending: true });

		if (isAdmin && !VOA && !isExec && !isSupv) renderHistoricalBasisListeners();

		// Historical Tax Basis column (#5621): every value is editable, whether an
		// already-reported basis or a derived fallback being promoted to a real one.
		// Writes go through TransactionService.js, same as Ledgers.
		function renderHistoricalBasisListeners() {
			document.querySelectorAll('.editable-tax-basis').forEach((cell) => {
				let originalText;

				// Click in: clear to just "$", cursor after it (same collapse() idiom
				// as .gift-qty's listeners above).
				cell.addEventListener('focus', function () {
					originalText = this.textContent.trim();
					this.textContent = '$';
					document.getSelection().collapse(this, this.childNodes.length);
				});

				cell.addEventListener('keydown', function (e) {
					if (e.key === 'Enter') {
						e.preventDefault();
						e.stopPropagation();
						this.blur();
					} else if (e.key === 'Escape') {
						// stopPropagation is essential here -- Materialize's modal
						// listens for Escape at the document level to close itself,
						// and would otherwise close the whole Gift Transfer modal out
						// from under the user instead of just reverting this cell.
						e.preventDefault();
						e.stopPropagation();
						this.textContent = originalText;
						this.blur();
					}
				});

				// Live-sanitize like .gift-qty above, keeping one leading "$". Skipped once
				// a letter's been typed, since "n/a" is a valid value here.
				cell.addEventListener('input', function () {
					const current = this.textContent;
					if (/[a-zA-Z]/.test(current)) return;

					const digits = current.replace(/[^-.\d]/g, '');
					const cleaned = '$' + digits;
					if (cleaned === current) return;

					const sel = document.getSelection();
					const caretInDigits = sel && sel.rangeCount ? Math.max(0, sel.getRangeAt(0).startOffset - 1) : digits.length;
					this.textContent = cleaned;
					document.getSelection().collapse(this.firstChild || this, Math.min(caretInDigits + 1, cleaned.length));
				});

				cell.addEventListener('blur', function () {
					const raw = this.textContent.trim();
					if (raw === originalText) return;

					// Nothing typed after focus cleared it to "$" -- not a real edit.
					const withoutDollar = raw.replace(/^\$/, '').trim();
					if (withoutDollar === '') {
						this.textContent = originalText;
						return;
					}

					const nrid = this.getAttribute('data-nrid');
					const shareClass = this.getAttribute('data-share-class');
					if (!nrid || !shareClass) {
						this.textContent = originalText;
						return;
					}

					// "#" is never relevant here -- unlike cert numbers, Tax Basis is a
					// plain number or an explicit "n/a" (meaning: don't derive one).
					const isExplicitNA = isNA(withoutDollar);
					const cleaned = withoutDollar.replace(/[^-.\d]/g, '');
					const isValidNumber = cleaned !== '' && isKnownNumber(cleaned);

					if (!isExplicitNA && !isValidNumber) {
						// Not a recognizable value -- revert rather than save garbage.
						this.textContent = originalText;
						return;
					}

					const newTaxBasis = isExplicitNA ? 'n/a' : cleaned;
					const cellElem = this;

					const t = giftTransactions.find((g) => g.nrid === nrid);
					if (!t) {
						cellElem.textContent = originalText;
						return;
					}

					// Full-record replace (see TransactionService.js docstring) --
					// every field must be resent, not just taxBasis, or the backend
					// blanks whatever's omitted.
					const transaction = {
						date: t.date,
						expDate: t.expDate,
						notes: t.notes,
						securityType: t.securityType,
						sharePrice: t.sharePrice,
						quantity: t.quantity,
						sender: t.sender,
						receiver: t.receiver,
						senderName: t.senderName,
						receiverName: t.receiverName,
						strikePrice: t.strikePrice,
						nrid: t.nrid,
						taxBasis: newTaxBasis,
					};

					// Snapshot the pre-edit state so a failed save can be rolled back --
					// applyLocalUpdate() below is optimistic (runs immediately, before
					// the network call resolves), so undoing it means restoring both
					// the transaction object and the cell's rendered state.
					const priorTaxBasis = t.taxBasis;
					const priorEffective = t._effectiveTaxBasis;
					const priorSource = t._basisSource;
					const priorClassName = cellElem.className;
					const priorTitle = cellElem.title;
					const priorOriginalText = originalText;

					function applyLocalUpdate() {
						t.taxBasis = newTaxBasis;
						const basis = resolveEffectiveTaxBasis(t, clientData);
						t._effectiveTaxBasis = basis.value;
						t._basisSource = basis.source;

						// Redraw with the same formatting the initial render uses (row
						// template above) -- without this the cell was just left showing
						// whatever raw text was typed (e.g. "42.5" instead of "42.50").
						const displayText = isNA(String(t._effectiveTaxBasis))
							? t._effectiveTaxBasis
							: Number.isFinite(t._effectiveTaxBasis)
								? t._effectiveTaxBasis.toLocaleString('en-US', usd)
								: '';
						cellElem.textContent = displayText;
						cellElem.className = `text-xs center-align p-0 editable-tax-basis ${basisColorClass(t._basisSource)}`;
						cellElem.title = 'Click to edit -- entering a value here sets the actual, reported Tax Basis on this transaction.';
						originalText = displayText;

						// The footer's average-cost-basis figure is derived from every
						// row's _effectiveTaxBasis and doesn't update itself -- recompute
						// it now that this row's value changed.
						showTotals();
					}

					function revertLocalUpdate() {
						t.taxBasis = priorTaxBasis;
						t._effectiveTaxBasis = priorEffective;
						t._basisSource = priorSource;
						cellElem.textContent = priorOriginalText;
						cellElem.className = priorClassName;
						cellElem.title = priorTitle;
						originalText = priorOriginalText;
						showTotals();
					}

					// Format immediately on Enter/blur rather than waiting on the
					// network round-trip -- the save happens in the background and
					// rolls the cell back only if it actually fails.
					applyLocalUpdate();

					// Both branches fire 'RefreshSessionStorage' on success, same as GT's other
					// write flows, so Events & Transactions (which snapshots its own
					// transactions array) picks up the new Tax Basis without a manual refresh.
					if (EXTERNAL_DATA) {
						queueExternalWrite({ record: transaction, action: 'update' });
						flushExternalQueue({
							userid: authedUser,
							cookie,
							client,
							clientData,
							isACR: typeof isACR !== 'undefined' && isACR,
						}).then((ok) => {
							if (!ok) revertLocalUpdate();
							else document.dispatchEvent(new Event('RefreshSessionStorage'));
						});
					} else {
						patchTransactionRemote({ userid: authedUser, cookie, client, clientData, shareClass, transaction })
							.then(() => document.dispatchEvent(new Event('RefreshSessionStorage')))
							.catch((err) => {
								console.error('Failed to save Tax Basis edit:', err);
								revertLocalUpdate();
							});
					}
				});
			});
		}

		trancheErrorText = '';

		let tranches = new Set([...priorTransfers, ...historicHoldings].map((item) => item.tranche));
		let sortedTranches = Array.from(tranches).sort((a, b) => lettersToNumbers(a) - lettersToNumbers(b));

		// Flag only when the transaction's own notes have a #-tagged, cert-shaped reference
		// but no certNumber -- not gated on CERT_COLUMN, which false-positived for Edmund Optics.
		{
			const seenMissingCerts = new Set();
			const certRefPattern = new RegExp('#\\s*' + CERT_TOKEN, 'i');

			giftTransactions.forEach((transaction) => {
				const certNumber = String(transaction.certNumber ?? '').trim();
				if (certNumber) return;
				if (!certRefPattern.test(transaction.notes || '')) return;

				const key =
					transaction.nrid ||
					[
						transaction.date || '',
						transaction.shareClass || '',
						transaction.sender || '',
						transaction.receiver || '',
						transaction.quantity || '',
						transaction.notes || '',
					].join('|');

				if (seenMissingCerts.has(key)) return;
				seenMissingCerts.add(key);

				const missingCertErrorText = `Gift transaction missing certNumber${
					transaction.date ? ` on ${moment.unix(Number(transaction.date)).utc().format('M/D/YYYY')}` : ''
				}${transaction.notes ? `: ${transaction.notes}` : ''}`;

				trancheErrorText += (trancheErrorText ? ' | ' : '') + missingCertErrorText;

				// Suppressed in Stepwise Analysis -- these dev-only diagnostics compare against
				// full transaction history, so a partial (stepped-to) view spuriously trips them.
				if (!getCookie('userIndex') && authedUser == 'cmcconnell@nthround.com' && !stepwiseAnalysis?.checked) {
					showToast('warning-toast', missingCertErrorText, 10000);
				}
			});
		}

		// Flag each unique historic holding note/certificate containing a # character
		const seenHashNotes = new Set();
		// Suppressed in Stepwise Analysis -- see comment on the missing-certNumber check above.
		let showErrorToast = !getCookie('userIndex') && authedUser == 'cmcconnell@nthround.com' && !stepwiseAnalysis?.checked;

		historicHoldings.forEach((holding) => {
			const notes = String(holding.notes || '');
			if (!notes.includes('#')) return;
			const key = [holding.tranche || '', holding.certNumber || '', notes].join('|');
			if (seenHashNotes.has(key)) return;
			seenHashNotes.add(key);
			const notesErrorText =
				'Historic holding notes contain #' +
				(holding.tranche ? ' in tranche ' + holding.tranche : '') +
				(holding.certNumber ? ', cert ' + holding.certNumber : '') +
				': ' +
				notes;
			trancheErrorText += (trancheErrorText ? ' | ' : '') + notesErrorText;
			if (showErrorToast) showToast('warning-toast', notesErrorText, 10000);
		});

		let reportSourceHoldings = [];
		sortedTranches.forEach((tranche) => {
			let transfers = priorTransfers.filter((item) => item.tranche == tranche);
			let holdings = collapseHistoricHoldingsForDisplay(historicHoldings.filter((item) => item.tranche == tranche));
			if (transfers.length) {
				let sumTransfers = transfers.reduce((a, b) => a + Number(b.quantity), 0);
				let sumHoldings = holdings.reduce((a, b) => a + Number(b.quantity), 0);
				// Epsilon compare (fractional quantities can differ by <1 share now).
				if (!quantitiesMatch(sumTransfers, sumHoldings)) {
					const mismatchErrorText = 'Tranche ' + tranche + ' sum mismatch (' + sumHoldings + ' != ' + sumTransfers + ').';
					// Scanner-only marker (does not affect the toast/UI): lets the report
					// distinguish a real mismatch from a tranche with a Buy-Back/Redemption,
					// where a quantity decline can be legitimate.
					const hasBuyBack = transfers.some((t) => /buy.?back|redemption/i.test(t.notes || ''));
					trancheErrorText += (trancheErrorText ? ' | ' : '') + mismatchErrorText + (hasBuyBack ? ' [Buy-Back/Redemption present]' : '');
					if (showErrorToast) showToast('warning-toast', mismatchErrorText, 10000);
				}
				holdings.sort((a, b) => Number(a.quantity) - Number(b.quantity));
				transfers.sort((a, b) => Number(a.quantity) - Number(b.quantity));
				addTableRow(tranche, holdings, transfers, sumTransfers);
				reportSourceHoldings.push(...holdings);
			}
		});

		// Scanner-only rollup: how many current sources (tranches) back this holder's
		// balance, and "tranche:Key" for each, oldest source first (a repeated tranche
		// letter here means that tranche was drawn on more than once).
		let sourceCount = reportSourceHoldings.length;
		let keysList = reportSourceHoldings
			.slice()
			.sort((a, b) => Number(a.date) - Number(b.date))
			.map((h) => (MATCH_KEY_LABELS[h._matchKey] ? h.tranche + ':' + MATCH_KEY_LABELS[h._matchKey] : ''))
			.filter(Boolean)
			.join('|');

		ttlBefore = 0;
		ttlAfter = 0;

		giftTransactions.forEach((row) => {
			const beforeQty = Number(row.before);
			const afterQty = row.after != null ? Number(row.after) : Number(row.quantity);
			ttlBefore += Number.isFinite(beforeQty) ? beforeQty : 0;
			ttlAfter += Number.isFinite(afterQty) ? afterQty : 0;
		});

		document.dispatchEvent(
			new CustomEvent('giftTransferTrancheAnalysisComplete', {
				detail: {
					userid: giftUserId,
					sharesOwned: Number(balanceBefore) || 0,
					sourceCount,
					keysList,
					ttlBefore,
					ttlAfter,
					trancheErrorText,
				},
			})
		);

		function collapseHistoricHoldingsForDisplay(holdings) {
			const merged = [];
			holdings.forEach((h) => {
				const qty = Number(h.quantity) || 0;

				// Only merge rows that are really the same source lot for display purposes
				let existing = merged.find(
					(m) =>
						(m.notes || '') == (h.notes || '') &&
						(m.shareClass || '') == (h.shareClass || '') &&
						(m.tranche || '') == (h.tranche || '') &&
						String(m.sender || '') == String(h.sender || '') &&
						String(m.receiver || '') == String(h.receiver || '') &&
						String(m.sharePrice || '') == String(h.sharePrice || '') &&
						String(m.taxBasis || '') == String(h.taxBasis || '')
				);

				if (existing) existing.quantity = (Number(existing.quantity) + qty).toString();
				else merged.push({ ...h });
			});
			return merged;
		}

		function trancheValueLabel(row) {
			const hasPrice = isKnownNumber(row?.sharePrice);
			const hasBasis = isKnownNumber(row?.taxBasis);
			const priceNum = hasPrice ? Number(String(row.sharePrice).replace(/[^.\d]/g, '')) : null;
			const basisNum = hasBasis ? Number(String(row.taxBasis).replace(/[^-.\d]/g, '')) : null;
			const priceText = priceNum == null || isNaN(priceNum) ? '' : priceNum.toLocaleString('en-US', usp);
			const basisText = basisNum == null || isNaN(basisNum) ? '' : basisNum.toLocaleString('en-US', usp);
			if (basisNum != null && priceNum != null) {
				if (Math.abs(basisNum - priceNum) < 0.005) return basisText;
				return priceText + ' | TB ' + basisText;
			}
			if (basisNum != null) return 'TB ' + basisText;
			if (priceNum != null) return priceText;
			return '';
		}

		function addTableRow(tranche, holdings, transfers, sumTransfers) {
			let tableBody = document.getElementById('tableBody');
			let row = document.createElement('tr');

			let trancheCell = document.createElement('td');
			let trancheDate = transfers[0] ? moment.unix(Number(transfers[0].date)).utc() : moment.utc();
			trancheCell.innerHTML =
				tranche + '<p class="text-xxs font-md mt-1 mb-0">' + trancheDate.format('MMM D') + '<br>' + trancheDate.format('YYYY') + '</p>';
			trancheCell.classList.add('text-xs', 'font-bold', 'center-align', 'p-0');
			trancheCell.setAttribute('data-sort', lettersToNumbers(tranche));
			row.appendChild(trancheCell);

			let classCell = document.createElement('td');
			let trancheClass = transfers[0] ? transfers[0].shareClass : '?';
			classCell.innerHTML = trancheClass + '<p class="text-xxs mt-1 mb-0">' + sumTransfers.toLocaleString() + '<br>shares</p>';
			classCell.classList.add('text-xs', 'center-align', 'p-0');
			row.appendChild(classCell);

			let holdingsCell = document.createElement('td');
			holdingsCell.innerHTML = holdings
				.map((h) => {
					let valueLabel = trancheValueLabel(h);
					return (
						h.notes +
						' - ' +
						Number(h.quantity).toLocaleString() +
						' shares from ' +
						h.senderName.replace('Issuer', capitalize(client)) +
						matchKeyBadge(h._matchKey) +
						(valueLabel ? '<span class="text-secondary">&ensp;' + valueLabel + '</span>' : '')
					);
				})
				.join('<br>');
			if (isVERM) holdingsCell.innerHTML = holdingsCell.innerHTML.replace(/from Vermeer/g, 'reissued');
			holdingsCell.classList.add('text-xs');
			row.appendChild(holdingsCell);

			let transfersCell = document.createElement('td');
			transfersCell.innerHTML = transfers
				.map((t) => {
					let valueLabel = trancheValueLabel(t);
					return (
						t.notes +
						' - ' +
						Number(t.quantity).toLocaleString() +
						' shares to ' +
						t.receiverName.replace('Issuer', capitalize(client)) +
						matchKeyBadge(t._matchKey || (t._basisCarried ? 'F' : '')) +
						(valueLabel ? '<span class="text-secondary">&ensp;' + valueLabel + '</span>' : '')
					);
				})
				.join('<br>');
			if (isVERM) transfersCell.innerHTML = transfersCell.innerHTML.replace(/to Vermeer/g, 'cancelled');
			transfersCell.classList.add('text-xs');
			row.appendChild(transfersCell);

			tableBody.appendChild(row);
		}

		let tableP, sortP;
		tableP = document.getElementById('trancheTable');
		if (tableP) sortP = new Tablesort(tableP, { ascending: true });

		if (addedGiftTransferListenersAlready) showTotals();
	}

	const debtEquityTabs = document.getElementById('debtEquityTabs');
	const trancheTable = document.getElementById('trancheTable');
	const trancheKeysLegend = document.getElementById('trancheKeysLegend');
	const showHidePriors = document.getElementById('showHidePriors');
	const selectRecipient = document.getElementById('selectRecipient');
	const addRecipientBtn = document.getElementById('addRecipientBtn');
	const newHoldingButton = document.getElementById('newHoldingButton');
	const showHidePriorsDiv = document.getElementById('showHidePriorsDiv');
	const selectRecipientDiv = document.getElementById('selectRecipientDiv');
	const previewGiftTransfer = document.getElementById('previewGiftTransfer');
	const statementWaitHeader = document.getElementById('statementWaitHeader');
	const totalCurrentValueDiv = document.getElementById('totalCurrentValueDiv');
	const historicalsSortTable = document.getElementById('historicalsSortTable');
	const consolidateTransfers = document.getElementById('consolidateTransfers');

	if (showHidePriors)
		showHidePriors.addEventListener('click', function (e) {
			// Prior Tranches always stays shown in Stepwise Analysis.
			if (stepwiseAnalysis?.checked) return;
			document.getElementById('viewGiftTransferModal').style.overflowY = 'auto';
			if (trancheTable.classList.contains('hidden')) {
				trancheTable.classList.remove('hidden');
				if (trancheKeysLegend) trancheKeysLegend.classList.remove('hidden');
				if (showHidePriorsDiv) {
					showHidePriorsDiv.classList.remove('mb-0');
					showHidePriorsDiv.classList.add('mb-2');
				}
				this.innerText = 'Hide Prior Tranches';
			} else {
				if (showHidePriorsDiv) {
					showHidePriorsDiv.classList.remove('mb-2');
					showHidePriorsDiv.classList.add('mb-0');
				}
				trancheTable.classList.add('hidden');
				if (trancheKeysLegend) trancheKeysLegend.classList.add('hidden');
				this.innerText = 'Show Prior Tranches';
			}
		});

	const showHideGains = document.getElementById('showHideGains');
	if (showHideGains)
		showHideGains.addEventListener('click', function () {
			showGainsColumns = !showGainsColumns;
			document.querySelectorAll('#historicalsSortTable .gains-column').forEach((el) => el.classList.toggle('hidden', !showGainsColumns));
			this.innerHTML = showGainsColumns ? 'Hide Gain/Loss' : 'Show Gain/Loss';
		});

	if (!addedGiftTransferListenersAlready) {
		addedGiftTransferListenersAlready = true;
		let showHideZeros = document.getElementById('showHideZeros');
		if (showHideZeros) {
			showHideZeros.addEventListener('click', function (e) {
				historicalsSortTable?.classList.toggle('hidden', toggle === 'Hide All');
				if (toggle !== 'Hide All') {
					let len = giftTransactions.length; //historicHoldings.length;
					for (let i = 0; i < len; i++) {
						let thisRow = document.getElementById('historicsTable' + i);
						let isZero = thisRow.cells[4].innerText.trim() === '0';
						if (toggle === 'Show All' || (toggle === 'Show Positves' && !isZero) || (toggle === 'Show Zeros' && isZero))
							thisRow.classList.remove('hidden');
						else thisRow.classList.add('hidden');
					}
				}
				if (toggle === 'Show All' || toggle === 'Show Positves') showTotals();
				switch (toggle) {
					// case "Open Modal": toggle = "Hide All"; break;
					case 'Show Positves':
						toggle = 'Hide All';
						break;
					case 'Show Zeros':
						toggle = 'Show Positves';
						break;
					case 'Show All':
						toggle = 'Show Zeros';
						break;
					case 'Hide All':
						toggle = 'Show All';
						break;
				}
				this.innerText = toggle;
			});
		}
		addRecipientBtn.addEventListener('click', async function (e) {
			e.preventDefault();

			if (selectRecipientDiv.classList.contains('hidden')) {
				selectRecipient.selectedIndex = 0;
				selectRecipient.dispatchEvent(new Event('change'));
				selectRecipientDiv.classList.remove('hidden');

				const recipientPicker = document.getElementById('recipientPicker');
				if (recipientPicker) recipientPicker.classList.remove('hidden');

				const oldSelectWrapper = selectRecipientDiv.querySelector('.select-wrapper');
				if (oldSelectWrapper) oldSelectWrapper.classList.add('hidden');

				totalCurrentValueDiv.classList.add('hidden');
			} else {
				selectRecipientDiv.classList.add('hidden');
				totalCurrentValueDiv.classList.remove('hidden');
			}

			await getNextCertNumber();
		});
		selectRecipient.addEventListener('change', function (e) {
			if (!this.value) return;
			// document.getElementById('viewGiftTransferModal').style.overflowY = "auto";
			let id, recip;
			let name = DOMPurify.sanitize(this.value.trim());
			// let name = this.value.includes('|') ? this.value.split('|')[1].trim() : this.value.trim();
			if (name == 'Issuer') {
				id = 'Issuer';
				recip = { userid: 'issuer', user_name: `${clientData.company_name} (Issuer)`, clients: { [client]: { shares: {} } } };
			} else {
				// see if the name has the email already, or if it's BB-style name
				let id = name.split('|')[1].trim();
				if (!validator.isEmail(id)) id = capTableData.find((u) => u.user_name == name).userid;
				if (!id || id == giftUserId) return;
				recip = members ? members.find((u) => u.userid == id) : capTableData ? capTableData.find((u) => u.userid == id) : null;
			}
			if (recip && !giftRecipients.find((r) => r.userid == id)) giftRecipients.push(recip);
			selectRecipientDiv.classList.add('hidden');
			totalCurrentValueDiv.classList.remove('hidden');
			renderGiftRecipients();
			// viewGiftTransferModal.scrollTo({ top: 0, behavior: 'smooth' });
			renderNewGiftListeners();
			buildSelectRecipientOptions();
			document.getElementById(recip.userid + 'AddGift').dispatchEvent(new Event('click'));
		});
		document.getElementById('removeRecipientBtn').addEventListener('click', function (e) {
			let giftsToRemove = allGifts.filter((g) => g.receiver == removeGiftId);
			giftsToRemove.forEach((g) => {
				adjustBalances(-Number(g.quantity.replace(/[^-.\d]/g, '')), g.receiver, g.tranche);
				if (g.notes.includes(lastCertNum[g.shareClass])) nextCertNum[g.shareClass] = lastCertNum[g.shareClass];
			});
			giftRecipients = giftRecipients.filter((r) => r.userid != removeGiftId);
			allGifts = allGifts.filter((g) => g.receiver != removeGiftId);
			delete cashSaleRecipients[removeGiftId];
			M.Modal.getInstance(document.getElementById('confirmRecipientRemove')).close();
			let recipientDiv = document.getElementById(removeGiftId + '|Div');
			recipientDiv.outerHTML = '';
			if (giftRecipients.length < 1) {
				document.getElementById('viewRecipients').innerHTML = `
					<div class="bg-grey-lightest rounded border-grey grey-text center-align p-1 mt-4">
						Recipients will appear here.
						<!-- <p class="mt-0"><a href="#addRecipient" class="modal-trigger">Add Recipient</a></p> -->
					</div>
				`;
				viewRecipients.addEventListener('click', (e) => addRecipientBtn.dispatchEvent(new Event('click')));
			}
			if (!allGifts.length) previewGiftTransfer.classList.add('disabled');
			renderNewGiftListeners();
			buildSelectRecipientOptions();
		});
		filterViewGT.addEventListener('change', function (e) {
			selectedClass = filterViewGT.value;
			showFilteredGiftTrans();
			if (selectedClass == 'ALL') initializeDebtEquityTabs(); //viewEventsIconLarge.dispatchEvent(new Event('click'));
		});
		document.getElementById('showDecimals').addEventListener('change', function (e) {
			e.preventDefault();
			if (includedClasses.length > 1) {
				if (!selectedClass) selectedClass = 'ALL';
				showFilteredGiftTrans();
			} else {
				showFilteredGiftTrans();
			}
		});
		if (newHoldingButton) {
			newHoldingButton.addEventListener('click', function (e) {
				window.location.replace('/admin/ledgers/?action=newtransaction&recipient=' + encodeURIComponent(shareholder));
			});
		}
		document.getElementById('exportGiftTransfer').addEventListener('click', async function (e) {
			e.preventDefault();

			let origHistoricalsHtml = historicalsSortTable.innerHTML;
			let origTrancheHtml = trancheTable.innerHTML;

			// export-only cleanup
			let footer = historicalsSortTable.querySelector('tfoot');
			if (footer) footer.remove();

			trancheTable.querySelectorAll('tbody tr').forEach((row) => {
				let cells = row.querySelectorAll('td');
				if (!cells.length) return;

				// Column A: AH | Dec 11 2025
				if (cells[0]) {
					let main = cells[0].innerHTML.split('<p')[0].trim();
					let sub = cells[0].querySelector('p')?.innerHTML.replace(/<br>/g, ', ').trim() || '';
					cells[0].innerHTML = sub ? main + ' | ' + sub : main;
				}

				// Column B: C | 680 shares
				if (cells[1]) {
					let main = cells[1].innerHTML.split('<p')[0].trim();
					let sub = cells[1].querySelector('p')?.innerHTML.replace(/<br>/g, ' ').trim() || '';
					cells[1].innerHTML = sub ? main + ' | ' + sub : main;
				}
			});

			try {
				await export_table_to_excel(
					'historicalsSortTable|trancheTable',
					giftUserData.user_name + ' Gift / Transfer Report (' + new Date().toLocaleString() + ').csv'
				);
			} finally {
				historicalsSortTable.innerHTML = origHistoricalsHtml;
				trancheTable.innerHTML = origTrancheHtml;
			}
		});
		previewGiftTransfer.addEventListener('click', async function (e) {
			if (fromUser) sendThisRequest();
			else {
				const giftsForPreview = consolidateTransfers?.checked ? await getConsolidatedTransferGifts() : allGifts;
				renderTransactionMapTable(giftsForPreview);
			}
		});
		if (historicalsSortTable)
			historicalsSortTable.addEventListener('click', async function (e) {
				// 🔹 Add `async`
				if (e.target.classList.contains('selectTrancheInput')) {
					let selected = e.target.getAttribute('data-tranche');
					let consolidate = document.getElementById('consolidate');
					let cbElem = document.getElementById('cbElem-' + selected);
					let imgElem = document.getElementById('imgElem-' + selected);
					let cbElems = document.querySelectorAll('.selectTrancheInput');

					if (selected == 'all') {
						selectedTranches = [];
						if (e.target.checked) {
							currentData.forEach((u) => selectedTranches.push(u.userid));
							document.querySelectorAll('input[type=checkbox]').forEach((el) => {
								if (el.classList.contains('selectTrancheInput')) el.checked = true;
							});
						} else {
							document.querySelectorAll('input[type=checkbox]').forEach((el) => {
								if (el.classList.contains('selectTrancheInput')) el.checked = false;
							});
						}
					} else {
						if (e.target.checked) {
							let thisClass = e.target.dataset.class;
							cbElem.style.display = 'block';
							imgElem.style.display = 'none';
							selectedTranches.push(selected);
							for (let i = 0; i < cbElems.length; i++) {
								if (cbElems[i].dataset.class != thisClass) cbElems[i].disabled = true;
								else cbElems[i].disabled = false;
							}
							addRecipientBtn.classList.add('text-secondary');
							addRecipientBtn.style.pointerEvents = 'none';
						} else {
							cbElem.style.display = 'none';
							imgElem.style.display = 'block';
							selectedTranches = selectedTranches.filter(function (tr) {
								return tr !== selected;
							});
						}
					}

					let trLen = selectedTranches.length;
					if (trLen == 0) {
						for (let i = 0; i < cbElems.length; i++) cbElems[i].disabled = false;
						addRecipientBtn.classList.remove('text-secondary');
						addRecipientBtn.style.pointerEvents = null;
					} else if (trLen == 1) {
						await getNextCertNumber();
						consolidate.innerHTML = 'Reissue Tranche: ' + (selectedTranches[0] || '');
						previewGiftTransfer.classList.remove('disabled');
						previewGiftTransfer.innerHTML = 'Preview This Reissuance';
					} else if (trLen > 1) {
						await getNextCertNumber();
						selectedTranches.sort();
						consolidate.innerHTML =
							'Consolidate Tranches: ' +
							(trLen > 1 ? `${selectedTranches.slice(0, -1).join(', ')} and ${selectedTranches[trLen - 1]}` : selectedTranches[0] || '');
						previewGiftTransfer.classList.remove('disabled');
						previewGiftTransfer.innerHTML = 'Preview This Consolidation';
					} else {
						consolidate.innerHTML = '';
						previewGiftTransfer.classList.add('disabled');
						previewGiftTransfer.innerHTML = 'Preview These Transfers';
					}

					console.log(selectedTranches);
				}
			});

		if (isAdmin) {
			const stepwiseControls = document.getElementById('stepwiseControls');
			const stepwiseCheckBox = document.getElementById('stepwiseCheckBox');
			stepwiseCheckBox?.classList.remove('hidden');
			// Hoisted out of the 'change' handler so this only ever binds once (was resetting
			// on every toggle, stacking duplicate button/keydown listeners).
			let _stepwiseBound = false;

			function initStepwiseListenersOnce() {
				if (_stepwiseBound) return;
				const firstBtn = document.getElementById('stepFirst');
				const prevBtn = document.getElementById('stepPrev');
				const nextBtn = document.getElementById('stepNext');
				const lastBtn = document.getElementById('stepLast');
				if (!firstBtn || !prevBtn || !nextBtn || !lastBtn) return;

				const onClick = (name) => (e) => {
					e.preventDefault();
					if (name === 'First') window._stepwiseTargetLen = 1;
					else if (name === 'Prev') window._stepwiseTargetLen = Math.max(1, (window._stepwiseTargetLen || 1) - 1);
					else if (name === 'Next') window._stepwiseTargetLen = Math.min(window._stepwiseMasterLen || 1, (window._stepwiseTargetLen || 1) + 1);
					else if (name === 'Last') window._stepwiseTargetLen = window._stepwiseMasterLen || 1;
					giftTransfer(giftUserId, false);
					showTrancheTable();
				};

				firstBtn.addEventListener('click', onClick('First'));
				prevBtn.addEventListener('click', onClick('Prev'));
				nextBtn.addEventListener('click', onClick('Next'));
				lastBtn.addEventListener('click', onClick('Last'));

				document.addEventListener(
					'keydown',
					(e) => {
						if (e.key === 'Home') return onClick('First')(e);
						if (e.key === 'ArrowLeft') return onClick('Prev')(e);
						if (e.key === 'ArrowRight') return onClick('Next')(e);
						if (e.key === 'Last') return onClick('Last')(e);
					},
					{ passive: false }
				);

				_stepwiseBound = true;
			}

			if (stepwiseAnalysis)
				stepwiseAnalysis.addEventListener('change', function (e) {
					if (stepwiseAnalysis.checked) {
						balanceHeadline.classList.add('hidden');
						stepwiseControls.classList.remove('hidden');
						giftTransferControls.classList.add('hidden');
						showTrancheTable();
						initStepwiseListenersOnce();
					} else {
						stepwiseControls.classList.add('hidden');
						balanceHeadline.classList.remove('hidden');
						giftTransferControls.classList.remove('hidden');
						window._stepwiseTargetLen = null; // set clamp set to end
						giftTransfer(giftUserId, false); // repaint with full range
					}
				});

			function showTrancheTable() {
				if (showHidePriorsDiv) {
					showHidePriorsDiv.classList.add('mb-2');
					showHidePriorsDiv.classList.remove('mb-0');
				}
				trancheTable.classList.remove('hidden');
				if (trancheKeysLegend) trancheKeysLegend.classList.remove('hidden');
				const showHide = document.getElementById('showHidePriors');
				if (showHide) showHide.innerText = 'Hide Prior Tranches';
			}

			const proTips = document.getElementById('proTips');
			if (proTips)
				proTips.addEventListener('click', function (e) {
					e.preventDefault();
					const helpModalEl = document.getElementById('trancheAnalysisHelpModal');
					const giftModal = M.Modal.getInstance(viewGiftTransferModal);
					const helpModal = M.Modal.getInstance(helpModalEl);
					if (giftModal && giftModal.isOpen) {
						giftModal.options.onCloseEnd = () => helpModal.open();
						giftModal.close();
					} else helpModal.open();
				});
		}

		function initializeDebtEquityTabs() {
			let instance = M.Tabs.init(debtEquityTabs, {});
			debtEquityTabs.classList.add('hidden');
			setTimeout(() => {
				if (showTabs) {
					debtEquityTabs.classList.remove('hidden');
					instance.updateTabIndicator();
					console.log('Intialized Debt-Equity tabs');
				}
			}, 100);
		}

		showHideZeros.dispatchEvent(new Event('click'));
		renderGiftRecipients();

		const removeThisRequest = document.getElementById('removeThisRequest');
		confirmRequestRemove = document.getElementById('confirmRequestRemove');
		const shareholderTransferRequests = shareholderData.clients[client].transfer_requests || {};

		// Extract all gifts that are "Open"
		const openGifts = Object.values(shareholderTransferRequests)
			.flat() // Flatten nested arrays
			.filter((gift) => gift.status === 'Open');

		if (openGifts.length > 0) {
			removeThisRequest.classList.remove('hidden');

			allGifts = openGifts;
			renderNewGiftListeners();
			getNextCertNumber();

			// Extract unique recipients
			const uniqueRecipients = new Set(allGifts.map((gift) => gift.receiver));

			// Fire events for each unique recipient
			uniqueRecipients.forEach((recipientId) => {
				let recip = capTableData.find((u) => u.userid == recipientId);
				if (!recip) {
					if (itsTreasury(recipientId, '')) {
						recip = {};
						recip.userid = recipientId;
						recip.user_name = clientData.treasury_account?.split('|')[0]?.trim() || 'Treasury';
					} else {
						alert('⛔ Missing recipient account: ' + recipientId + '.\n\n⚠️ Please add this account to the cap table before continuing.');
						return;
					}
				}
				if (!giftRecipients.find((r) => r.userid == recipientId)) giftRecipients.push(recip);

				renderGiftRecipients();
				renderNewGiftListeners();
				buildSelectRecipientOptions();
				document.getElementById(recip.userid + 'AddGift').dispatchEvent(new Event('click'));
			});

			// Adjust balances based on gifts
			allGifts.forEach((g) => adjustBalances(Number(g.quantity), g.receiver, g.tranche));

			// Add event listeners for request removal
			document.getElementById('removeThisRequest').addEventListener('click', function (e) {
				M.Modal.getInstance(confirmRequestRemove).open();
			});
			document.getElementById('removeRequestBtn').addEventListener('click', function (e) {
				M.Modal.getInstance(viewGiftTransferModal).close();
				M.Modal.getInstance(confirmRequestRemove).close();
				M.Modal.getInstance(statementWaitState).open();
				clearPendingRequests('Removed');
			});
		} else {
			removeThisRequest?.classList.add('hidden');
		}
	}

	let labelBB = clientData.language?.label_buy_back?.includes('Redemption') ? 'Redemption' : 'Buy-Back';

	if (BUY_BACK) {
		let treasuryAccount = clientData.treasury_account;
		if (!treasuryAccount) treasuryAccount = 'Treasury | temp_treasury@' + client + '.com';
		holders.push(treasuryAccount);
		treasuryId = treasuryAccount.split('|')[1].trim();
		let treasuryName = treasuryAccount.split('|')[0].trim();
		members = [{ userid: treasuryId, user_name: treasuryName, clients: { [client]: { shares: {} } } }];
		buildSelectRecipientOptions();
		selectRecipient.value = treasuryAccount;
		selectRecipient.dispatchEvent(new Event('change'));

		// now do these
		addRecipientBtn.classList.add('hidden');
		totalCurrentValueDiv.classList.remove('hidden');
		previewGiftTransfer.innerHTML = 'Request This ' + labelBB;
		document.getElementById('toTheseRecipientsDiv').classList.add('hidden');
		document.getElementById('requestHeadline').classList.remove('hidden');
		document.getElementById('requestInstructions').classList.remove('hidden');
		document.getElementById('taxLiabilityNotice').classList.remove('hidden');
		totalCurrentValueDiv.innerHTML = `Total Current Value${isEDM ? ` (${currentPrice('C')?.toLocaleString('en-US', usd)} per share)` : ``}:&ensp;<span id="totalCurrentValue" class="text-black"></span>`;
		const firstTranche = document.getElementById(' |' + treasuryId + '|TrancheId');
		if (firstTranche)
			setTimeout(() => {
				firstTranche.focus();
			}, 10);
	} else if (capTableData && !fromUser) {
		// <-- 2/10/2025 added !fromUser to more accurately preview user experience
		capTableData.forEach(function (i) {
			if (i.userid == giftUserId) return;
			if ((isBB && client != 'onwardfe') || i.user_name.includes('|')) holders.push(i.user_name);
			else holders.push(i.user_name + ' | ' + i.userid);
		});
		// capTableData.forEach(function (i) { if (i.userid != giftUserId) holders.push((i.clients[client].notes ? (i.clients[client].notes + "\xa0\xa0|\xa0\xa0") : "") + i.user_name); });
		// capTableData.forEach(function (i) { if (i.userid != giftUserId) holders.push(i.user_name + "\xa0\xa0|\xa0\xa0" + i.userid); });
		// capTableData.forEach(function (i) { holders.push(i.user_name); });
		holders.sort();
		buildSelectRecipientOptions();
	} else {
		// regular user needs members
		addRecipientBtn.classList.add('text-secondary');
		addRecipientBtn.style.pointerEvents = 'none';
		addRecipientBtn.innerHTML = '<i class="material-icons align-middle mb-1 mr-05">person_add</i>One moment...';
		axios
			.post(
				'https://api.nthround.io/portal/user/alias-names',
				JSON.stringify({
					eventName: 'gift-transfer.family_members',
					alias_list: [], // workspaces are trust accounts
					client: client,
					userid: authedUser,
					cookie: cookie,
				})
			)
			.then(function (res) {
				addRecipientBtn.classList.remove('text-secondary');
				addRecipientBtn.style.pointerEvents = null;
				addRecipientBtn.innerHTML = '<i class="material-icons align-middle mb-1 mr-05">person_add</i>Add Recipient';
				members = removeNthAdmins(res.data);

				// Family Filter for USV
				const FF = isUSV || client == 'onwardfe';
				const sliceFF = isUSV ? 1 : 2;
				const familyCode = userData.clients[client]?.tags?.[0]?.slice(0, sliceFF) || '';
				if (FF && fromUser && familyCode) members = members.filter((r) => r.tags?.[0]?.slice(0, sliceFF) === familyCode);

				members.forEach(function (i) {
					if (i.userid == giftUserId) return;
					if ((isBB && client != 'onwardfe') || i.user_name.includes('|')) holders.push(i.user_name);
					else holders.push(i.user_name + ' | ' + i.userid);
				});
				holders.sort();
				buildSelectRecipientOptions();
			})
			.catch(function (error) {
				console.error(error);
			});
	}

	function showTotals() {
		ttlAfter = 0;
		ttlBefore = 0;
		let totalBasisDollars = 0;
		let basisUnknown = false;
		let totalGainLoss = 0;
		let anyGainLossKnown = false;
		let len = giftTransactions.length;
		if (len < 1) return;

		for (let i = 0; i < len; i++) {
			let row = giftTransactions[i];
			let beforeQty = Number(row.before);
			let afterQty = row.after != null ? Number(row.after) : Number(row.quantity);

			ttlBefore += beforeQty;
			ttlAfter += afterQty;
			if (afterQty > 0) {
				let basisPerShare = isKnownNumber(row._effectiveTaxBasis) ? Number(row._effectiveTaxBasis) : null;
				if (basisPerShare === null) basisUnknown = true;
				else totalBasisDollars += afterQty * basisPerShare;
			}
			// #6144: gross Gain/Loss total -- sums the same per-row estimate shown in
			// the (optional) Gain/Loss column for every currently-held (non-zero-qty) row.
			if (Number(row.quantity) !== 0) {
				const gl = computeGainLoss(row, clientData);
				if (gl) {
					totalGainLoss += gl.totalGain;
					anyGainLossKnown = true;
				}
			}
		}

		document.getElementById('totalBefore').innerHTML = ttlBefore.toLocaleString('en-US', stringOptions);
		document.getElementById('totalAfter').innerHTML = ttlAfter.toLocaleString('en-US', stringOptions);
		document.getElementById('avgCostBasis').innerHTML =
			basisUnknown || ttlAfter <= 0 ? 'n.a.' : (totalBasisDollars / ttlAfter).toLocaleString('en-US', usd);
		const totalGainLossEl = document.getElementById('totalGainLoss');
		if (totalGainLossEl) {
			totalGainLossEl.innerHTML = !anyGainLossKnown
				? 'n.a.'
				: `<span class="${totalGainLoss >= 0 ? 'text-primary' : 'text-error'}">${totalGainLoss.toLocaleString('en-US', usd)}</span>`;
		}
	}

	function updateRecipientSearchHighlight() {
		const resultsDiv = document.getElementById('recipientSearchResults');
		if (!resultsDiv) return;

		const rows = resultsDiv.querySelectorAll('.recipient-search-row');
		rows.forEach((row, idx) => {
			row.classList.toggle('active', idx === recipientSearchIndex);
		});

		const activeRow = rows[recipientSearchIndex];
		if (activeRow) {
			activeRow.scrollIntoView({ block: 'nearest' });
		}
	}

	function closeRecipientSearchResults() {
		const resultsDiv = document.getElementById('recipientSearchResults');
		if (!resultsDiv) return;
		resultsDiv.classList.add('hidden');
		recipientSearchIndex = -1;
	}

	function selectRecipientSearchMatch(idx) {
		const match = recipientSearchMatches[idx];
		if (!match) return;

		let value;
		if (match.name === 'Issuer') value = 'Issuer';
		else if (isBB) value = match.name;
		else if (match.userid) value = match.name + ' | ' + match.userid;
		else value = match.name;

		selectRecipient.value = value;
		selectRecipient.dispatchEvent(new Event('change'));

		const input = document.getElementById('recipientSearchInput');
		if (input) input.value = '';

		closeRecipientSearchResults();
	}

	function buildRecipientSearchDataset() {
		recipientSearchData = [];

		const selectedRecipientIds = new Set(giftRecipients.map((r) => r.userid).filter(Boolean));

		let sourceRecipients = Array.isArray(members) && members.length ? members : Array.isArray(capTableData) ? capTableData : [];

		sourceRecipients.forEach((m) => {
			if (!m || m.userid === giftUserId) return;
			if (selectedRecipientIds.has(m.userid)) return;

			const idNumber = m.clients?.[client]?.idNumber || '';
			const name = m.user_name || '';
			const userid = m.userid || '';

			if (!name && !userid) return;

			recipientSearchData.push({
				idNumber,
				name,
				userid,
				searchText: `${idNumber} ${name} ${userid}`.toLowerCase(),
			});
		});

		recipientSearchData.unshift({
			idNumber: '',
			name: 'Issuer',
			userid: 'Cancellation or Redemption',
			searchText: 'issuer cancellation redemption',
		});
	}

	function initializeRecipientPickerShell() {
		const recipientPicker = document.getElementById('recipientPicker');
		const recipientSearchInput = document.getElementById('recipientSearchInput');
		const recipientSearchResults = document.getElementById('recipientSearchResults');

		if (!recipientPicker || !recipientSearchInput || !recipientSearchResults) return;

		recipientPicker.classList.remove('hidden');
		recipientSearchInput.value = '';
		recipientSearchResults.innerHTML = `
			<div class="recipient-search-empty text-xs text-secondary p-1">
				Start typing to search recipients.
			</div>
		`;
		recipientSearchResults.classList.add('hidden');

		if (!recipientSearchInput.dataset.bound) {
			function openRecipientSearchResults() {
				const q = recipientSearchInput.value.trim().toLowerCase();
				if (q) {
					const matches = recipientSearchData.filter((r) => r.searchText.includes(q));
					renderRecipientSearchResults(matches, 25);
				} else {
					recipientSearchMatches = [];
					recipientSearchIndex = -1;
					recipientSearchResults.innerHTML = `
						<div class="recipient-search-empty text-xs text-secondary p-1">
							Start typing (Name, Email,or Id Number) to list matching potential recipients
						</div>
					`;
					recipientSearchResults.classList.remove('hidden');
				}

				recipientSearchIndex = -1;
				if (recipientSearchMatches.length) recipientSearchResults.classList.remove('hidden');
			}

			recipientSearchInput.addEventListener('input', function () {
				const q = this.value.trim().toLowerCase();

				if (!q) {
					recipientSearchMatches = [];
					recipientSearchIndex = -1;
					recipientSearchResults.classList.add('hidden');
					return;
				}

				const matches = recipientSearchData.filter((r) => r.searchText.includes(q));
				renderRecipientSearchResults(matches, 25);
			});

			recipientSearchInput.addEventListener('keydown', function (e) {
				const resultsOpen = !recipientSearchResults.classList.contains('hidden');

				if (e.key === 'ArrowDown') {
					if (!recipientSearchMatches.length) return;
					e.preventDefault();
					if (!resultsOpen) recipientSearchResults.classList.remove('hidden');
					recipientSearchIndex = Math.min(recipientSearchIndex + 1, recipientSearchMatches.length - 1);
					updateRecipientSearchHighlight();
					return;
				}

				if (e.key === 'ArrowUp') {
					if (!recipientSearchMatches.length) return;
					e.preventDefault();
					if (!resultsOpen) recipientSearchResults.classList.remove('hidden');
					recipientSearchIndex = Math.max(recipientSearchIndex - 1, 0);
					updateRecipientSearchHighlight();
					return;
				}

				if (e.key === 'Enter' || e.key === 'Tab') {
					if (resultsOpen && recipientSearchIndex >= 0 && recipientSearchMatches.length) {
						e.preventDefault();
						selectRecipientSearchMatch(recipientSearchIndex);
					}
					return;
				}

				if (e.key === 'Escape') {
					e.preventDefault();
					closeRecipientSearchResults();
					return;
				}
			});

			recipientSearchInput.addEventListener('focus', function () {
				openRecipientSearchResults();
			});

			recipientSearchInput.addEventListener('click', function () {
				openRecipientSearchResults();
			});

			recipientSearchInput.addEventListener('blur', function () {
				setTimeout(() => {
					closeRecipientSearchResults();
				}, 150);
			});

			recipientSearchInput.dataset.bound = 'true';
		}
	}

	function buildSelectRecipientOptions() {
		let recips = [];
		selectRecipient.options.length = 1;
		giftRecipients.forEach((r) => recips.push(r.user_name));
		selectRecipient.options[1] = new Option('Issuer | Cancellation or Redemption', 'Issuer');
		holders.forEach((h) => {
			if (!recips.includes(h)) selectRecipient.options[selectRecipient.options.length] = new Option(h, h);
		});
		let options = {
			dropdownOptions: {
				container: 'authed',
				coverTrigger: false,
			},
		};
		let selects = document.querySelectorAll('select');
		let instances = M.FormSelect.init(selects, options);
		selectRecipient.parentElement.style.display = 'none';
		const oldSelectWrapper = selectRecipientDiv.querySelector('.select-wrapper');
		if (oldSelectWrapper) oldSelectWrapper.classList.add('hidden');

		initializeRecipientPickerShell();
		buildRecipientSearchDataset();
	}

	function renderRecipientSearchResults(results, maxRows = 25) {
		const resultsDiv = document.getElementById('recipientSearchResults');
		if (!resultsDiv) return;
		resultsDiv.innerHTML = '';
		const sortedResults = results.slice().sort((a, b) => {
			if (a.name === 'Issuer') return -1;
			if (b.name === 'Issuer') return 1;
			return String(a.name || '').localeCompare(String(b.name || ''));
		});
		const visibleResults = sortedResults.slice(0, maxRows);
		recipientSearchMatches = visibleResults.slice();
		recipientSearchIndex = recipientSearchMatches.length ? 0 : -1;
		if (!visibleResults.length) {
			resultsDiv.innerHTML = `
				<div class="recipient-search-empty text-xs text-secondary p-1">
					No recipients found
				</div>
			`;
			resultsDiv.classList.remove('hidden');
			return;
		}

		visibleResults.forEach((r, idx) => {
			const row = document.createElement('div');
			row.className = 'recipient-search-row' + (idx === recipientSearchIndex ? ' active' : '');
			row.dataset.index = idx;
			row.innerHTML = `
				<div class="recipient-row-main">
					<span class="recipient-id">${r.idNumber || ''}</span>
					<span class="recipient-name">${r.name}</span>
				</div>
				<div class="recipient-row-sub">${r.userid}</div>
			`;

			row.addEventListener('mousedown', function (e) {
				e.preventDefault();
				selectRecipientSearchMatch(idx);
			});

			resultsDiv.appendChild(row);
		});

		resultsDiv.classList.remove('hidden');
	}

	function renderGiftRecipients() {
		let recipientsMarkup = '';
		let emptyState = `
			<div class="bg-grey-lightest rounded border-grey grey-text center-align p-1 mt-4">
				Recipients will appear here.
				<!-- <p class="mt-0"><a href="#addRecipient" class="modal-trigger">Add Recipient</a></p> -->
			</div>
		`;

		let index = giftRecipients.length - 1;
		if (index < 0) {
			recipientsMarkup = emptyState;
		} else {
			let recipOwned = 0;
			let recip = giftRecipients[index];
			let recipClientData = recip.clients ? recip.clients[client] : recip;
			let recipNotes = recip.clients ? recip.clients[client].notes : recip.notes;
			classes.forEach((c) => (recipOwned += Number(recipClientData.shares?.[c]?.owned || '0')));
			giftRecipients[index].before = recipOwned;
			giftRecipients[index].after = recipOwned;

			recipientsMarkup = `
				<div id="${recip.userid}|Div" class="recip-div card px-4 mt-4 mb-0 rounded">
					${
						BUY_BACK
							? ``
							: `
						<div class="row center-align mt-0 mb-0">
							<!-- <div class="col s12 m6 left-align mt-2" title="${
								recip.friendly_name ? recip.userid + ' [' + recip.friendly_name.replace(/_/g, ' ') + ']' : recip.userid
							}">${(recipNotes ? recipNotes + '\xa0\xa0|\xa0\xa0' : '') + recip.user_name}</div> -->
							<div class="col s12 m6 left-align mt-2" title="${recip.userid}">${recip.user_name}</div>
							<div class="text-sm text-secondary col s12 m5 mt-2" style="display:flex;flex-wrap:nowrap;align-items:center;justify-content:space-between;">
								<label style="display:inline-flex;align-items:center;flex-shrink:0;white-space:nowrap;transform: scale(0.8);" title="Check only if this is an actual cash sale, not a gift/transfer -- Share Price will mirror Tax Basis for every gift/transfer to this recipient while checked.">
									<input type="checkbox" class="cash-sale-checkbox" id="${recip.userid}|CashSale" data-key="${recip.userid}" ${cashSaleRecipients[recip.userid] ? 'checked' : ''} />
									<span class="text-xs">Cash Sale</span>
								</label>
								<span style="white-space:nowrap;">Shares Owned:&emsp;<span class="text-primary">${recip.before.toLocaleString()}</span>&nbsp;Before&emsp;
								<span id="${recip.userid}|After" class="text-primary">${recip.after.toLocaleString()}</span>&nbsp;After</span></div>
							<a href="#confirmRecipientRemove" class="col s12 m1 modal-trigger remove-gift-recipient waves-effect waves-red btn-flat red-text text-xxs right" data-key="${recip.userid}">Remove</a>
						</div>
					`
					}
					<div id="${recip.userid}Gifts"></div>
					<div class="flex center-align mb-0 hidden">
						<p class="text-xs mt-2 mb-0 left"><a id="${recip.userid}AddGift" href="#" class="add-recip text-sm" data-key="${recip.userid}">+ Add Gift/Transfer</a></p>
						<a href="#confirmRecipientRemove" class="col s12 m1 modal-trigger remove-gift-recipientHIDDEN waves-effect waves-red btn-flat p-0 mt-2 mb-0 right" data-key="${recip.userid}">Remove</a>
					</div>
				</div>
			`;
		}

		viewRecipients = document.getElementById('viewRecipients');
		if (index == 0) viewRecipients.innerHTML = recipientsMarkup;
		else viewRecipients.innerHTML += recipientsMarkup;
		// viewRecipients.addEventListener('click', e => addRecipientBtn.dispatchEvent(new Event('click')));
	}

	function renderNewGiftListeners() {
		document.querySelectorAll('.add-recip').forEach((recip) => {
			// console.log("ADDED add-recip", recip);
			recip.addEventListener('click', function (e) {
				currentTranche = '';
				recipientId = e.target.dataset.key;
				let recipient = capTableData?.find((u) => u.userid == recipientId) || members?.find((u) => u.userid == recipientId); // for regular users, not admins
				recipientName = recipientId == 'issuer' ? clientData.company_name : recipient ? recipient.user_name : 'UNKNOWN';

				theseGifts = {};
				let giftArray = allGifts.filter((g) => g.receiver == recipientId);
				giftArray.forEach((g) => (theseGifts[g.tranche] = g));
				// theseGifts[" "] = ["","",,];
				if (!theseGifts.length) theseGifts[' '] = ['', '', ,];
				gifts = Object.keys(theseGifts);

				const HIDE_COST_BASIS = false; //BUY_BACK && isEDM;	<-- EO wants this displayed now (6-10-2026)
				const ce = BUY_BACK ? '' : 'contenteditable';
				const basisHeader = basisHeaderContent(!!cashSaleRecipients[recipientId]);
				let giftTable = document.getElementById(recipientId + 'Gifts');
				let giftTableMarkup = `
					${
						gifts.length
							? `
					<div class="table-wrap mb-0">
						<div class="sm-hz-scroll-no-margin">
							<table id="recipientsGiftTable" class="recip-table highlight table-content">
								<thead>
									<tr class="text-xs">
										<th class="center-align p-05">Tranche</th>
										${CLASS_COLUMN ? `<th class="center-align -05">Class</th>` : ``}
										<th class="center-align p-05">Date</th>
										<!-- <th class="sm-fixed-left p-05">Notes</th> -->
										<th class="sm-fixed-left p-05">Description</th>
										<th id="qtyHeader|${recipientId}" class="center-align p-05">Quantity</th>
										${HIDE_COST_BASIS ? `` : `<th id="basisHeader|${recipientId}" class="center-align p-05" title="${basisHeader.title}">${basisHeader.label}</th>`}
										<th class="center-align p-05">Current Value</th>
									</tr>
								</thead>
								<tbody>
								${gifts
									.map(
										(gift, index) => `
									<tr id="${gift + '|' + recipientId + '|'}Tranche" class="add-tranche">
										<td id="${gift + '|' + recipientId + '|'}TrancheId"
											class="new-tranche text-xs center-align font-bold p-05" contenteditable style="${index === gifts.length - 1 ? 'outline: 1px solid rgba(0,0,0,0.1); outline-offset: -1px; background-color: #ffffcc;' : ''}">
											${gift}
										</td>
										${
											CLASS_COLUMN
												? `
										<td class="text-xs center-align p-05">
											${theseGifts[gift].shareClass ? theseGifts[gift].shareClass : ''}
										</td>
										`
												: ``
										}
										<td id="editableDate-${gift}" class="gift-date text-xs center-align p-05" ${ce} ${isAdmin ? '' : 'title="Must be within the next 60 days."'}>
											${
												theseGifts[gift].date
													? moment
															.unix(theseGifts[gift].date / 1000)
															.utc()
															.format('MMM D, YYYY')
													: ''
											}
										</td>
										<!-- <td id="editablePre-${gift + '|' + recipientId}" class="select-notes text-xs sm-fixed-left p-05" ${ce}>
											${theseGifts[gift].pre ? theseGifts[gift].pre : ''}
										</td> -->
										<td id="editableNotes-${gift}" class="text-xs sm-fixed-left p-05" title="${gift}" ${ce}>
											${theseGifts[gift].notes ? theseGifts[gift].notes.replace(/_/g, ' ') : ''}
										</td>
										<td id="${recipientId + '|' + gift}-tranche" class="gift-qty text-xs center-align p-05" contenteditable>
											${theseGifts[gift].quantity ? Number(theseGifts[gift].quantity).toLocaleString('en-US', stringOptions) : ''}
										</td>
										${
											HIDE_COST_BASIS
												? ``
												: `
											<td id="editableBasis-${gift}" class="select-gift text-xs center-align p-05" ${isAdmin ? ce : ''}>
											${
												isKnownNumber(theseGifts[gift].taxBasis)
													? Number(String(theseGifts[gift].taxBasis).replace(/[^-.\d]/g, '')).toLocaleString('en-US', usp)
													: isNA(theseGifts[gift].taxBasis)
														? theseGifts[gift].taxBasis
														: gift.trim()
															? 'n.a.'
															: ''
											}
										</td>
										`
										}
										<td id="currentValue-${gift + '|' + recipientId}" class="text-xs center-align p-05">
											${!theseGifts[gift].quantity ? '' : isNaN(currentPrice(theseGifts[gift].shareClass)) ? 'n.a.' : (Number(theseGifts[gift].quantity) * currentPrice(theseGifts[gift].shareClass)).toLocaleString('en-US', usd)}
										</td>
									</tr>
									`
									)
									.join('')}
								</tbody>
							</table>
						</div>
					</div>
					`
							: `
					<div class="bg-grey-lightest rounded border-grey grey-text center-align p-2">
						No gifts found
					</div>
					`
					}
				`;

				giftTable.innerHTML = giftTableMarkup;

				// scroll to bottom
				viewGiftTransferModal.scrollTop = viewGiftTransferModal.scrollHeight;

				renderTrancheListeners();

				let newTranche = document.getElementById(' |' + recipientId + '|Tranche');
				if (gifts.length > 1) newTranche.focus();
				// Should automatically inject new tranche if only one possibility, but...
				// else if (historicHoldings.length == 1) {
				// 	newTranche.children[0].innerHTML = "A";
				// 	let thisTranche = document.querySelectorAll('.new-tranche')[0];
				// 	thisTranche.dispatchEvent(new Event('keyup'));
				// }
				else newTranche.children[0].focus();
			});
		});

		// document.querySelectorAll('.recip-div').forEach(table => {
		// 	console.log("ADDED recip-div", table);
		// 	table.addEventListener('focusin', function(e) {
		// 		recipientId = e.target.id.split('|')[0];
		// 		console.log("table focus, recipientId = " + recipientId);
		// 	});
		// });

		// Cash Sale: all-or-nothing per recipient -- checking mirrors Share Price to
		// Tax Basis for every existing gift of theirs; unchecking reverts to 0.
		// commitTranche() and the 'editableBasis' handler below both consult
		// cashSaleRecipients so this stays in sync going forward too.
		document.querySelectorAll('.cash-sale-checkbox').forEach((checkbox) => {
			checkbox.addEventListener('change', function (e) {
				const thisRecipientId = e.target.dataset.key;
				cashSaleRecipients[thisRecipientId] = e.target.checked;

				// Relabel the Tax Basis/Share Price header live if this recipient's
				// gift table happens to be open right now (see basisHeaderContent()).
				const basisHeaderEl = document.getElementById('basisHeader|' + thisRecipientId);
				if (basisHeaderEl) {
					const { label, title } = basisHeaderContent(e.target.checked);
					basisHeaderEl.innerHTML = label;
					basisHeaderEl.title = title;
				}

				allGifts
					.filter((g) => g.receiver == thisRecipientId)
					.forEach((g) => {
						g.sharePrice = e.target.checked && isKnownNumber(g.taxBasis) ? Number(g.taxBasis).toString() : '0';
					});
			});
		});

		document.querySelectorAll('.remove-gift-recipient').forEach((remove) => {
			// console.log("ADDED remove-gift-recipient", remove);
			remove.addEventListener('click', function (e) {
				e.preventDefault();
				removeGiftId = e.target.getAttribute('data-key');
				let recipientRemoveName = document.getElementById('recipientRemoveName');
				recipientRemoveName.innerHTML = e.target.parentElement.innerText.split('Shares Owned:')[0].trim();
			});
		});

		function renderTrancheListeners() {
			document.querySelectorAll('.add-tranche').forEach((tranche) => {
				const trancheCell = tranche.children[0] || tranche;
				const rowRecipientId = tranche.id.split('|')[1];

				function getAvailableTranches() {
					let priorTranches = priorTransfers.map((t) => t.tranche);
					let availableTranches = giftTransactions.filter((t) => Number(t.quantity) != 0).map((t) => t.tranche);

					availableTranches = availableTranches.filter((a) => {
						let thisHH = giftTransactions.find((h) => h.tranche == a);
						let balance = thisHH ? Number(thisHH.quantity) : 0;
						return !priorTranches.includes(a) || balance > 0;
					});

					return availableTranches;
				}

				function getApplicableTranches(typed) {
					return getAvailableTranches().filter((a) => a.startsWith(typed));
				}

				function commitTranche(resolvedTranche) {
					let availableTranches = getAvailableTranches();
					let gifts = allGifts.filter((g) => g.receiver == rowRecipientId).map((g) => g.tranche);
					gifts.push(' ');

					if (gifts.includes(resolvedTranche) || !availableTranches.includes(resolvedTranche)) {
						trancheCell.innerHTML = '';
						return false;
					}

					trancheCell.innerHTML = resolvedTranche;

					gifts.splice(-1, 0, resolvedTranche);
					let thisTrans = giftTransactions.find((t) => t.tranche == resolvedTranche);
					if (!thisTrans) return false;

					let qtyTrans = thisTrans.quantity;
					let qtyAfterElem = document.getElementById('quantity' + resolvedTranche);
					let qtyAfter = qtyAfterElem ? qtyAfterElem.innerHTML : '';
					if (qtyAfter) qtyTrans = Number(qtyAfter.replace(/[^-.\d]/g, '')).toString();

					// Use prior quantity rather than remaining balance, if available and new recipient
					if (allGifts.length && rowRecipientId != allGifts[allGifts.length - 1].receiver) {
						let priorQty = Number(allGifts[allGifts.length - 1].quantity);
						let availQty = Number(qtyTrans);
						if (availQty >= priorQty) qtyTrans = priorQty.toString();
					}

					let newDate = allGifts.length ? allGifts[allGifts.length - 1].date : Date.now().toString();
					let newNotes = labelBB + ' (' + thisTrans.notes.trim() + ' dated ' + moment.unix(Number(thisTrans.date)).utc().format('M/D/YY') + ')';
					if (thisTrans.certNumber) newNotes = labelBB + ' (' + thisTrans.certNumber + ')';

					if (!BUY_BACK) {
						if (rowRecipientId != 'issuer') {
							if (CERT_COLUMN) {
								const transClass = thisTrans.shareClass;
								const nextCert = nextCertNum[transClass];
								const lastCert = lastCertNum[transClass];
								incrementNextCertNumber(transClass);
								newNotes = nextCert
									? 'Transfer (' + nextCert + (thisTrans.certNumber ? ' from ' + thisTrans.certNumber.replace(/^0+/, '') + ')' : ')')
									: 'Transfer';
								const lastCertNumber = document.getElementById('lastCertNumber');
								const originalLastCert = originalLastCertNum?.[transClass] || lastCertNum[transClass];
								lastCertNumber.innerHTML = lastCert
									? 'Last issued certificate number: ' +
										originalLastCert +
										'&emsp;&emsp;Next available certificate number: ' +
										nextCertNum[transClass]
									: '';
								lastCertNumber.classList.remove('hidden');
							} else {
								newNotes = 'Transfer (' + thisTrans.notes.trim() + ' dated ' + moment.unix(Number(thisTrans.date)).utc().format('M/D/YY') + ')';
							}
						}
						if (isBB) {
							let firstWord = thisTrans.notes.split(' ')[0];
							let dateStart =
								Number.isInteger(Date.parse(firstWord)) ||
								['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'].some((m) =>
									firstWord.toLowerCase().startsWith(m)
								);
							newNotes =
								'Gift from ' +
								(giftUserData.user_name.includes('|') ? giftUserData.user_name.split('|')[1].trim() : giftUserData.user_name.trim()) +
								' - ' +
								moment.unix(Number(thisTrans.date)).utc().format('M/D/YY') +
								' ' +
								(dateStart ? '| ' : '') +
								thisTrans.notes.trim();
						}
					}

					let effectiveTaxBasis = '';
					if (isKnownNumber(thisTrans.taxBasis)) effectiveTaxBasis = Number(thisTrans.taxBasis).toString();
					else if (isKnownNumber(thisTrans._effectiveTaxBasis)) effectiveTaxBasis = Number(thisTrans._effectiveTaxBasis).toString();

					// Cash Sale: a new tranche for a recipient whose box is already checked
					// starts with Share Price mirroring this tranche's Tax Basis (same rule
					// as the checkbox/editableBasis handlers); otherwise the normal 0 default.
					const cashSaleDefaultPrice = cashSaleRecipients[rowRecipientId] && isKnownNumber(effectiveTaxBasis) ? effectiveTaxBasis : '0';

					theseGifts[resolvedTranche] = {
						tranche: resolvedTranche,
						shareClass: thisTrans.shareClass,
						securityType: thisTrans.securityType,
						date: newDate,
						notes: newNotes,
						sender: shareholder,
						senderName: nameWithOverride(shareholder),
						receiver: rowRecipientId,
						receiverName: recipientName,
						quantity: qtyTrans,
						sharePrice: BUY_BACK ? currentPrice(thisTrans.shareClass).toString() : cashSaleDefaultPrice,
						taxBasis: effectiveTaxBasis,

						// NEW
						certNumber: thisTrans.certNumber || '',
						sourceCert: thisTrans.certNumber || '',
						sourceNotes: thisTrans.notes || '',
						sourceDate: thisTrans.date.toString() || '',
					};

					allGifts.push(theseGifts[resolvedTranche]);
					updateConsolidatedTransferCheckbox();
					previewGiftTransfer.classList.remove('disabled');
					adjustBalances(Number(qtyTrans.replace(/[^-.\d]/g, '')), rowRecipientId, resolvedTranche);
					document.getElementById(rowRecipientId + 'AddGift').dispatchEvent(new Event('click'));
					if (isVERM) checkVermeerRule(rowRecipientId);
					return true;
				}

				tranche.addEventListener('focusin', function () {
					currentTranche = trancheCell.innerText.trim().toUpperCase();
					recipientId = rowRecipientId;
				});

				trancheCell.addEventListener('keydown', function (e) {
					let typed = trancheCell.innerText.trim().toUpperCase();
					let availableTranches = getAvailableTranches();
					let applicableTranches = getApplicableTranches(typed);

					if (e.key === 'Tab' || e.key === 'Enter') {
						e.preventDefault();

						if (!typed) return;

						// Prefer exact typed tranche, so "A" wins over AA/AB/...
						if (availableTranches.includes(typed)) {
							commitTranche(typed);
							return;
						}

						// Otherwise accept a unique prefix match
						if (applicableTranches.length === 1) {
							commitTranche(applicableTranches[0]);
							return;
						}
					}
				});

				tranche.addEventListener('keyup', function (e) {
					const isTrancheInput = e.target === trancheCell;
					const onTarget = e.target?.id?.endsWith('|TrancheId') ?? false;
					let thisTranche = trancheCell.innerText.trim().toUpperCase();

					recipientId = rowRecipientId;

					// Ignore bubbled tranche logic from non-tranche cells
					if (!isTrancheInput && onTarget) return;

					// Clear any pending auto-commit for this row
					const timerKey = tranche.id;
					if (trancheAutoTimers[timerKey]) {
						clearTimeout(trancheAutoTimers[timerKey]);
						delete trancheAutoTimers[timerKey];
					}

					if (!thisTranche && !onTarget) {
						e.target.innerText = '';
						return;
					}

					if (!thisTranche) {
						let thisQty = document.getElementById(rowRecipientId + '|' + currentTranche + '-tranche');
						thisQty = thisQty ? thisQty.innerText : null;
						if (thisQty) {
							adjustBalances(-Number(thisQty.replace(/[^-.\d]/g, '')), rowRecipientId, currentTranche);
							allGifts = allGifts.filter((g) => g.receiver != rowRecipientId || g.tranche != currentTranche);
							updateConsolidatedTransferCheckbox();
							document.getElementById(rowRecipientId + 'AddGift').dispatchEvent(new Event('click'));
							renderNewGiftListeners();
							if (isVERM) checkVermeerRule(rowRecipientId);
						}
						return;
					}

					if (onTarget) {
						let availableTranches = getAvailableTranches();
						let applicableTranches = getApplicableTranches(thisTranche);

						if (applicableTranches.length == 1) thisTranche = applicableTranches[0];
						else if (applicableTranches.length > 1 && e.key != 'Tab' && e.key != 'Enter') {
							trancheAutoTimers[timerKey] = setTimeout(() => {
								let pausedTranche = trancheCell.innerText.trim().toUpperCase();
								if (!pausedTranche) return;
								if (getAvailableTranches().includes(pausedTranche)) commitTranche(pausedTranche);
							}, 1000);
							return;
						}

						// For plain typing, auto-commit only when there is exactly one match
						if (availableTranches.includes(thisTranche) && applicableTranches.length == 1) {
							commitTranche(thisTranche);
						}
					} else {
						let i = allGifts.findIndex((g) => g.receiver == rowRecipientId && g.tranche == thisTranche);
						if (i < 0) return;

						switch (e.target.id.split('-')[0]) {
							case 'editableDate':
								allGifts[i].date = Date.parse(e.target.innerText).toString();
								break;
							// case 'editablePre': allGifts[i].pre = e.target.innerText; break;
							case 'editableNotes':
								allGifts[i].notes = e.target.innerText;
								break;
							case 'editableBasis':
								{
									const rawBasis = e.target.innerText.replace(/[^-.\d]/g, '');
									allGifts[i].taxBasis = rawBasis;
									// Cash Sale: mirror Share Price to Tax Basis live on every keystroke;
									// falls back to 0 mid-edit (e.g. "-" or "") before it's a real number.
									if (cashSaleRecipients[rowRecipientId]) {
										allGifts[i].sharePrice = isKnownNumber(rawBasis) ? Number(rawBasis).toString() : '0';
									}
								}
								break;
						}
					}
				});
			});

			document.querySelectorAll('.gift-date').forEach((dateElem) => {
				dateElem.addEventListener('blur', function () {
					const rawText = this.innerText.trim();

					let parsed = moment(rawText, ['MMMM D, YYYY', 'MMM D, YYYY', 'M/D/YYYY', 'M-D-YYYY', 'YYYY-MM-DD'], true);

					const today = moment.utc().startOf('day');
					const maxDate = moment.utc().add(60, 'days').startOf('day');

					// Replace junk or empty with today
					if (!parsed.isValid()) parsed = today;

					// Clamp to range
					if (!isAdmin) {
						if (parsed.isBefore(today)) parsed = today;
						if (parsed.isAfter(maxDate)) parsed = maxDate;
					}

					// Update display
					this.innerText = parsed.format('MMMM D, YYYY');
				});
			});

			let origQuantity;
			let newQuantity;
			document.querySelectorAll('.gift-qty').forEach((qty) => {
				// renderTrancheListeners() re-scans ALL .gift-qty cells every call, not just
				// the current recipient's. The old guard compared unrelated things and could
				// double-bind listeners (corrupting Before/After) or skip unbound cells.
				// Track binding on the element itself so each cell gets listeners exactly once.
				if (qty.dataset.giftQtyBound) return;
				qty.dataset.giftQtyBound = '1';
				qty.addEventListener('focus', function () {
					recipientId = this.id.split('|')[0];
					let thisElem = document.getElementById(this.id);
					origQuantity = Number(thisElem.innerHTML.replace(/[^-.\d]/g, ''));
					if (origQuantity == 0) thisElem.innerHTML = '';
				});
				qty.addEventListener('blur', function () {
					let thisElem = document.getElementById(this.id);
					origQuantity = Number(thisElem.innerHTML.replace(/[^-.\d]/g, ''));
					thisElem.innerHTML = origQuantity.toLocaleString('en-US', stringOptions);
				});
				qty.addEventListener('input', function () {
					let targetTranche = this.id.split('|')[1].replace('-tranche', '').trim();
					let foundTranche = giftTransactions.find((g) => g.tranche == targetTranche);
					let maxQuantity = Number(foundTranche ? foundTranche.quantity : 0);
					let thisElem = document.getElementById(this.id);
					let newText = thisElem.innerHTML.replace(/[^-.\d]/g, '');
					// Insert the leading zero live (".99" -> "0.99"); also keeps "." from
					// parsing as NaN, which fed the bug fixed below.
					newText = newText.replace(/^(-?)\./, '$10.');
					newQuantity = Number(newText);
					thisElem.innerHTML = newQuantity || newText.includes('.') || newText == '0' ? newText : '';
					document.getSelection().collapse(this, this.childNodes.length);
					if (newQuantity > maxQuantity || newQuantity < 0) {
						// Clamp to a valid share count; reassign newText too so the
						// displayed value matches (don't leave a stale negative on screen).
						newQuantity = newQuantity < 0 ? 0 : maxQuantity;
						newText = newQuantity.toString();
						thisElem.innerHTML = newQuantity ? newText : '';
						document.getSelection().collapse(this, this.childNodes.length);
					}
					// Skip on NaN (in-progress keystroke) instead of poisoning
					// origQuantity, which was silently dropping a step of the edit
					// from the running totals.
					if (!isNaN(newQuantity)) {
						let difference = newQuantity - origQuantity;
						adjustBalances(difference, recipientId, targetTranche);
						let thisGiftIndex = allGifts.findIndex((g) => g.receiver == recipientId && g.tranche == targetTranche);
						// GH #6139: guard against stale DOM referencing a tranche no longer in allGifts (-1 index).
						if (thisGiftIndex >= 0) {
							allGifts[thisGiftIndex].quantity = newQuantity.toString();
							let correntValueElem = document.getElementById('currentValue-' + targetTranche + '|' + recipientId);
							let newPrice = currentPrice(allGifts[thisGiftIndex].shareClass);
							if (correntValueElem) correntValueElem.innerHTML = isNaN(newPrice) ? 'n.a.' : (newQuantity * newPrice).toLocaleString('en-US', usd);
						}
						origQuantity = newQuantity;
					}
					if (isVERM) checkVermeerRule(recipientId);
				});
			});
		}
	}

	function adjustBalances(adjustment, recipient, tranche) {
		if (isNaN(adjustment)) adjustment = 0;
		// TO headline
		console.log('adjustBalances: ', adjustment, recipient, tranche);
		let thisRecip = giftRecipients.find((r) => r.userid == recipient);
		if (thisRecip) {
			thisRecip.after += adjustment;
			if (!BUY_BACK) document.getElementById(recipient + '|After').innerHTML = thisRecip.after.toLocaleString('en-US', stringOptions);
		}

		// FROM headline
		balanceAfter -= adjustment;
		let balanceAfterElem = document.getElementById('balanceAfterElem');
		balanceAfterElem.innerHTML = balanceAfter.toLocaleString('en-US', stringOptions);
		if (balanceAfter < 0) {
			balanceAfterElem.classList.add('red-text', 'font-bold');
		} else {
			balanceAfterElem.classList.remove('red-text', 'font-bold');
		}

		// FROM table
		let quantityTranche = document.getElementById('quantity' + tranche);
		if (quantityTranche) {
			let quantityBefore = Number(quantityTranche.innerHTML.replace(/[^-.\d]/g, ''));
			let quantityAfter = quantityBefore - adjustment;
			quantityTranche.innerHTML = quantityAfter.toLocaleString('en-US', stringOptions);
			if (quantityAfter < 0) {
				quantityTranche.classList.add('red-text');
				quantityTranche.classList.add('font-bold');
				previewGiftTransfer.classList.add('disabled');
			} else {
				quantityTranche.classList.remove('red-text');
				quantityTranche.classList.remove('font-bold');
				previewGiftTransfer.classList.remove('disabled');
			}
		}

		// FROM total footer
		if (toggle != 'Show Zeros') {
			ttlAfter -= adjustment;
			document.getElementById('totalAfter').innerHTML = ttlAfter.toLocaleString('en-US', stringOptions);
		}

		let thisGift = allGifts.find((g) => g.receiver === recipient && g.tranche === tranche);
		let thisShareClass = thisGift ? thisGift.shareClass : '';
		let price = currentPrice(thisShareClass);
		let totalCurrentValue = document.getElementById('totalCurrentValue'); // need re-rendered element
		if (!isNaN(price)) {
			if (!isKnownNumber(ttlCurrentValue)) ttlCurrentValue = 0;
			ttlCurrentValue += adjustment * price;
		}
		totalCurrentValue.innerHTML = isNaN(ttlCurrentValue) ? 'n.a.' : ttlCurrentValue.toLocaleString('en-US', usd);
		totalCurrentValueDiv.classList.remove('hidden');

		// resize modal
		document.getElementById('viewGiftTransferModal').style.overflowY = 'auto';
	}

	function currentPrice(shareClass) {
		// #6063: shareClass may not resolve to anything in clientData.equity (e.g. adjustBalances falls back to '').
		// Return NaN like any other "no price" case -- callers already check isNaN(price)/currentPrice(...)?.
		let classVals = clientData.equity[shareClass]?.valuations;
		if (!classVals) return NaN;
		classVals.sort((a, b) => Number(b.date) - Number(a.date)); // newest to oldest
		if (!classVals?.[0]) return 'n.a.';
		return Number(classVals[0].sharePrice.replace(/[^.\d]/g, ''));
	}

	async function getNextCertNumber() {
		if (!nextCertNum) {
			lastCertNum = {};
			nextCertNum = {};
			if (EXTERNAL_DATA) {
				const dataFile = clientData.transactions_data ? clientData.transactions_data : '';
				if (!dataFile) return;
				let dataFileUrl = await getContentUrl(dataFile);
				try {
					const response = await fetch(dataFileUrl); // 🔹 Ensure fetch waits
					console.log(response);
					const results = await response.text(); // 🔹 Ensure text conversion waits
					// NOTE BENE (3/9/2025): External Data may or may not have a dedicated Certificate column. Painful...
					const data = Papa.parse(results).data;
					const certColumn = data[0][3].toLowerCase().startsWith('cert');
					CERT_COLUMN ||= data.some((row) => row[3]?.includes('#'));
					if (CERT_COLUMN || certColumn) {
						classes.forEach((c) => {
							nextCertNum[c] = '';
							let highestCertNum = 0;
							let classData = data.filter((d) => d[0] == c);
							classData = classData.filter((row) => row[3]?.includes('#') || row[4]?.includes('#'));
							classData.forEach((row) => {
								[3, 4].forEach((index) => {
									// Check only columns 3 and 4
									let cell = row[index];
									if (cell.includes('#')) {
										let certNumber = getCertNumber(cell);
										if (certNumber) {
											let certString = `#${certNumber}`; // Full matched cert (e.g., "#PA-22")
											// Extract numeric portion from certificate (e.g., "22" from "#PA-22")
											let numMatch = certString.match(/(\d+)/);
											if (numMatch) {
												let certNum = Number(numMatch[1], 10);
												if (certNum > highestCertNum) {
													highestCertNum = certNum;
													nextCertNum[c] = certString; // Store full certificate string
												}
											}
										}
									}
								});
							});
							incrementNextCertNumber(c);
							originalLastCertNum ||= {};
							originalLastCertNum[c] = lastCertNum[c];
						});
					}
				} catch (err) {
					console.warn('Something went wrong.', err);
				}
			} else {
				let clientEquity = clientData.equity;
				for (let c in clientEquity) {
					let transactions = clientEquity[c].transactions;
					nextCertNum[c] =
						'#' +
						transactions.reduce((maxCertNum, transaction) => {
							let match = transaction.notes.match(/\(#(\d+)\)/);
							let certNumber = match ? Number(match[1]) : 0;
							return Math.max(maxCertNum, certNumber);
						}, 0); // Start from 1 above the highest certificate number
					incrementNextCertNumber(c);
					originalLastCertNum ||= {};
					originalLastCertNum[c] = lastCertNum[c];
				}
			}
		}
	}

	function incrementNextCertNumber(c) {
		lastCertNum[c] = nextCertNum[c];
		const certNumber = getCertNumber(nextCertNum[c]);
		if (certNumber) {
			let newNumber;
			const suffix = certNumber.split('-')[1];
			if (suffix) newNumber = suffix.replace(/[^\d.]/g, '');
			else newNumber = certNumber.replace(/[^\d.]/g, '');
			if (isNaN(newNumber)) nextCertNum[c] = '';
			// Re-prefix with "#" -- nextCertNum[c] always carries a leading "#" by
			// convention (see its other assignments above/below), but getCertNumber()
			// never includes one in its return value; without re-adding it here, the
			// *next* getCertNumber(nextCertNum[c]) call on this value would find no match.
			else nextCertNum[c] = `#${certNumber.replace(newNumber, Number(newNumber) + 1)}`;
		} else nextCertNum[c] = '';
	}

	function checkVermeerRule(recipient) {
		let header = document.getElementById('qtyHeader|' + recipient);
		let recipGifts = allGifts.filter((g) => g.receiver == recipient);
		if (!recipGifts) {
			header.innerHTML = 'Quantity';
			return;
		}
		let qtyClassE = recipGifts.filter((g) => g.shareClass == 'E').reduce((a, b) => a + Number(b.quantity), 0);
		let qtyOther = recipGifts.filter((g) => ['V', 'NV'].includes(g.shareClass)).reduce((a, b) => a + Number(b.quantity), 0);
		if (qtyClassE == qtyOther) header.innerHTML = 'Quantity';
		else header.innerHTML = '<span class="orange-text" title="E != V + NV">⚠</span> Quantity';
		console.log('header.innerHTML = ' + header.innerHTML);
	}

	function updateConsolidatedTransferCheckbox() {
		const consolidateTransfers = document.getElementById('consolidateTransfers');
		if (!consolidateTransfers) return;

		const counts = {};
		allGifts.forEach((g) => {
			if (!g?.receiver || !g?.tranche) return;
			counts[g.receiver] = (counts[g.receiver] || 0) + 1;
		});

		const enable = Object.values(counts).some((n) => n > 1);
		consolidateTransfers.disabled = !enable;
		if (!enable) consolidateTransfers.checked = false;
	}

	function certFromTransferNotes(notes) {
		const m = String(notes || '').match(/\((#[^\s)]+)/);
		return m ? m[1] : '';
	}

	function rewindNextCertNumber(c, count) {
		while (count-- > 0) {
			const certNumber = getCertNumber(nextCertNum[c]);
			if (!certNumber) return;
			const suffix = certNumber.split('-')[1];
			const currentNumber = suffix ? suffix.replace(/[^\d.]/g, '') : certNumber.replace(/[^\d.]/g, '');
			if (isNaN(currentNumber)) return;
			// Re-prefix with "#" -- see incrementNextCertNumber's comment above.
			nextCertNum[c] = `#${certNumber.replace(currentNumber, Number(currentNumber) - 1)}`;
		}
	}

	async function getConsolidatedTransferGifts() {
		const out = [];
		const giftsByRecipient = {};
		await getNextCertNumber();

		allGifts.forEach((g) => {
			if (!g?.receiver) return;
			if (!giftsByRecipient[g.receiver]) giftsByRecipient[g.receiver] = [];
			giftsByRecipient[g.receiver].push(g);
		});

		Object.values(giftsByRecipient).forEach((gifts) => {
			if (gifts.length < 2) {
				out.push(...gifts);
				return;
			}

			const first = gifts[0];
			const qtyTotal = gifts.reduce((sum, g) => sum + Number(String(g.quantity || 0).replace(/[^-.\d]/g, '')), 0);

			const basisNumerator = gifts.reduce((sum, g) => {
				const qty = Number(String(g.quantity || 0).replace(/[^-.\d]/g, ''));
				const basis = isKnownNumber(g.taxBasis) ? Number(g.taxBasis) : isKnownNumber(g.sharePrice) ? Number(g.sharePrice) : 0;
				return sum + qty * basis;
			}, 0);

			// Cash Sale: Share Price mirrors Tax Basis per tranche, so it needs the
			// same quantity-weighted average as taxBasis above (no-op otherwise).
			const priceNumerator = gifts.reduce((sum, g) => {
				const qty = Number(String(g.quantity || 0).replace(/[^-.\d]/g, ''));
				const price = isKnownNumber(g.sharePrice) ? Number(g.sharePrice) : 0;
				return sum + qty * price;
			}, 0);

			const sourceLabel = compactSourceLabel(gifts);
			const newCertNum = CERT_COLUMN ? certFromTransferNotes(first.notes) : '';
			if (newCertNum && gifts.length > 1) rewindNextCertNumber(first.shareClass, gifts.length - 1);

			out.push({
				...first,
				tranche: gifts.map((g) => g.tranche).join('+'),
				quantity: qtyTotal.toString(),
				taxBasis: qtyTotal ? (basisNumerator / qtyTotal).toString() : first.taxBasis,
				sharePrice: qtyTotal ? (priceNumerator / qtyTotal).toString() : first.sharePrice,
				notes: CERT_COLUMN ? 'Transfer (' + newCertNum + ' from ' + sourceLabel + ')' : 'Transfer (from ' + sourceLabel + ')',
				sourceTranches: gifts.map((g) => ({
					tranche: g.tranche,
					quantity: g.quantity,
					taxBasis: g.taxBasis,
					sharePrice: g.sharePrice,
					nrid: g.nrid,
					certNumber: g.certNumber || '',
					sourceNotes: g.sourceNotes || '',
					sourceDate: g.sourceDate || '',
				})),
			});
		});

		return out;
	}

	function compactSourceLabel(gifts) {
		const certs = gifts
			.map((g) => {
				if (g.sourceCert || g.certNumber) return g.sourceCert || g.certNumber;
				const refs = parseCertificateRefs(g.notes);
				if (refs.sourceCerts?.length) return refs.sourceCerts[0];
				return '';
			})
			.filter(Boolean)
			.map((c) => String(c).replace(/^#/, '').replace(/^0+/, ''));
		if (certs.length) return certs.join(', ');
		return gifts
			.map((g) => g.tranche)
			.filter(Boolean)
			.join(', ');
	}

	function renderTransactionMapTable(giftsForPreview) {
		let rowErrors;
		giftSources = [];
		let badDataCounter = 0;
		let importTransCounter = 0;
		let pendingBatchRecords = [];
		let pendingBatchAdjustments = [];
		let newTransactionsOk = true;
		let importGiftTransferMarkup;
		// need new object
		let importTransMap = JSON.parse(JSON.stringify(giftsForPreview || allGifts));
		const CANCEL_REISSUE = CERT_COLUMN; //isVERM;	// need for Ausable and Etnyre too.  Maybe Lucas??

		function expandConsolidatedGifts(gifts) {
			const expanded = [];
			gifts.forEach((g) => {
				if (!Array.isArray(g.sourceTranches) || g.sourceTranches.length < 2) {
					expanded.push(g);
					return;
				}

				// Break back into source-level gifts
				g.sourceTranches.forEach((s) => {
					expanded.push({
						...g,
						tranche: s.tranche,
						quantity: s.quantity,
						taxBasis: s.taxBasis,
						sharePrice: s.sharePrice,
						sourceTranches: [s], // now single
						notes: g.notes, // keep consolidated note for UI
					});
				});
			});
			return expanded;
		}

		const stubCalcMap = expandConsolidatedGifts(importTransMap);

		function sourceTranchesForGift(gift) {
			if (Array.isArray(gift.sourceTranches) && gift.sourceTranches.length) {
				return gift.sourceTranches.map((s) => (typeof s === 'string' ? s : s.tranche)).filter(Boolean);
			}

			return String(gift.tranche || '')
				.split('+')
				.map((t) => t.trim())
				.filter(Boolean);
		}

		if (CANCEL_REISSUE) {
			let uniqueTranches = [...new Set(stubCalcMap.flatMap((item) => sourceTranchesForGift(item)).filter(Boolean))];

			uniqueTranches.forEach((t) => {
				let theseSources = giftTransactions.filter((g) => g.tranche == t);
				if (theseSources.length != 1) {
					console.log('Gift source issue:', t, theseSources);
					alert('Gift source issue.');
					return;
				}
				const giftSource = theseSources[0];
				giftSources.push(giftSource);

				const giftDate = stubCalcMap.find((i) => sourceTranchesForGift(i).includes(t))?.date;
				const trancheSum = stubCalcMap.filter((i) => sourceTranchesForGift(i).includes(t)).reduce((acc, j) => acc + Number(j.quantity), 0);
				const stubAmount = Number(giftSource.quantity) - trancheSum;
				const nextCert = nextCertNum[giftSource.shareClass];

				if (stubAmount > 0) {
					// create two more transactions: a cancellation and a reissue
					let cancellation = {
						tranche: t,
						shareClass: giftSource.shareClass,
						securityType: giftSource.securityType,
						date: giftDate,
						notes: giftSource.certNumber ? 'Cancellation (' + giftSource.certNumber + ')' : 'Cancellation',
						sender: giftSource.receiver,
						senderName: giftSource.receiverName,
						receiver: '',
						receiverName: 'Issuer',
						quantity: stubAmount.toString(),
						sharePrice: giftSource.sharePrice || '',
						taxBasis: giftSource._effectiveTaxBasis != null ? giftSource._effectiveTaxBasis.toString() : '',
					};

					let reissuance = {
						tranche: t,
						shareClass: giftSource.shareClass,
						securityType: giftSource.securityType,
						date: giftDate,
						notes: nextCert ? 'Reissuance (' + nextCert + ' from ' + giftSource.certNumber + ')' : 'Reissuance',
						sender: '',
						senderName: 'Issuer',
						receiver: giftSource.receiver,
						receiverName: giftSource.receiverName,
						quantity: stubAmount.toString(),
						sharePrice: giftSource.sharePrice || '',
						taxBasis: giftSource._effectiveTaxBasis != null ? giftSource._effectiveTaxBasis.toString() : '',
					};

					let insertPoint = 0;
					for (let i = importTransMap.length - 1; i >= 0; i--) {
						if (sourceTranchesForGift(importTransMap[i]).includes(t)) {
							insertPoint = i;
							break;
						}
					}

					importTransMap.splice(insertPoint + 1, 0, cancellation);
					importTransMap.splice(insertPoint + 2, 0, reissuance);
					if (nextCert) incrementNextCertNumber(giftSource.shareClass);
				}
			});
		}

		if (selectedTranches.length > 0) {
			let reason = ''; //selectedTranches.length > 1 ? 'Stock Certificate' : 'Stock Certificate';
			let grossQty = 0;
			let grossDollars = 0;
			importTransMap = [];
			selectedTranches.sort();
			selectedTranches.forEach((t) => {
				let giftSource = giftTransactions.find((g) => g.tranche == t);
				giftSources.push(giftSource);
				let consolTranche = {
					tranche: t,
					shareClass: giftSource.shareClass,
					securityType: giftSource.securityType,
					date: Date.now().toString(),
					notes: reason + (giftSource.certNumber ? 'Cancellation (' + giftSource.certNumber + ')' : 'Cancellation'),
					sender: giftSource.receiver,
					senderName: giftSource.receiverName,
					receiver: '',
					receiverName: 'Issuer',
					quantity: giftSource.quantity,
					sharePrice: giftSource.sharePrice || '',
					taxBasis: giftSource._effectiveTaxBasis != null ? giftSource._effectiveTaxBasis.toString() : '',
				};
				let consolQty = Number(giftSource.quantity.replace(/[^.\d]/g, ''));
				grossQty += consolQty;
				grossDollars += consolQty * Number(isNaN(giftSource.sharePrice) ? 0 : giftSource.sharePrice.replace(/[^.\d]/g, ''));
				importTransMap.push(consolTranche);
			});

			let reissuance = JSON.parse(JSON.stringify(importTransMap[0]));
			const nextCert = nextCertNum[reissuance.shareClass];
			reissuance.tranche = 'NEW';
			reissuance.notes = reason + (nextCert && CERT_COLUMN ? ' Reissuance (' + nextCert + ')' : ' Reissuance');
			reissuance.receiver = reissuance.sender;
			reissuance.receiverName = reissuance.senderName;
			reissuance.sender = '';
			reissuance.senderName = 'Issuer';
			reissuance.quantity = grossQty.toString();
			reissuance.sharePrice = (grossDollars / grossQty).toLocaleString('en-US', m5);

			importTransMap.push(reissuance);
			// incrementNextCertNumber(reissuance.shareClass);
		}

		let importTransTarget = importTransMap.length;
		let headings = ['Tranche', 'Date', 'Sender', 'Receiver', 'Description', 'Quantity', 'Share Price', 'Tax Basis'];
		if (CLASS_COLUMN) headings = ['Tranche', 'Class', 'Date', 'Sender', 'Receiver', 'Description', 'Quantity', 'Share Price', 'Tax Basis'];

		// #6144: same Share Price/Tax Basis wording as the Ledgers transaction modals
		// (index.html #transactionSharePriceInput/#transactionTaxBasisInput labels, 2026-08-05
		// per Chris/Stuart) -- one explanation across the app instead of another bespoke one.
		const columnTooltips = {
			'Share Price':
				'Also called Price Paid: the price actually paid or received per\nshare/unit in this transaction. Often $0 for gifts and transfers.\nFor a cash sale, Tax Basis usually equals Share Price.',
			'Tax Basis':
				'Also called Cost Basis: the value used for tax-reporting purposes.\nMay differ from Share Price/Price Paid. For gifts and transfers,\nTax Basis often carries over from the prior owner.',
		};

		// Explicit break (not CSS auto-wrap) so the icon can sit glued to 'Price'/'Basis'
		// with no space to wrap away on.
		const headingBreaks = { 'Share Price': ['Share', 'Price'], 'Tax Basis': ['Tax', 'Basis'] };

		if (!importTransMap || !importTransMap.length) {
			importGiftTransferMarkup = `
			<div class="col s12">
				<div class="py-12 bg-grey-lightest border-blue-dotted rounded center-align">
					<p class="max-w-sm mx-auto">No transfer data found.</p>
				</div>
			</div>
			`;
		} else {
			rowErrors = new Array(importTransMap.length).fill('');
			importGiftTransferMarkup = `
				<div class="row">
					<div class="col s12">
					<h3 id="importGiftTransferHeading" class="text-lg center-align mb-0 font-bold">Ready for Import</h3>
					</div>
				</div>
				<div class="table-wrap border-grey" style="width: fit-content;">
					<div class="sm-hz-scroll-no-margin">
						<table id="importGiftTransferSortTable" class="highlight table-content">
							<thead>
								<tr>
									${headings
										.map(
											(h) => `
										<th class="text-xs center-align" title="${columnTooltips[h] || ''}">
							${
								headingBreaks[h]
									? `${headingBreaks[h][0]}${
											columnTooltips[h]
												? '<i class="material-icons" style="font-size: 10px; vertical-align: middle; color: #999; margin-left: 2px;">info_outline</i>'
												: ''
										}&ensp;&ensp;<br>${headingBreaks[h][1]}`
									: h
							}
						</th>
										`
										)
										.join('')}
									<th class="text-xs center-align">Status</th>
								</tr>
							</thead>
							<tbody>
							${importTransMap
								.map(
									(t, i) =>
										`
								<tr class="import-tranche">
									<td>
										<p class="text-xs mb-0 mt-0 center-align">${t.tranche}</p>
									</td>
									${
										CLASS_COLUMN
											? `
									<td>
										<p class="text-xs mb-0 mt-0 center-align">${t.shareClass}</p>
									</td>`
											: ``
									}
									<td>
										<p id="editableImportDate-${i}" class="text-xs mb-0 mt-0 center-align" contenteditable>${moment
											.unix(Number(t.date) / 1000)
											.utc()
											.format('MMM D, YYYY')}</p>
									</td>
									<td>
										<p class="text-xs mb-0 mt-0 center-align">${t.senderName}</p>
									</td>
									<td>
										<p class="text-xs mb-0 mt-0 center-align">${t.receiverName}</p>
									</td>
									<td>
										<p id="editableImportNotes-${i}" class="text-xs mb-0 mt-0 center-align" contenteditable>${t.notes}</p>
										<!-- <p class="text-xs mb-0 mt-0 center-align">${t.pre && t.notes ? t.pre + ' - ' + t.notes : t.pre ? t.pre : t.notes}</p> -->
									</td>
									<td>
										<p class="text-xs mb-0 mt-0 center-align">${Number(t.quantity).toLocaleString('en-US', stringOptions)}</p>
									</td>
									<td>
										<p id="editableImportSharePrice-${i}" class="text-xs mb-0 mt-0 center-align" contenteditable>${
											isKnownNumber(t.sharePrice)
												? Number(String(t.sharePrice).replace(/[^.\d]/g, '')).toLocaleString('en-US', usp)
												: isNA(t.sharePrice)
													? t.sharePrice
													: 'n.a.'
										}
										</p>
									</td>
									<td>
										<p id="editableImportBasis-${i}" class="text-xs mb-0 mt-0 center-align" contenteditable>${
											isKnownNumber(t.taxBasis)
												? Number(String(t.taxBasis).replace(/[^.\d]/g, '')).toLocaleString('en-US', usp)
												: isNA(t.taxBasis)
													? t.taxBasis
													: 'n.a.'
										}
										</p>
									</td>
									<td id="${'row-' + i}" class="center-align">${dataOkay(t, i) ? greenCheck : redClear}</td>
								</tr>
								`
								)
								.join('')}
							</tbody>
						</table>
					</div>
				</div>
				<div class="flex justify-between items-center">
					<a id="continueEditing" href="#" class="modal-close waves-effect waves-light btn grey mt-6 left">Go Back</a>
	                  <label class="mt-4" title="If checked, cap table (or capital account) balances will be&#10;adjusted according to the allocations. Otherwise the&#10;cap table will remain unchanged." style="transform: scale(0.8);">
	                    <input type="checkbox" id="adjustCapTableAutomatically" checked>
	                    <span style="display:inline-table;">Automatically adjust cap table balances</span>
	                  </label>
					<p class="mr-4 mt-8 text-sm right"><a id="exportGiftTransferMap" href="" download="" target="_blank" rel="noopener noreferrer">Export to Excel</a></p>
					<button id="proceedWithGiftTransferImport" class="right waves-effect waves-light btn mt-6 ml-8 bg-grey-darker">Proceed with Import</button>
				</div>
			`;
		}

		let importGiftTransfer = document.getElementById('importGiftTransfer');
		let importGiftTransferTable = document.getElementById('importGiftTransferTable');
		importGiftTransferTable.innerHTML = importGiftTransferMarkup;
		importGiftTransferTable.classList.remove('hidden');

		let proceedWithGiftTransferImport = document.getElementById('proceedWithGiftTransferImport');
		let importGiftTransferHeading = document.getElementById('importGiftTransferHeading');
		let statementWaitState = document.getElementById('statementWaitState');
		let continueEditing = document.getElementById('continueEditing');
		let processHeader = document.getElementById('processHeader');

		function dataOkay(newTrans, index) {
			if (!capTableData) capTableData = NthUtil.sessionStore.readCapTable(client);
			let validUsers = [];
			capTableData.forEach((u) => validUsers.push(u.userid));
			if (clientData.treasury_account) {
				const t = clientData.treasury_account.split('|')[1]?.trim();
				if (t && !validUsers.includes(t)) validUsers.push(t);
			}
			badDataCounter++;
			let thisSender = capTableData.find((s) => s.userid == newTrans.sender);
			let senderShares = thisSender ? thisSender.clients[client].shares[newTrans.shareClass] : null;
			let senderApproved = senderShares ? senderShares.approved : '0';
			let timeStamp = Number(newTrans.date);
			let types = [
				'Ordinary Shares',
				'Restricted Shares',
				'RSUs',
				'Profits Interest',
				'SARs',
				'Options',
				'Warrants',
				'Convertibles',
				'Debt',
				'Capital Contribution',
				'Partnership Interest',
			];
			if (!classes.includes(newTrans.shareClass)) {
				rowErrors[index] = newTrans.shareClass + ' is not a Class in Company Profile / Equity.';
				return false;
			}
			if (!types.includes(newTrans.securityType)) {
				rowErrors[index] = newTrans.securityType + ' is not a recognized Security Type.';
				return false;
			}
			if (newTrans.strikePrice && newTrans.strikePrice <= 0) {
				rowErrors[index] = newTrans.strikePrice + ' is not a valid Strike Price.';
				return false;
			}
			if (newTrans.expDate && newTrans.expDate.toLowerCase() == 'invalid date') {
				rowErrors[index] = newTrans.expDate + ' is not a valid exp date.';
				return false;
			}
			if (newTrans.date.toLowerCase() == 'invalid date' || timeStamp < 0 || timeStamp > 1924992000000) {
				// December 31, 2030
				rowErrors[index] =
					moment
						.unix(Number(newTrans.date) / 1000)
						.utc()
						.format('MMM D, YYYY') + ' is not a valid date.';
				return false;
			}
			if (!validUsers.includes(newTrans.sender) && newTrans.senderName.toLowerCase() != 'issuer') {
				rowErrors[index] = newTrans.sender + ' is not a valid Sender.';
				return false;
			}
			if (!validUsers.includes(newTrans.receiver) && !['issuer', clientData.company_name.toLowerCase()].includes(newTrans.receiverName.toLowerCase())) {
				rowErrors[index] = newTrans.receiver + ' is not a valid Receiver.';
				return false;
			}
			if (Number(newTrans.quantity) <= 0) {
				rowErrors[index] = newTrans.quantity + ' is not a valid Quantity.';
				return false;
			}
			// if (thisSender && thisSender.userid.toLowerCase() != "issuer" && Number(senderApproved) < Number(newTrans[7])) {
			// 	rowErrors[index] = newTrans[nmCol] + " exceeds Approved balance.";
			// 	return false;
			// }
			if (Number(newTrans.sharePrice) < 0) {
				rowErrors[index] = newTrans.sharePrice + ' is not a valid Share Price.';
				return false;
			}
			if (Number(newTrans.taxBasis) < 0) {
				rowErrors[index] = newTrans.taxBasis + ' is not a valid Tax Basis.';
				return false;
			}
			badDataCounter--;
			return true;
		}

		let num = 'zero one two three four five six seven eight nine ten eleven twelve thirteen fourteen fifteen sixteen seventeen eighteen nineteen'.split(
			' '
		);
		let tens = 'twenty thirty forty fifty sixty seventy eighty ninety'.split(' ');
		function number2words(n) {
			if (n < 20) return num[n];
			let digit = n % 10;
			if (n < 100) return tens[~~(n / 10) - 2] + (digit ? '-' + num[digit] : '');
			if (n < 1000) return num[~~(n / 100)] + ' hundred' + (n % 100 == 0 ? '' : ' and ' + number2words(n % 100));
			return number2words(~~(n / 1000)) + ' thousand' + (n % 1000 != 0 ? ' ' + number2words(n % 1000) : '');
		}

		if (badDataCounter > 0) {
			rowErrors.forEach(function (err, i) {
				if (err.length)
					document.getElementById('row-' + i).innerHTML =
						`<span class="material-icons-outlined align-middle" style="color: red; font-size: 30px;" title="${err}">clear</span>`;
			});
			importGiftTransferHeading.innerHTML =
				capitalize(number2words(badDataCounter)) + (badDataCounter > 1 ? ' rows have' : ' row has') + ' bad data.  Please try again.';
			proceedWithGiftTransferImport.classList.add('disabled');
			// return false;
		} else if (importTransMap && importTransMap.length) {
			importGiftTransferHeading.innerHTML =
				capitalize(number2words(importTransMap.length)) + ' Transaction' + (Number(importTransMap.length) > 1 ? 's' : '') + ' Ready for Import';
			proceedWithGiftTransferImport.classList.remove('disabled');
		}

		let table = document.getElementById('importGiftTransferSortTable');
		if (table) {
			let sort = new Tablesort(table);
			document.getElementById('exportGiftTransferMap').addEventListener('click', function (e) {
				e.preventDefault();
				export_table_to_excel('importGiftTransferSortTable', 'Import Gift Transfer Map (' + new Date().toLocaleString() + ').csv');
			});
		}

		M.Modal.getInstance(viewGiftTransferModal).close();
		M.Modal.getInstance(importGiftTransfer).open();

		document.querySelectorAll('.import-tranche').forEach((tranche) => {
			tranche.addEventListener('keyup', function (e) {
				switch (e.target.id.split('-')[0]) {
					case 'editableImportDate':
						importTransMap[e.target.id.replace('editableImportDate-', '')].date = Date.parse(e.target.innerText).toString();
						break;
					case 'editableImportNotes':
						importTransMap[e.target.id.replace('editableImportNotes-', '')].notes = e.target.innerText;
						break;
					case 'editableImportSharePrice':
						importTransMap[e.target.id.replace('editableImportSharePrice-', '')].sharePrice = Number(
							e.target.innerText.replace(/[^-.\d]/g, '')
						).toLocaleString('en-US', usp);
						break;
					case 'editableImportBasis':
						importTransMap[e.target.id.replace('editableImportBasis-', '')].taxBasis = Number(
							e.target.innerText.replace(/[^-.\d]/g, '')
						).toLocaleString('en-US', usp);
						break;
				}
			});
		});

		if (continueEditing)
			continueEditing.addEventListener('click', function (e) {
				e.preventDefault();
				M.Modal.getInstance(importGiftTransfer).close();
				M.Modal.getInstance(viewGiftTransferModal).open();
			});

		if (proceedWithGiftTransferImport) {
			proceedWithGiftTransferImport.addEventListener('click', async function (e) {
				e.preventDefault();
				M.Modal.getInstance(importGiftTransfer).close();
				M.Modal.getInstance(statementWaitState).open();
				// Wait for axios call completion before continuing
				if (STD_EXT_DATA) compileNewTransactions();
				else importNewTransactions();
			});
		}

		// Rewritten (#5971 Task #38) to route through TransactionService.js's
		// queueExternalWrite/flushExternalQueue instead of the old per-file
		// modifyCSV/uploadCSV/sendToS3 pipeline. STD_EXT_DATA counterpart to
		// importNewTransactions()'s JSON path; same batching semantics.
		// Unlike the old modifyCSV(), this does NOT strip '#' from nrid (only
		// from notes) -- nrid is the row's lookup key (_findRowIndexByNrid),
		// so rewriting it was likely an unintentional side effect, not a fix.
		// Ledger write now happens before cap table balances are touched, so a
		// failed/no-op flush can't leave balances adjusted with no ledger entry (#6140).
		async function compileNewTransactions() {
			statementWaitHeader.innerHTML = 'Compiling ' + importTransMap.length + ' new transaction' + (importTransMap.length > 1 ? 's.' : '.');
			let adjust = document.getElementById('adjustCapTableAutomatically').checked;

			for (let i = 0; i < importTransMap.length; i++) {
				let r = importTransMap[i];

				let record = buildTransactionRecord({
					date: (Number(r.date) / 1000).toFixed(0), // r.date is milliseconds (Date.now()/Date.parse); buildTransactionRecord expects unix-seconds.
					notes: r.notes,
					securityType: r.securityType,
					shareClass: r.shareClass,
					sharePrice: r.sharePrice,
					quantity: r.quantity,
					sender: r.sender,
					receiver: r.receiver,
					senderName: r.senderName,
					receiverName: r.receiverName,
					taxBasis: r.taxBasis,
					// no expDate/strikePrice: gift-transfer tranches are plain
					// share reissuance/cancellation, never derivative
					// securities -- matches the old modifyCSV, which also
					// always left those two columns blank for these rows.
				});
				queueExternalWrite({ record, action: 'create' });
			}

			for (let i = 0; i < giftSources.length; i++) {
				let s = giftSources[i];

				let record = buildTransactionRecord({
					date: s.date,
					expDate: s.expDate,
					notes: s.notes.replace('#', ''),
					securityType: s.securityType,
					sharePrice: s.sharePrice,
					quantity: s.quantity,
					sender: s.sender,
					receiver: s.receiver,
					senderName: s.senderName,
					receiverName: s.receiverName,
					strikePrice: s.strikePrice,
					taxBasis: s.taxBasis,
					shareClass: s.shareClass,
				});
				record.nrid = s.nrid;
				queueExternalWrite({ record, action: 'update' });
			}

			let ok = false;
			try {
				ok = await flushExternalQueue({
					userid: authedUser,
					cookie,
					client,
					clientData,
					isACR: typeof isACR !== 'undefined' && isACR,
				});
			} catch (err) {
				console.error(err);
			}

			if (!ok) {
				statementWaitHeader.innerHTML = 'Import failed.';
				showToast('error-toast', 'Could not save these transactions to the ledger. Nothing was changed -- please try again.');
				M.Modal.getInstance(statementWaitState).close();
				return;
			}

			if (giftSources[0]) incrementNextCertNumber(giftSources[0].shareClass);

			if (adjust) {
				for (let i = 0; i < importTransMap.length; i++) {
					let r = importTransMap[i];
					const params = {
						shareClass: r.shareClass,
						quantity: r.quantity.replace(/[^-.\d]/g, ''),
						senderId: r.sender,
						receiverId: r.receiver,
					};
					// GH #6160: keep going past a failed/skipped tranche instead of
					// losing the rest of the batch, and surface any failures below.
					try {
						const result = await adjustCapTable({ client, userid: authedUser, cookie, capTableData, shareholderData, adjustParams: params });
						if (!result?.senderAdjusted) {
							console.error('adjustCapTable: sender-side cap table adjustment skipped for', params, result?.reason);
							showToast(
								'error-toast',
								`Ledger updated, but the cap table balance could not be fully adjusted (${result?.reason || 'see console'}). Please reconcile manually.`
							);
						}
					} catch (err) {
						console.error('adjustCapTable threw for', params, err);
						showToast('error-toast', 'Ledger updated, but the cap table balance could not be adjusted for one tranche. Please reconcile manually.');
					}
				}
			}

			document.dispatchEvent(new Event('RefreshSessionStorage'));
			clearPendingRequests('Processed');
		}

		async function importNewTransactions() {
			statementWaitHeader.innerHTML = 'Importing ' + importTransMap.length + ' new transaction' + (importTransMap.length > 1 ? 's.' : '.');

			// Import transactions sequentially
			for (let i = 0; i < importTransMap.length; i++) {
				await createNewTransaction(transParams(importTransMap[i]), false);
			}

			// createNewTransaction() already surfaced an error and left balances
			// untouched if the ledger write failed -- don't also consume gift
			// sources (reduce the sender's position) against transactions that
			// were never actually created.
			if (!newTransactionsOk) {
				M.Modal.getInstance(statementWaitState).close();
				return;
			}

			console.log('Finished importing transactions.');

			// Process gift sources sequentially -- routed through the shared service (#5971)
			// instead of a raw axios.patch, which (1) ignored EXTERNAL_DATA branching (an
			// Alera client is EXTERNAL_DATA but not STD_EXT_DATA) and (2) omitted
			// expDate/strikePrice, silently wiping them on derivative gift sources since this
			// hits a full-record-REPLACE endpoint. Both fixed: expDate/strikePrice are read
			// off giftSources[i] and included like every other field.
			for (let i = 0; i < giftSources.length; i++) {
				const source = giftSources[i];
				const shareClass = source.shareClass;
				const transaction = {
					date: source.date.toString(),
					expDate: source.expDate,
					notes: source.notes.replace('#', ''),
					securityType: source.securityType,
					sharePrice: (source.sharePrice || '').replace(/[^.\d]/g, ''),
					quantity: source.quantity.replace(/[^-.\d]/g, ''),
					sender: source.sender,
					receiver: source.receiver,
					senderName: source.senderName,
					receiverName: source.receiverName,
					strikePrice: source.strikePrice,
					nrid: source.nrid,
					// Negative Tax Basis not allowed (per Chris, 2026-07-30).
					taxBasis: (source.taxBasis || '').replace(/[^.\d]/g, ''),
				};

				try {
					if (EXTERNAL_DATA) {
						queueExternalWrite({ record: transaction, action: 'update' });
					} else {
						await patchTransactionRemote({ userid: authedUser, cookie, client, clientData, shareClass, transaction });
					}
				} catch (err) {
					console.error(err);
				}
			}

			// EXTERNAL_DATA clients: flush the whole batch once, same as
			// createNewTransaction's own batching above -- one CSV re-upload for
			// every gift source processed, not one per source.
			if (EXTERNAL_DATA && giftSources.length) {
				try {
					const ok = await flushExternalQueue({
						userid: authedUser,
						cookie,
						client,
						clientData,
						isACR: typeof isACR !== 'undefined' && isACR,
					});
					if (!ok) showToast('error-toast', 'New transactions saved, but updating the gift source(s) failed -- please check the ledger.');
				} catch (err) {
					console.error(err);
					showToast('error-toast', 'New transactions saved, but updating the gift source(s) failed -- please check the ledger.');
				}
			}

			document.dispatchEvent(new Event('RefreshSessionStorage'));
			console.log('Completed processing gift sources.');
			if (CERT_COLUMN) incrementNextCertNumber(giftSources[0]?.shareClass);
		}

		// Transaction parameters helper
		function transParams(row) {
			return {
				shareClass: row.shareClass,
				securityType: row.securityType,
				date: (Number(row.date) / 1000).toFixed(0),
				notes: row.notes,
				sender: row.sender,
				receiver: row.receiver,
				senderName: row.senderName,
				receiverName: row.receiverName,
				quantity: row.quantity.replace(/[^-.\d]/g, ''),
				sharePrice: String(row.sharePrice || '').replace(/[^.\d]/g, ''),
				// Negative Tax Basis not allowed (per Chris, 2026-07-30).
				taxBasis: String(row.taxBasis || '').replace(/[^.\d]/g, ''),
			};
		}

		// Unified with Ledgers' own createNewTransaction (#5971) instead of a
		// hand-rolled axios.put() that duplicated _buildParams and never
		// branched on EXTERNAL_DATA (CSV clients wrote straight to JSON).
		// Also fixed: senderName/receiverName were only set in the "one side is
		// Issuer" branches, leaving both undefined in the regular case.
		// Records a created transaction's nrid against the originating transfer-request
		// execution (admin-review-design doc, Section 3 Change 3). Best-effort: log on
		// failure rather than throwing, since the transaction already exists either way.
		async function recordTransferExecutionTransaction(nrid) {
			if (!options.wfExecutionId || !nrid) return;
			try {
				await window.axios.post(
					wfExecutionsProgressURL,
					JSON.stringify({
						client,
						userid: authedUser,
						cookie,
						execution_id: options.wfExecutionId,
						action_type: 'transaction_created',
						reference_id: nrid,
					})
				);
			} catch (err) {
				console.error('[gift-transfer] recordTransferExecutionTransaction failed:', err);
			}
		}

		async function createNewTransaction(transParams, isJustOne) {
			if (!isJustOne) importTransCounter++;

			// Only the genuinely page-specific parts stay here: this function's
			// only caller (transParams(), from the bulk gift-CSV importer) already
			// hands over bare sender/receiver ids and separate name fields (no
			// "Name | id" pipe-format to split, unlike Ledgers' <select> values),
			// and already-unix-seconds date/expDate (converted from the CSV row
			// before this function ever sees them -- do NOT run these through
			// dateStringToUnix, which expects a date STRING and silently blanks
			// an already-numeric value; caught and reverted here 2026-07-30).
			let shareClass = DOMPurify.sanitize(transParams.shareClass);
			let quantity = DOMPurify.sanitize(transParams.quantity.replace(/[^-.\d]/g, ''));
			let sender = DOMPurify.sanitize(transParams.sender);
			let receiver = DOMPurify.sanitize(transParams.receiver);

			let senderId, receiverId, senderName, receiverName;
			if (sender.startsWith('Issuer')) {
				receiverId = receiver;
				senderName = 'Issuer';
				receiverName = DOMPurify.sanitize(transParams.receiverName || '');
			} else if (receiver.startsWith('Issuer')) {
				senderId = sender;
				receiverName = 'Issuer';
				senderName = DOMPurify.sanitize(transParams.senderName || '');
			} else {
				senderId = sender;
				receiverId = receiver;
				senderName = DOMPurify.sanitize(transParams.senderName || '');
				receiverName = DOMPurify.sanitize(transParams.receiverName || '');
			}

			// Sanitize + full-record shape now shared with Ledgers' own
			// createNewTransaction via TransactionService.js's
			// buildTransactionRecord() (#5971, Round Two, 2026-07-30) -- this used
			// to duplicate that logic independently and had drifted from it.
			let newTransaction = buildTransactionRecord({
				date: transParams.date,
				expDate: transParams.expDate,
				notes: transParams.notes,
				shareClass,
				securityType: transParams.securityType,
				strikePrice: transParams.strikePrice,
				sharePrice: transParams.sharePrice,
				taxBasis: transParams.taxBasis,
				quantity,
				sender: senderId,
				receiver: receiverId,
				senderName,
				receiverName,
			});

			const adjust = document.getElementById('adjustCapTableAutomatically').checked;
			const adjustParams = { shareClass, quantity, senderId, receiverId };

			// adjustCapTable() only runs once its ledger write is confirmed (#6140).
			// EXTERNAL_DATA flushes once per batch (last row), so each row's
			// adjustment is deferred in pendingBatchAdjustments until that succeeds.
			try {
				if (EXTERNAL_DATA) {
					queueExternalWrite({ record: newTransaction, action: 'create' });
					pendingBatchRecords.push(newTransaction);
					pendingBatchAdjustments.push(adjustParams);

					// Flush once per batch (last row), matching Ledgers' bulk-import
					// behavior, instead of one full CSV re-upload per row.
					if (isJustOne || importTransCounter >= importTransTarget) {
						const ok = await flushExternalQueue({
							userid: authedUser,
							cookie,
							client,
							clientData,
							isACR: typeof isACR !== 'undefined' && isACR,
						});

						if (!ok) {
							newTransactionsOk = false;
							showToast('error-toast', 'Could not save these transactions to the ledger. Nothing was changed -- please try again.');
							pendingBatchRecords = [];
							pendingBatchAdjustments = [];
							return;
						}

						// Record every row in the batch, not just the current one. Read .nrid
						// AFTER the flush resolves -- _applyCreate() assigns it in place, so
						// reading earlier (round 1 fix) silently recorded nothing.
						for (const rec of pendingBatchRecords) {
							await recordTransferExecutionTransaction(rec.nrid);
						}

						if (adjust) {
							for (const params of pendingBatchAdjustments) {
								const result = await adjustCapTable({
									client,
									userid: authedUser,
									cookie,
									capTableData,
									shareholderData,
									adjustParams: params,
								});
								if (!result?.senderAdjusted) {
									console.error('adjustCapTable: sender-side cap table adjustment skipped for', params, result?.reason);
									showToast(
										'error-toast',
										`Ledger updated, but the cap table balance could not be fully adjusted (${result?.reason || 'see console'}). Please reconcile manually.`
									);
								}
							}
						}

						pendingBatchRecords = [];
						pendingBatchAdjustments = [];
					}
				} else {
					const created = await createTransactionRemote({
						userid: authedUser,
						cookie,
						client,
						clientData,
						shareClass,
						transaction: newTransaction,
					});
					newTransaction.nrid = created.nrid;
					await recordTransferExecutionTransaction(created.nrid);

					if (adjust) {
						const result = await adjustCapTable({ client, userid: authedUser, cookie, capTableData, shareholderData, adjustParams });
						if (!result?.senderAdjusted) {
							console.error('adjustCapTable: sender-side cap table adjustment skipped for', adjustParams, result?.reason);
							showToast(
								'error-toast',
								`Ledger updated, but the cap table balance could not be fully adjusted (${result?.reason || 'see console'}). Please reconcile manually.`
							);
						}
					}
				}

				if (!isJustOne && importTransCounter >= importTransTarget) {
					clearPendingRequests('Processed');
				}
			} catch (err) {
				console.error(err);
				newTransactionsOk = false;
				showToast('error-toast', 'Could not save this transaction to the ledger. Please check the ledger before retrying.');
			}
		}
	}

	async function clearPendingRequests(newStatus) {
		// Get transfer requests for this user
		let requests = shareholderData.clients[client].transfer_requests;
		if (requests && Object.keys(requests).length > 0) {
			statementWaitHeader.innerHTML = 'Your browser will refresh. Stand by...';

			// Update all "Open" gifts' status to "Processed"
			Object.values(requests)
				.flat()
				.forEach((gift) => {
					if (gift.status === 'Open') gift.status = newStatus;
				});

			// Save updated data
			sessionStorage.setItem('shareholder.Data', JSON.stringify(shareholderData));

			try {
				const response = await axios.post(
					'https://api.nthround.io/portal/user/update-user-client-data',
					JSON.stringify({
						client: client,
						userid: authedUser,
						cookie: cookie,
						holder: shareholder,
						data: shareholderData.clients[client],
					})
				);
				console.log(response);
				showToast('success-toast', 'Data updated successfully!');
				document.dispatchEvent(new Event('RefreshSessionStorage'));
				setTimeout(() => {
					M.Modal.getInstance(statementWaitState).close();
					location.reload();
				}, 5000);
			} catch (error) {
				console.error(error);
			}
		} else {
			showToast('success-toast', 'Data updated successfully!');
			document.dispatchEvent(new Event('RefreshSessionStorage'));
			M.Modal.getInstance(statementWaitState).close();
			document.dispatchEvent(new CustomEvent('renderShareholderView', { detail: { shareholder: shareholder, refreshed: true } }));
		}
	}

	function sendThisRequest() {
		let timestamp = Date.now().toString();
		const waitingModal = document.getElementById('waitingModal');
		M.Modal.getInstance(viewGiftTransferModal).close();
		M.Modal.getInstance(waitingModal).open();
		// previewGiftTransfer.classList.add('disabled');
		// previewGiftTransfer.innerHTML = "One moment please...";
		let purpose = BUY_BACK ? labelBB + ' Request' : 'Share Transfer Request';
		let giftHtml =
			giftHeadline.innerHTML +
			'<br>' +
			balanceHeadline.innerHTML.replace('Shares Owned Now:', 'Shares Owned:') +
			'<br>' +
			userGiftTransferTable.innerHTML +
			'<br>' +
			totalCurrentValueDiv.innerHTML +
			'<br>' +
			viewRecipients.innerHTML;
		giftHtml = cleanAndFormatHtml(giftHtml);

		function cleanAndFormatHtml(giftHtml) {
			// Parse the raw HTML into a DOM object
			const parser = new DOMParser();
			const doc = parser.parseFromString(giftHtml, 'text/html');

			// Add professional heading at the top
			const headingDiv = document.createElement('div');
			const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
			headingDiv.innerHTML = `
				<h1 style="text-align: center; font-size: 20px; color: #2c3e50; margin: 34px 0 5px;">${purpose}</h1>
				<div style="text-align: center; font-size: 14px; color: #555; margin: 0 0 20px;">Made on ${today} by ${userData.user_name}</div>
			`;

			doc.getElementById('showHidePriorsDiv')?.remove();

			// Inject the heading before the content
			const body = doc.body;
			body.insertBefore(headingDiv, body.firstChild);

			// Remove all hidden elements
			doc.querySelectorAll(".hidden, [style*='display: none']").forEach((el) => el.remove());

			// Remove "Remove" buttons
			doc.querySelectorAll('.remove-gift-recipient').forEach((el) => el.remove());

			// Remove checkboxes but retain the "Tranche" designation
			doc.querySelectorAll("input[type='checkbox']").forEach((checkbox) => {
				const td = checkbox.closest('td');
				if (td) {
					const trancheDesignation = td.querySelector('div.shareholder-img');
					if (trancheDesignation) {
						td.innerHTML = trancheDesignation.outerHTML; // Keep the tranche designation
					} else {
						td.innerHTML = ''; // Replace with empty content if no designation exists
					}
				}
			});

			// Add professional styles
			const style = `
				<style>
					body {
						font-family: 'Source Sans Pro', sans-serif;
						font-size: 12px;
						margin: 1in;
					}
					h1, h2, h5 {
						color: #2c3e50;
						text-align: center;
						margin: 0.1in 0 0.1in;
					}
					.table-heading {
						background-color: #2c3e50;
						color: white;
						font-weight: bold;
						text-align: center;
						padding: 4px;
						font-size: 12px; /* Larger heading size */
					}
					table {
						width: 100%;
						border-collapse: collapse;
						margin: 0.1in 0;
					}
					th, td {
						border: 1px solid #ddd;
						text-align: center;
						padding: 4px;
						font-size: 12px; /* Table font size remains consistent */
					}
					th {
						background-color: #f4f4f4;
						font-weight: bold;
						font-size: 13px; /* Slightly larger than table rows */
					}
					strong {
						font-weight: bold;
						font-size: 14px; /* Larger font size for names */
					}
					.ownership {
						font-weight: bold;
						font-size: 14px; /* Match label size for numerals */
						color: #555;
					}
					.recip-div {
						margin-top: 0.2in; /* Add vertical spacing between recipient sections */
					}
				</style>
			`;

			// Inject the styles into the document head
			const styleElement = document.createElement('style');
			styleElement.innerHTML = style;
			doc.head.appendChild(styleElement);

			// Add "To: " before each recipient's name and ensure font size matches sender's name
			doc.querySelectorAll('.recip-div .row .col.s12.m6').forEach((el) => {
				el.innerHTML = `To: <strong>${el.textContent.trim()}</strong>`;
				el.querySelector('strong').style.fontSize = '14px'; // Larger size for names
			});

			// Make sender's name bold and ensure proper size
			const senderElement = doc.querySelector('span.text-light');
			if (senderElement) {
				senderElement.innerHTML = `<strong>${senderElement.textContent.trim()}</strong>`;
				senderElement.querySelector('strong').style.fontSize = '14px'; // Match recipient font size
			}

			// Ensure before/after ownership numerals match label size
			doc.querySelectorAll('.text-primary').forEach((el) => {
				el.style.fontSize = '14px'; // Match the subheading labels
				el.style.fontWeight = 'bold'; // Ensure boldness
				el.style.color = '#555'; // Consistent coloring
			});

			// Remove empty rows at the bottom of each recipient's section
			doc.querySelectorAll('.recip-div table tbody').forEach((tbody) => {
				const rows = Array.from(tbody.querySelectorAll('tr'));
				rows.forEach((row) => {
					const isEmptyRow = Array.from(row.cells).every((cell) => !cell.textContent.trim());
					if (isEmptyRow) row.remove();
				});
			});

			// Reduce vertical spacing for p, .row, and tables
			doc.querySelectorAll('p, .row, table').forEach((el) => {
				el.style.marginTop = '0.1in';
				el.style.marginBottom = '0.1in';
			});

			// Return the cleaned and formatted HTML as a string
			return doc.documentElement.outerHTML;
		}

		const giftString = JSON.stringify(theseGifts);
		const giftBlob = new Blob([JSON.stringify(theseGifts)], { type: 'application/json' });
		const giftFile = new File([giftBlob], purpose.replace(/ /g, '_') + '_from_' + userData.userid + '.json', { type: 'application/json' });

		uploadGiftRequestToYourDocs();

		async function uploadGiftRequestToYourDocs() {
			// Prepare new document to be added to the user's documents
			let newDoc = {
				type: 'Call/Notice',
				tag: 'ALL',
				name: purpose,
				investor: userData.userid,
				description: 'From ' + userData.user_name,
				require2Factor: false,
				date: moment().format('MMM D, YYYY'),
				id: timestamp,
			};

			// Prepare parameters for the document upload API
			let params = {
				...newDoc,
				client: client,
				userid: authedUser,
				cookie: cookie,
				htmlResults: giftHtml,
				callOrigin: 'gift-transfer',
			};

			// Add transfer_request to User json
			let userTransferRequests = userData.clients[client].transfer_requests || {};

			// Initialize an array for the timestamp if not present
			userTransferRequests[timestamp] = userTransferRequests[timestamp] || [];

			// Push all gifts into the transfer_requests, each with a status
			allGifts.forEach((gift) => {
				userTransferRequests[timestamp].push({ ...gift, status: 'Open' });
			});

			// Update the userData object and save it to session storage
			userData.clients[client].transfer_requests = userTransferRequests;
			sessionStorage.setItem('user.Data', JSON.stringify(userData));

			// Prepare the payload for the DynamoDB update API
			let dynamoDbPayload = {
				userid: authedUser,
				client: client,
				cookie: cookie,
				holder: userData.userid,
				data: userData.clients[client],
			};

			// Call both APIs sequentially
			try {
				// 1. Update transfer_requests in DynamoDB
				const dynamoRes = await axios.post('https://api.nthround.io/portal/user/update-user-client-data', JSON.stringify(dynamoDbPayload));
				console.log('DynamoDB update response:', dynamoRes);

				// 2. Upload the document
				const docRes = await axios.post('https://api.nthround.io/portal/auth/upload-to-your-docs', params);
				console.log('upload-to-your-docs response:', docRes);
				if (AppState.activeRequestsCampaign?.send_emails) sendGiftRequestEmail(docRes.data.pdf_url);

				// If both succeed
				console.log('Transfer Request successfully uploaded and transfer_requests updated.');

				let holder = userData.userid; // preview breadcrumb
				document.dispatchEvent(new Event('RefreshSessionStorage'));
				setTimeout(() => {
					document.getElementById('waitingHeader').innerHTML = 'Success!!';
					document.getElementById('waitingImage').src = './../../resources/assets/celebrate-v4.svg';

					// previewGiftTransfer.classList.remove('disabled');
					// previewGiftTransfer.innerHTML = "Request These Transfers";

					M.Modal.getInstance(waitingModal).options.onCloseEnd = () => {
						if (sessionStorage.getItem('preview'))
							document.dispatchEvent(new CustomEvent('previewShareholder', { detail: { uid: holder, path: '' } }));
						else document.dispatchEvent(new Event('renderInvestorPortal'));
					};
				}, 2000);
			} catch (err) {
				// Log any errors from either API call
				console.error('Error during transfer request processing:', err);
			}
		}

		function sendGiftRequestEmail(pdfUrl) {
			const ending = `<br><br>${firstName(userData)}<br><br><a href="https://${client}.nthround.io/admin/users/edit/?email=${userData.userid}">View this ${purpose}</a>`;
			axios
				.post(
					'https://api.nthround.io/portal/auth/send-email',
					JSON.stringify({
						client: client,
						userid: authedUser,
						cookie: cookie,
						sender_email: 'messages@nthround.com',
						sender_name: userData.user_name,
						replyto_email: userData.userid,
						recipient: clientData.contact_email,
						cc_emails: [userData.userid],
						subject: purpose,
						message:
							'Hi--<br><br>Please review this ' +
							purpose +
							'.<br><br>I understand that you will reach out to me for confirmation prior to processing.<br><br>Thank you!' +
							ending +
							'<br><br>[Note: email replies will go to ' +
							userData.user_name +
							' &lt;' +
							userData.userid +
							'&gt;.]',
						attachment: pdfUrl,
					})
				)
				.then(function (res) {
					const btnGroupLink = document.getElementById('btnGroupLink');
					const leaveMessageLink = document.getElementById('leaveMessageLink');
					const leaveMessageModal = document.getElementById('leaveMessageModal');
					leaveMessageLink.classList.add('disabled');
					leaveMessageLink.innerHTML = purpose + ' Pending';
					btnGroupLink.classList.replace('bg-grey-darker', 'grey');
					leaveMessageModal.innerHTML = `
					<div class="p-2 center-align">
						<div class="mb-4"><img src="./../../resources/assets/celebrate-v4.svg" alt="Musicians celebrating" class="responsive-img px-12"/></div>
						<h3 class="text-lg mb-4 mt-4 font-bold">Your request has been sent!</h3>
						<p class="text-secondary text-sm mt-0 mb-0">We emailed a copy to ${userData.userid}.</p>
						<p class="text-secondary text-sm mt-0 mb-0">The company will reach out shortly to confirm your request.</p>
						<p class="text-secondary text-xs mt-0 mb-0">(On closing this modal your browser will refresh.)</p>
					</div>
				`;
				})
				.catch(function (err) {
					console.error(err);
				});
		}
	}

	function toNumber(str) {
		let num = Number(str);
		return isNaN(num) ? 1 : num; // assumed share price, zero is trouble mathwise!
	}

	function initModal() {}

	// Reinitialize M.Modal to include dynamically added instances
	// var modals = document.querySelectorAll('.modal');
	// let modalOptions = { onOpenStart: initModal };
	// var instances = M.Modal.init(modals, modalOptions);
}

export { giftTransfer, resetGiftTransferState };
